import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// English translations
const enTranslations = {
  duplicateLaunch: {
    title: 'This booth is already open',
    secondBooth: 'The app was started twice. Close this window and use the booth that is already open.',
    closeIn: 'Close this window ({{seconds}})',
    closeByHand: 'Press Alt+F4 to close this window.',
    keepSecondBooth: 'Use this as a second booth',
    noOtherWindow: 'No other booth window open? Restart the computer.',
    deadTitle: 'Dreambooth is already open on this computer',
    deadBody:
      "This window can't read this booth's settings, login or activation code, so the booth looks reset. This usually happens when the app is started twice. Don't enter the activation code here.",
    closeNow: 'Close this window',
    deadNoOtherWindow:
      'No other Dreambooth window open? Close Dreambooth completely or restart the computer, then open it once. Updating to the latest version stops this from happening.',
    deadContinue: 'Continue in this window anyway'
  },
  crash: {
    title: 'The booth stopped',
    body: 'Something went wrong and the booth could not carry on. Sessions already uploaded are safe. Reload to start the booth again.',
    reload: 'Reload the booth',
    contact: 'If this keeps happening, send us this line:'
  },
  cameraTest: {
    label: 'Test photo',
    shoot: 'Take a test photo',
    shooting: 'Taking a photo...',
    empty: 'Check the real photo, not just the preview.',
    hint: 'This is what a guest gets. Tap anywhere to close.',
    open: 'Open the test photo',
    noFrame: 'The camera returned no photo. Try again.',
    copy: 'Copy the test photo',
    copied: 'Photo copied.',
    copyFailed: 'Could not copy the photo.',
    save: 'Save the test photo',
    saved: 'Photo saved.'
  },
  cameraPresets: {
    title: 'Copy settings from another booth',
    count: '{{count}} saved',
    empty: 'No saved settings yet. This booth saves its own when you close this window, and you can copy it onto another booth from there.',
    choose: 'Choose a booth to copy from',
    use: 'Use these settings',
    checking: 'Checking the camera...',
    unknownProject: 'Unknown booth',
    unknownModel: 'unknown',
    approxWarning: 'This was saved on a different camera ({{model}}). Values that do not exist here will be skipped, and close ones will be approximated.',
    keep: 'Keep as reference',
    keptTitle: 'Kept as reference - auto-save leaves it alone',
    keepFailed: 'Could not save that ({{detail}}). Try again.',
    rename: 'Rename',
    renameLabel: 'Name',
    renameFailed: 'Could not rename ({{detail}}). Try again.',
    modifiedFrom: 'Modified from {{label}}',
    revert: 'Revert',
    revertGone: 'That saved configuration is no longer available.',
    planFailed: 'Could not read the camera. Check the cable and try again.',
    planSummary: '{{apply}} will change - {{adjust}} approximated - {{skip}} skipped',
    alreadyMatching: '{{count}} settings already match - they will not be touched.',
    showDetail: 'Show details',
    hideDetail: 'Hide details',
    wanted: 'wanted {{v}}',
    confirm: 'Yes, change the camera',
    applyNotice: 'The live preview pauses while this runs. Do not unplug the camera.',
    applying: 'Applying {{done}} of {{total}}...',
    stop: 'Stop',
    resultSummary: '{{applied}} applied - {{skipped}} skipped',
    resultFailed: '{{count}} failed',
    reconnect: 'Unplug and reconnect the camera before running a session.',
    unhealthy: 'The camera stopped responding. Unplug and reconnect it before the next session.',
    undo: 'Undo - put the previous settings back',
    previewPaused: 'Preview paused while camera settings are applied',
    tier: { exact: 'Exact match', brand: 'Same brand', cross: 'Other camera' },
    groups: {
      choose: 'Choose what to copy',
      hide: 'Done',
      summary: 'Copies: {{list}}.',
      none: 'Nothing selected to copy.',
      exposure: { title: 'How bright the photo is', examples: 'ISO, shutter speed, aperture, metering' },
      image: { title: 'Colour and quality', examples: 'White balance, picture style, file size' },
      behaviour: { title: 'How the camera shoots', examples: 'Focus mode, drive mode, bracketing' },
      geometry: { title: 'How the picture is framed', examples: 'Mirror, rotation, crop ratio - these depend on how this booth is physically built. Only copy them between identical builds.' }
    },
    skip: {
      missing: 'this camera has no such setting',
      readonly: 'set on the camera body, not over USB',
      noChoice: 'no comparable value on this camera',
      modeLocked: 'turn the mode dial to M to apply this',
      groupOff: 'group turned off above',
      denied: 'not safe to change automatically',
      crossDisabled: 'copying between different cameras is turned off'
    }
  },
  diskCleanup: {
    title: 'Storage',
    free: '{{size}} free',
    lowTitle: 'Disk almost full',
    scanning: 'Checking files…',
    running: 'Cleaning…',
    sessions: '{{count}} sessions',
    nothing: 'Nothing old enough to delete.',
    protected: '{{count}} not uploaded yet, kept',
    deleted: '{{count}} sessions deleted',
    skippedOffline: '{{count}} kept: server unreachable',
    skippedNotUploaded: '{{count}} kept: not on the server yet',
    failed: 'Cleanup failed. Try again.',
    confirm: 'Clean up'
  },
  common: {
    continue: 'Continue',
    cancel: 'Cancel',
    save: 'Save',
    change: 'Change',
    refresh: 'Refresh',
    select: 'Select',
    edit: 'Edit',
    download: 'Download',
    autoStart: 'Auto-start',
    back: 'Back',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    logout: 'Logout',
    upgrade: 'Upgrade',
    next: 'Next',
    or: 'or',
    loading: 'Starting...',
    retry: 'Retry',
    savePathNew: 'Files are saved to your Pictures/Dreambooth Gallery/{{date}} folder.'
  },
  settings: {
    groups: {
      navLabel: 'Settings sections',
      camera: 'Camera',
      printing: 'Printing',
      booth: 'Booth',
      files: 'Files',
      payments: 'Payments'
    },
    payments: {
      intro: 'For guests who cannot pay with {{rail}}. Card is offered underneath it, and {{rail}} stays the main option on screen.',
      introNoOnline: 'Online payment is not available in this region yet, so guests redeem a voucher code. A card terminal is the only way to take payment at the booth.',
      railQris: 'QRIS',
      railOnline: 'the QR code',
      none: 'No card payments',
      noneHint: 'No card button. Guests pay with {{rail}} only.',
      noneHintNoOnline: 'No card button. Guests can only redeem a voucher code.',
      manual: 'Card terminal, confirmed by staff',
      manualHint: 'The guest taps the card button, staff run the card on the terminal, then confirm on the booth with the PIN.',
      connected: 'Connected terminal',
      connectedHint: 'The booth sends the amount to the terminal and the result comes back by itself.',
      connectedUnavailable: 'A Nayax reader (connect your Nayax account in Studio → Wallet first), or Mercado Pago Point in Mexico.',
      nayaxLoading: 'Checking your Nayax account...',
      nayaxAccountError: 'Could not reach Studio to check your Nayax account.',
      nayaxNoAccount: 'Connect your Nayax account in Studio first: Wallet → Add payment gateway → Nayax card reader.',
      nayaxSerial: 'Reader serial',
      nayaxSerialInvalid: 'Digits only: the serial on the reader, or its Nayax machine id.',
      nayaxChecking: 'Checking...',
      nayaxPair: 'Check & pair',
      nayaxPaired: 'Paired: {{serial}} on {{account}}',
      nayaxUnpair: 'Unpair',
      terminalName: 'Terminal name',
      terminalNamePlaceholder: 'e.g. Card terminal — front desk',
      terminalNameHint: 'Shown to staff on the confirm step, and stored on every card session.',
      requireCode: 'Require the approval code',
      requireCodeHint: 'From the terminal receipt. It is what makes a day of card sessions reconcilable against the terminal batch.',
      showMarks: 'Show Visa / Mastercard marks',
      showMarksHint: 'Helps a visitor recognise the button.',
      terminalTitle: 'Terminal',
      terminalLoading: 'Looking for terminals...',
      terminalNotApplicable:
        'Your active payment gateway is not Mercado Pago. Make your Mercado Pago wallet active in the dashboard to use a connected terminal.',
      terminalError: 'Could not load terminals: {{reason}}',
      terminalRetry: 'Try again',
      terminalNone: 'No Point terminals found on your Mercado Pago account.',
      terminalSerial: 'Serial {{serial}}',
      terminalReady: 'Ready (PDV mode)',
      terminalNotPdv: 'Not in PDV mode yet',
      terminalSwitchToPdv: 'Switch to PDV mode',
      terminalSwitching: 'Switching...',
      terminalPdvDone: 'Switched to PDV mode. Restart the terminal to finish.',
      terminalPdvFailed: 'Could not switch the terminal: {{reason}}',
      terminalSaved: 'This booth now charges on that terminal',
      terminalCleared: 'No terminal paired with this booth',
      terminalRefresh: 'Refresh list',
      terminalMissing: 'The saved terminal ({{serial}}) is not on this Mercado Pago account.',
      terminalClear: 'Stop using it',
      terminalNotPicked: 'Pick a terminal. Until then the booth shows no card button.',
      terminalRestartHint:
        'A terminal takes payments from the booth only in PDV mode. After switching, restart the terminal and check that its linking mode shows Punto de Venta (PDV).'
    },
    tiers: {
      advancedPrinter: 'Advanced',
      advancedPrinterHint: 'Margins, scale, resolution, position and print method',
      advancedCamera: 'Advanced',
      advancedCameraHint: 'Quality, rotation, presets and camera configuration'
    },
    title: 'Booth Setup',
    boothGroups: {
      lockdown: 'Lockdown',
      lockdownHint: 'Keep guests inside the booth.',
      startup: 'Startup',
      startupHint: 'Comes back on its own after a restart or a power cut.',
      screen: 'Screen',
      screenHint: 'Where the booth shows, and what it does when nobody is there.'
    },
    project: 'Project',
    credits: {
      title: 'Credits',
      refresh: 'Refresh credits',
      unlimited: 'Unlimited'
    },
    camera: {
      title: 'Camera',
      noPreview: 'No Preview Available',
      selectCamera: 'Select Camera',
      defaultCamera: 'Default',
      noDevicesFound: 'No cameras found',
      detect: 'Detect Camera...',
      troubleshoot: 'Having connection trouble? Replug the usb and refresh might help!',
      guideDescriptionPrefix: 'See ',
      guideLink: 'camera guide',
      guideDescriptionSuffix: 'for DSLR/mirrorless camera connection',
      dslrInstruction:
        'To connect to a DSLR camera, follow this installation instruction and choose "Detect Camera"',
      settings: 'Camera Settings',
      settingsHint: 'Ratio, mirror, rotation, quality and presets',
      debugFps: 'Show FPS overlay on the preview',
      eject: 'Eject Camera',
      ejecting: 'Ejecting camera...',
      ejected: 'Camera ejected successfully',
      ejectFailed: 'Failed to eject camera',
      ejectAndRestart: 'Camera ejected, restarting app...',
      ejectDevMode: 'Camera ejected. Please restart the app manually in development mode.',
      replugRequired: 'Please replug the USB cable or refresh the app',
      driverStatus: {
        installed: 'Driver ready',
        uninstalled: 'Uninstalled/Occupied',
        wrongDriver: 'Wrong driver — run Zadig',
        disconnected: 'Disconnected',
        unknown: 'Driver check failed'
      },
      editModeOptional:
        'Camera selection is optional for edit mode. You can continue without selecting a camera.',
      instructionLink: 'Instruction',
      unknownWebcam: 'Unknown Webcam'
    },
    paperRoll: {
      title: 'Paper',
      notSet: 'Not set. Enter the count when you load a roll.',
      remaining: '~{{left}} of {{total}} left',
      loaded: 'loaded {{date}}',
      newRoll: 'New roll',
      countPlaceholder: 'Left on printer',
      errorCount: 'Enter the number the printer shows.',
      errorAuth: 'Login expired (401). Log out, log in again, then retry.',
      errorDevice: 'This booth is not registered yet (404). Wait a few minutes after opening the app, then retry.',
      errorNetwork: 'No connection. Check the internet, then retry.',
      errorGeneric: 'Could not save'
    },
    printer: {
      title: 'Printer',
      enablePrinting: 'Enable Printing',
      selectPrinter: 'Select Printer',
      savedNotDetected: '{{printer}} — not detected right now',
      settings: 'Printer Settings',
      settingsHint: 'Paper size, DPI, margins, scale and position',
      notAvailable: 'Printing configuration is not available.',
      webPrintHint:
        "When enabled, the app can trigger printing (usually your browser's print dialog). Silent auto-print requires the desktop app.",
      downloadDesktop: 'Download the desktop application for auto-print configuration.',
      guideDescriptionPrefix: 'See',
      guideLink: 'printer guide',
      guideDescriptionSuffix: 'for printer setup and configuration',
      systemDefault: 'System Default',
      scaleFactor: 'Scale Factor (10-200%)',
      scaleFactorHelp: 'Scale percentage (10-200%)',
      printBackground: 'Print Background',
      printBackgroundHint: 'Include background colours and images in the print.'
    },
    download: {
      title: 'Download Settings',
      saveLocal: 'Save files to local device',
      explanation:
        'When enabled, photos, GIFs, and videos will be automatically downloaded to your device.',
      savePath: 'They will be saved in the pictures/dreambooth path.',
      savePathNew: 'Files are saved to your Pictures/Dreambooth Gallery/{{date}} folder.'
    },
    pin: {
      title: 'Lock Fullscreen PIN',
      placeholder: 'PIN',
      noPin: 'No PIN set',
      change: 'Change'
    },
    autoStart: {
      title: 'Auto-Start Settings',
      withProject: 'Auto-start with this project',
      explanation: 'When enabled, the app will automatically start with this project on launch'
    },
    display: {
      title: 'Monitor',
      monitor: 'Monitor',
      primary: 'Main',
      here: 'In use',
      moved: 'Booth moved to that monitor',
      moveFailed: 'Could not move the booth to that monitor',
      unavailable: 'Choosing a monitor is not available in this environment',
      explanation:
        'Tap a monitor to move this booth there right away. Each booth remembers its own monitor.'
    },
    screensaver: {
      title: 'Screensaver',
      label: 'Enable Screensaver (Idle Mode)',
      noVideo: 'No screensaver video configured for this project. Upload one in the dashboard to use this feature.',
      usingProjectTimeout: 'Using project idle timeout:',
      seconds: 'seconds',
      configuredInDashboard: '(configured in dashboard)',
      timeout: 'Timeout (seconds):',
      explanation: "Automatically plays the project's screensaver video when idle, and disconnects the camera to save power.",
      tapToContinue: 'Tap anywhere to continue',
      controlledByProject: 'This setting is controlled by the project configuration in the dashboard.'
    },
    browserGestures: {
      title: 'Web browser',
      label: 'Block browser gestures (pull-to-refresh, swipe-back, pinch zoom)',
      explanation:
        'Kiosk touch guard for tablet browsers: suppresses pull-to-refresh and overscroll chaining, blocks the edge swipe-back/forward navigation, and disables pinch and double-tap zoom. Some system gestures (home indicator, app switcher) can only be locked by the OS — on iPad use Guided Access. Adding this app to the home screen (standalone) improves behavior further.'
    },
    autoLaunch: {
      title: 'Auto-Launch',
      label: 'Start app at system login',
      explanation:
        'Automatically start the app when you log into your computer (after startup, restart, or login). Does not apply to sleep/hibernate wake-up.',
      enabled: 'App will start at system login',
      disabled: 'App will not start at system login',
      error: 'Failed to update auto-launch setting'
    },
    systemControls: {
      title: 'Windows System Controls',
      description:
        'These are recommended Windows settings to optimize your photobooth experience. These settings require administrator privileges and will prompt for elevation when applying changes.',
      edgeSwipe: {
        title: 'Disable screen-edge touch swipes',
        explanation:
          'Prevents customers from accidentally exiting the photobooth app by swiping from screen edges. (A restart will be required after changing this setting.)'
      },
      lockOnWake: {
        title: 'Lock screen on wake',
        disable: 'Disable',
        enable: 'Enable',
        explanation:
          'Disables the lock screen when the computer wakes up, allowing the photobooth app to open directly without requiring a password or sign in button click'
      },
      timeouts: {
        title: 'Prevent screen off & sleep (except on battery-powered laptops)',
        enable: 'Enable',
        explanation:
          'Prevents the screen from turning off during photobooth sessions. AC power: never sleep/display off. DC power (laptops): 30 minutes timeout'
      },
      scriptDownloaded:
        'Script downloaded to Downloads folder. Right-click and select "Run with PowerShell" to apply.',
      edgeSwipesEnabled: 'Edge swipes enabled',
      lockOnWakeEnabled: 'Lock screen on wake enabled',
      applyingChanges: 'Applying changes...',
      storeInstallWarning:
        'Windows system controls require administrator privileges. If automatic execution fails, scripts will be downloaded to your Downloads folder for manual execution.'
    },
    appVersion: {
      label: 'App Version:',
      updateAvailable: 'New App Version Available! Please update to'
    },
    autoUpdate: {
      title: 'App Updates',
      toggleLabel: 'Update automatically',
      checkNow: 'Check now',
      installNow: 'Install and restart',
      openDashboard: 'Open dashboard',
      installFailed: 'Could not install the update. {{reason}}',
      installBusy:
        'The booth is busy: a session, print or upload is still running. Try again when it is free.',
      explanation:
        'The booth downloads patch and minor updates on its own while it is free, and installs them the next time the app opens (you can skip it then) or when you press Install and restart. A major version is never installed on its own: download it from the dashboard.',
      launchPrompt: {
        title: 'Updating to version {{version}}',
        body: 'The app closes and opens again on its own. This takes about a minute.',
        countdown: 'Starting in {{seconds}} s',
        skip: 'Open without updating'
      },
      status: {
        upToDate: 'Up to date',
        checking: 'Checking for updates...',
        available: 'Version {{version}} is available — switch this on to install it',
        waiting: 'Version {{version}} downloads once the booth is free',
        downloading: 'Downloading {{version}} ({{percent}}%)',
        ready:
          'Version {{version}} is ready. It installs the next time the app opens, or press Install and restart',
        installing: 'Installing version {{version}} in a moment',
        major: 'Version {{version}} is a major update — install it from the dashboard',
        error: 'Could not reach the update server. It will try again later.'
      }
    },
    buttons: {
      seeAllProjects: 'See All Projects',
      editProject: 'Edit Project',
      continue: 'Continue'
    },
    pinModal: {
      change: {
        title: 'Change PIN',
        subtitle: 'Enter a 6-digit PIN which will be used to:'
      },
      set: {
        title: 'Set PIN',
        subtitle: 'Enter a new 6-digit PIN which will be used to:'
      },
      usage: {
        unlock: 'Unlock fullscreen-mode',
        skip: 'Skip payment if needed'
      }
    },
    feedback: {
      selectCamera: 'Select a camera first!',
      pinUpdated: 'PIN updated successfully'
    }
  },
  camera: {
    prompt: {
      title: 'Click to Start Camera',
      instruction: 'Click anywhere to connect your camera'
    }
  },
  nav: {
    dashboard: 'Dashboard',
    title: 'Dreambooth',
    settings: 'Settings'
  },
  login: {
    successMessage: 'Successfully logged in!',
    networkError: 'Network error. Please try again later.',
    logoAlt: 'DreamBooth Logo',
    welcomeBack: 'Welcome Back',
    signInPrompt: 'Sign in to your account',
    email: 'Email',
    password: 'Password',
    loginButton: 'Login',
    resetPasswordPrompt: 'Need to reset password or create an account?',
    visitDashboard: 'Visit the web dashboard',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    or: 'OR',
    continueWithGoogle: 'Continue with Google',
    googleWaitingTitle: 'Finish signing in with Google',
    googleWaitingBody:
      'Your browser has opened. Sign in there, then return to this app — it will continue on its own.',
    googleWaitingCancel: 'Cancel'
  },
  projects: {
    title: 'Projects',
    subtitle: 'Manage your projects here',
    sessionCredits: 'Credits',
    refreshCredits: 'Refresh credits',
    topUpButton: 'Top Up',
    loading: 'Loading projects...',
    noProjects: 'No projects available',
    lastUpdated: 'Last Updated',
    menuOptions: 'Options',
    run: 'Run'
  },
  projectNav: {
    logoAlt: 'DreamBooth Logo',
    deviceSubscription: 'Device Subscription',
    activeSubscription: 'Active Subscription',
    planType: 'Plan Type:',
    computerName: 'Computer Name:',
    osInfo: 'OS Info:',
    activationCode: 'Activation Code:',
    changeSubscription: 'Change Subscription',
    activateSubscription: 'Activate Subscription',
    enterActivationCode: 'Enter your activation code to {{action}} this device.',
    reactivate: 'reactivate',
    activate: 'activate',
    deactivateWarning: 'Note: This will deactivate the subscription on your other device.',
    enterActivationCodePlaceholder: 'Enter your activation code',
    noCodeQuestion: "Don't have an activation code?",
    purchaseSubscription: 'Get a subscription',
    greeting: 'Hello, {{username}}!',
    subscription: 'subscription',
    failedReactivate: 'Failed to reactivate subscription',
    deviceChanged: 'Device changed successfully!',
    failedActivate: 'Failed to activate subscription',
    activatedSuccess: 'Subscription activated successfully!',
    reactivating: 'Reactivating...',
    activating: 'Activating...',
    recoverTitle: 'Restore your subscription',
    recoverSavedCode: 'Saved code',
    recoverReapply: 'Re-apply saved subscription',
    recoverCheckAgain: 'Check again',
    recoverMoveDevice: 'Move licence to this computer',
    recovering: 'Restoring...',
    recoverMoveConfirmTitle: 'Move subscription to this device?',
    recoverMoveConfirmMessage:
      'This will activate your subscription on THIS computer and deactivate it on the other device. Continue?',
    recoverExpiredNote: 'Your subscription has expired. Renew it on the dashboard, then check again.',
    recoverNotActiveNote:
      'Your subscription is not active yet. Activate it on the dashboard, then check again.',
    recoverSessionNote: 'Please sign in again to restore your subscription.',
    recoverWrongAccountNote:
      'This subscription belongs to a different account. Sign in with the correct account.',
    recoverSuccess: 'Subscription restored successfully!',
    recoverStillInvalid: 'Could not restore the subscription. Please try again or contact support.',
    getSubscription: 'Get subscription'
  },
  proTrial: {
    expired: 'Expired',
    timeRemainingDays: '{{days}} days left',
    timeRemainingRealtime: '{{hours}}h {{minutes}}m {{seconds}}s left'
  },
  interruptedSession: {
    title: 'Your session was interrupted',
    message: "You've already paid. Carry on without paying again.",
    resume: 'Continue my session',
    decline: 'Not my session',
  },
  controls: {
    settings: 'Settings',
    exitFullscreen: 'Exit Fullscreen',
    enterFullscreen: 'Enter Fullscreen',
    skipPayment: 'Skip Payment',
    exitSession: 'Exit Session',
    enterPin: 'Enter 6-digit PIN to exit fullscreen-mode',
    skipPaymentPin: 'Enter 6-digit PIN to skip payment',
    exitPaidSessionPin: 'This session is paid. Enter the 6-digit PIN to end it.',
    cancelSession: 'Cancel Session',
    cancelConfirm: 'Are you sure you want to cancel this session?',
    wrongPin: 'Oops! Wrong PIN. Please try again.',
    quitting: 'Ending session…',
    quittingMessage: 'Please wait while we save your session data.'
  },
  reprint: {
    title: 'Reprint',
    pinRequired: 'PIN Required',
    enterPinToAccess: 'Enter PIN to access reprint menu'
  },
  cameraSettings: {
    title: 'Camera Settings',
    appSettings: 'App Settings',
    appSettingsHint: {
      title: 'App Settings Guide',
      description:
        "These settings control how the app interacts with your camera. Adjust preview quality for better performance, set your preferred capture ratio, enable mirror mode for selfie-style preview, and choose between using the camera's physical shutter or capturing the preview frame. You can also enable/disable filter effects on the live preview."
    },
    powerSaving: {
      title: 'Power Saving Mode',
      enable: 'Enable power saving mode',
      description: 'Reduces camera load and prevents overheating'
    },
    frameRate: {
      title: 'Preview Frame Rate',
      adjust: 'Adjust frame rate',
      fps: 'FPS',
      description: 'Lower frame rates reduce camera load and heat generation'
    },
    previewQuality: {
      title: 'Preview Quality',
      options: {
        custom: 'Custom',
        pixelated: 'Pixelated (240p)',
        low: 'Low (480p)',
        medium: 'Medium (720p)',
        high: 'High (1080p)',
        ultra: 'Ultra (1440p)'
      }
    },
    captureRatio: {
      title: 'Capture Ratio',
      options: {
        auto: 'Auto',
        square: 'Square (1:1)',
        portrait: 'Portrait (3:4)',
        landscape: 'Landscape (4:3)',
        widePortrait: 'Wide Portrait (9:16)',
        wideLandscape: 'Wide Landscape (16:9)',
        classicPortrait: 'Classic Portrait (2:3)',
        classicLandscape: 'Classic Landscape (3:2)'
      }
    },
    mirror: {
      title: 'Mirror Preview',
      enable: 'Mirror camera preview'
    },
    useShutterCapture: {
      title: 'Use shutter capture',
      description:
        'Use the camera physical shutter for capturing photos. When disabled, the app will capture the current preview frame instead. Use shutter mode for better quality photos, or preview capture for faster response.',
      enable: 'Toggle shutter capture'
    },
    focusMode: {
      title: 'Focus',
      description:
        'When the booth focuses before taking a photo. Focusing happens during the countdown, so the shutter is not waiting on it.',
      everyPhoto: 'Every photo (default)',
      oncePerSession: 'Once per session',
      fixed: 'Fixed \u2014 never focus',
      hint: {
        'every-photo': 'Safest. Use this if guests stand at different distances.',
        'once-per-session':
          'Focuses once, then reuses it for the rest of the strip. Best with a floor marker so everyone stands in the same spot.',
        fixed:
          'The booth never touches focus. Only use this if you have pre-focused the lens manually (lens switch on MF).'
      }
    },
    flashSetupGuide: {
      title: 'Using flash? These camera-menu settings are required',
      description:
        'These settings live in the camera’s own menu and cannot be changed by the app. In live view, cameras only fire a flash they recognize as a dedicated TTL unit — with a manual single-pin flash (e.g. Godox TT520) the camera stays in silent/electronic mode and the flash never fires.',
      canonDslr: {
        title: 'Canon DSLR (1500D / 2000D class)',
        silentLv:
          'Live View shooting menu → Silent LV shoot → Disable. This is the key fix — the flash will not fire in live view until it is off.',
        expoSim: 'Exposure simulation → Disable, so the preview stays bright in a flash-dark room.',
        flashFiring: 'Flash Control → Flash firing → Enable (it silently persists as Disabled).',
        mf: 'Lens to MF, pre-focused at guest distance — in a dark room autofocus fails and blocks the shutter.',
        exposure: 'Manual exposure at or below sync speed: 1/125–1/160, f/7–f/8, ISO 100–200.'
      },
      canonMirrorless: {
        title: 'Canon mirrorless (R100, M50)',
        silentShutter:
          'Silent Shutter Mode → Off, and Shutter mode → Mechanical or Electronic 1st-curtain. Full electronic shutter never fires flash.',
        m50Firmware:
          'M50: firmware 1.0.2 or newer (official Canon fix for unreliable hot-shoe flash communication).',
        expoSimMf: 'Exposure simulation → Disable; lens to MF.'
      },
      nikon: {
        title: 'Nikon',
        silentLv: 'Photo Shooting Menu → Silent live view photography → Off.',
        releasePriority: 'Custom settings a1/a2 (AF-C/AF-S priority) → Release, not Focus.',
        mf: 'Manual focus via the lens switch (focus mode is read-only over USB on entry bodies).',
        popupAuto: 'Close the pop-up flash; mode dial off AUTO.'
      },
      sony: {
        title: 'Sony',
        silent:
          'Silent Shooting → Off, AND re-set e-Front Curtain Shutter manually — turning Silent off does not restore it.',
        pcRemote: 'USB mode → PC Remote.',
        releasePriority: 'Priority Set in AF-S → Release.',
        verify: 'Verify the flash menu is no longer greyed out.'
      },
      hardwareTip:
        'Flash hardware: run manual speedlites at ≤ 1/2 power (full-power recycle takes up to 5 s on AA batteries — shots during recycle come out black). A TTL trigger on the hot shoe (e.g. Godox X1T) is detected by the camera and disables silent modes automatically.'
    },
    cameraConfig: 'Camera Configuration',
    cameraConfigWarning: {
      title: 'Camera Configuration Warning',
      description:
        'Camera settings may vary between different camera models. Some settings might not be adjustable through this software. Please test each setting to ensure it works with your camera.'
    },
    primary: 'Primary Camera',
    secondary: 'Secondary Camera',
    qualityPresets: {
      pixelated: 'Pixelated (240p)',
      low: 'Low (480p)',
      medium: 'Medium (720p)',
      high: 'High (1080p)',
      ultra: 'Ultra (1440p)',
      ultraPlus: 'Ultra+ (1800p)',
      max4k: 'Maximum (4K · 2160p)'
    },
    captureImpact: {
      level: {
        light: 'Light upload',
        moderate: 'Heavier upload',
        heavy: 'Heavy upload'
      },
      estimate: '≈{{size}} MB per photo · ≈{{seconds}}s upload on a {{mbps}} Mbps link',
      optionEstimate: '≈{{size}} MB',
      infoLabel: 'Resolution impact details',
      infoTitle: 'Higher resolution costs upload time',
      infoBody:
        'Every captured photo is uploaded to the gallery. Doubling the resolution roughly doubles the file size, so each upload takes longer, the queue drains slower on weak venue Wi-Fi, and the local disk fills faster. Estimates assume a {{mbps}} Mbps uplink.',
      heavyWarning:
        'This resolution produces large files. Use it only on a stable, fast connection — on weak venue Wi-Fi the upload queue falls behind and guests wait longer for their photos.',
      jpegComparison: 'As JPEG: ≈{{size}} MB per photo',
      heavyWarningPng:
        'PNG is what makes these files heavy. On weak venue Wi-Fi set Photo Format, at the bottom of this section, to JPEG — or the upload queue falls behind and guests wait longer for their photos.'
    },
    captureFormat: {
      title: 'Photo Format',
      description:
        'File type of every saved photo — what the strip, prints and gallery upload are made from. Applies to new photos only.',
      options: {
        png: 'PNG · lossless, largest files',
        jpeg: 'JPEG · smaller files, faster upload'
      },
      hint: {
        png: 'Before it is saved, the frame is already JPEG-compressed at the Capture Image Quality preset, so PNG keeps that frame losslessly but mostly adds file size.',
        jpeg: 'Saved at the Capture Image Quality preset. From High upward it looks the same in prints and the gallery, at roughly a third of the PNG size.'
      }
    },
    shutterSpeed: {
      '1_4': '1/4s (slowest, brightest)',
      '1_8': '1/8s',
      '1_15': '1/15s',
      '1_30': '1/30s (default)',
      '1_60': '1/60s',
      '1_125': '1/125s',
      '1_200': '1/200s (fastest, flash sync limit)'
    },
    warning: {
      blackTitle: 'Black Image Detected',
      blackImage:
        'Camera is showing a black image. Please check if the lens cover is removed or the camera is properly connected.',
      darkTitle: 'Image Too Dark',
      darkImage: 'Camera image is too dark. Please check lighting conditions or camera settings.',
      noiseTitle: 'Poor Image Quality',
      noiseImage: 'Camera image quality is poor. Please check camera connection and settings.',
      genericTitle: 'Camera Issue Detected',
      generic: 'Camera has quality issues. Please check camera settings and connection.',
      troubleshooting: 'Troubleshooting Tips:',
      tip1: 'Ensure camera lens cover is removed',
      tip2: 'Check if camera is properly connected',
      tip3: 'Verify adequate lighting in the area',
      tip4: 'Try reconnecting the camera',
      retry: 'Retry Camera',
      ignore: 'Ignore'
    }
  },
  cameraConfig: {
    label: '{label}'
  },
  cameraGuide: {
    title: "Let's setup your camera first!",
    step1: {
      title: 'Connect Your Camera',
      bullet1: 'Ensure your camera is turned ON',
      bullet2: 'Connect it to your computer via USB cable',
      bullet3: 'Wait a few seconds for Windows to detect it'
    },
    step2: {
      title: 'Install Driver',
      storeWarning:
        'Driver installation requires administrator privileges. In the Microsoft Store version, drivers must be installed manually.',
      viewManualInstructions: 'View manual installation instructions',
      electronInstruction: 'Open Zadig to install the necessary driver.',
      webInstruction: 'Download and run Zadig to install the necessary driver.',
      processing: 'Processing...',
      openZadig: 'Open Zadig',
      downloadZadig: 'Download Zadig',
      failedToOpen: 'Failed to open Zadig'
    },
    step3: {
      title: 'In Zadig',
      instruction1: 'Click Options → List All Devices',
      instruction2: 'Find your camera in the dropdown list (e.g. Canon Digital Camera)',
      instruction3: 'Click Replace Driver and wait for completion',
      instruction4: 'Close Zadig and click Done'
    },
    buttons: {
      close: 'Close',
      done: 'Done'
    }
  },
  printerGuide: {
    title: 'Printer Setup Guide',
    faq1: {
      question: 'How to enable double inch cut?',
      answer:
        'Double inch cut allows you to print two images on one sheet that can be cut in half. Ensure your printer driver is installed correctly. Open Windows Print Preferences (right-click printer in Settings > Printers & scanners > Printer preferences), configure the paper size accordingly, and adjust print settings. The app will handle the layout automatically.'
    },
    faq2: {
      question: 'What is secondary and tertiary printer?',
      answer:
        'Secondary and tertiary printers offer two purposes: Special Frames - route specific frames to dedicated printers (useful for different frame types on different printers), and Parallel Printing - distribute print jobs across multiple printers to speed up multi-print sessions.'
    },
    faq3: {
      question: 'What is remote printer?',
      answer:
        'Remote printer allows you to print from a web interface to a printer connected to your desktop app via a secure tunnel. The desktop app creates a tunnel URL that the web interface uses to send print jobs. This is useful for printing from remote locations or web-based workflows.'
    },
    buttons: {
      close: 'Close'
    }
  },
  remotePrintSettings: {
    title: 'Remote Print Settings',
    subtitle: "Override the booth's print settings for prints sent from this device.",
    noPrinter: 'Select a remote printer first.',
    useBoothDefaults: 'Use booth defaults'
  },
  printerSettings: {
    projectBinding: {
      useForProject: 'Use this printer for the active project: {{title}}',
      noPrinter: 'No printer is selected on this slot — bound prints fall back to frame/category/default routing.',
      unbind: 'Remove binding',
      help: 'Frames and categories claimed by another printer still win; everything else from this project prints here.'
    },
    orientation: 'Orientation',
    landscape: 'Landscape',
    portrait: 'Portrait',
    pageSize: 'Page Size (microns)',
    width: 'Width',
    height: 'Height',
    dpi: 'Print Quality (DPI)',
    'dpi.horizontal': 'Horizontal',
    'dpi.vertical': 'Vertical',
    margins: 'Margins',
    'margins.default': 'Default',
    'margins.none': 'None',
    'margins.printableArea': 'Printable Area',
    'margins.custom': 'Custom',
    'margins.top': 'Top',
    'margins.bottom': 'Bottom',
    'margins.left': 'Left',
    'margins.right': 'Right',
    'margins.topTitle': 'Top margin',
    'margins.bottomTitle': 'Bottom margin',
    'margins.leftTitle': 'Left margin',
    'margins.rightTitle': 'Right margin',
    printPositionShift: 'Print Position Shift (cm)',
    testPrintInfo: {
      title: 'Test Print Configuration',
      description:
        'Use this section to test print settings and save your preferred configuration. These settings will be used for automated printing during photo sessions.'
    },
    secondaryPrinterInfo: {
      title: 'Secondary Printer Configuration',
      description:
        'Configure a secondary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
    },
    tertiaryPrinterInfo: {
      title: 'Tertiary Printer Configuration',
      description:
        'Configure a tertiary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
    },
    frameTest: {
      title: 'Frame For Print Testing',
      dragAndDrop: 'Drag and drop an image here, or click to select'
    },
    feedback: {
      selectPrinter: 'Please select a printer first',
      printInitiated: 'Printing Initialized',
      printFailed: 'Failed to print'
    },
    smartSetup: {
      loading: 'Loading printer capabilities...'
    },
    androidPrinterIp: {
      label: 'Printer IP (silent print)',
      explanation:
        'Network address of the printer, for direct printing on port 9100. Leave empty to use the Android print dialog instead.'
    },
    selectFrames: 'Select Frames',
    selectFramesDefault: 'Select Frames for Default Printer',
    selectFramesDescriptionDefault:
      'Choose which frames and categories this printer should print. Unclaimed frames still print here as a fallback.',
    routing: {
      framesForThisPrinter: 'Frames for this printer',
      allFrames: 'All frames (default)',
      onlySelected: 'Only selected frames and categories',
      defaultSummary:
        '{{frames}} frames, {{categories}} categories claimed. Unclaimed frames still print here as a fallback.',
      categories: 'Categories',
      categoriesHint:
        'Claiming a category includes every frame in it — also frames added to the category later.',
      individualFrames: 'Individual frames',
      frameCount: '{{count}} frames',
      viaCategory: 'Included via category',
      sharedBadge: 'Also on another printer'
    },
    custom: 'Custom',
    noCapabilities: 'No capabilities available - configure manually',
    preset: 'Preset',
    presetHelp: 'Select a preset or choose Custom to manually configure settings',
    noCapabilitiesHelp: 'Configure settings manually in Details section below',
    recommended: {
      label: 'Dreambooth Recommended',
      short: 'Recommended'
    },
    details: 'Preset Details',
    detailsHint: 'Orientation, page size and DPI behind this preset',
    printerDriverDefaults: {
      title: 'Use Printer Driver Defaults',
      description:
        'Use printer driver default settings for margins and DPI. Better compatibility with custom printer drivers.',
      toggle: 'Toggle printer driver defaults'
    },
    csharpPrinting: {
      title: 'C# Direct Printing (Experimental)',
      description: 'Use .NET printing for better reliability with certain printers',
      toggle: 'Toggle C# printing'
    },
    printerPurpose: 'Printer Purpose',
    printerPurposeHelpSecondary:
      'Select the purpose for the secondary printer: special frames for frame-based routing, or parallel for round-robin distribution.',
    printerPurposeHelpTertiary:
      'Select the purpose for the tertiary printer: special frames for frame-based routing, or parallel for round-robin distribution.',
    specialFramesPrint: 'Special frames print',
    parallelPrinting: 'Parallel Printing',
    positionShift: 'Print Position Shift (cm)',
    positionShiftHelp:
      'Fine-tune print alignment. Adjusts @page margins independently from margin settings.',
    positionShiftTop: 'Top',
    positionShiftBottom: 'Bottom',
    positionShiftLeft: 'Left',
    positionShiftRight: 'Right',
    positionShiftTopTitle: 'Top position shift',
    positionShiftBottomTitle: 'Bottom position shift',
    positionShiftLeftTitle: 'Left position shift',
    positionShiftRightTitle: 'Right position shift',
    remotePrinterStatus: {
      title: 'Remote Printer Status',
      rowTitle: 'Remote printer',
      stateActive: 'Active',
      stateInactive: 'Not connected',
      activeHint: 'Reachable as {{name}} — print to it from {{appUrl}}/strip',
      inactiveHint: 'Not reachable as a remote printer right now.',
      active: 'Status: Active',
      inactive: 'Status: Inactive or not connected.',
      name: 'Name: ',
      description: 'The remote printer is active. Go to {{appUrl}}/strip to print remotely.'
    },
    preview: 'Print',
    noPreview: 'No preview available',
    upload: 'Upload Template',
    copies: 'Copies:',
    copiesTitle: 'Number of copies to print',
    printResolutionScale: {
      title: 'Print Resolution Scale',
      description:
        'Adjust the resolution of the final printed image. Higher values result in better quality but larger file sizes.'
    },
    printResultMinDisplay: {
      title: 'Minimum time on the processing screen before Result',
      shortTitle: 'Hold the processing screen',
      shortHint: 'Seconds to keep it up after print and save finish, before Result. 0 = as soon as work completes.',
      unit: 's',
      description:
        'After printing and saving finish, keep the processing screen open for at least this many seconds. Use 0 to go to Result as soon as work completes. Only applies when printing is enabled and the guest is actually printing.',
      suffix: 'seconds (0 = off, max {{max}})'
    },
    defaultPrinter: 'Default Printer',
    secondaryPrinter: 'Secondary Printer',
    tertiaryPrinter: 'Tertiary Printer'
  },
  capture: {
    getReady: 'Get Ready!',
    sessionStarting: 'Session will start in',
    startNow: 'Start Now',
    orWaitCountdown: 'or wait for the countdown',
    lookAtCamera: 'Look at the Camera',
    stayStill: 'Please stay still while we capture your photo...',
    captureFailed: 'Capture Failed',
    stayStillCapturing: 'Please stay still when capturing',
    retryingMoment: 'Retrying in a moment...',
    lastCaptured: 'Last captured',
    noPhotoYet: 'No photo yet',
    recording: 'REC',
    errorStartingSessionRecorder: 'Error starting session recorder',
    errorProcessingSessionVideo: 'Error processing session video',
    selfPhotoScanQR: 'Scan to trigger from phone',
    selfPhotoQRAlt: 'Trigger from phone',
    selfPhotoEndSession: 'End session',
    selfPhotoPhotoCount: '{{count}} photos',
    selfPhotoAutoLoop: 'Auto',
    selfPhotoSessionTimer: 'Session time',
    selfPhotoEndSessionBtn: 'End Session',
    selfPhotoAutoLoopOn: 'Auto capture on',
    selfPhotoAutoLoopOff: 'Auto capture off',
    selfPhotoManualOnly: 'Manual',
    selfPhotoTriggerUrl: 'Trigger URL',
    selfPhotoTriggerNow: 'Trigger capture now',
    removePhoto: 'Remove photo'
  },
  stickers: {
    title: 'Stickers',
    pageTitle: 'Decorate your strip',
    tabStickers: 'Stickers',
    tabDraw: 'Draw',
    autoLayout: 'Auto layout',
    replaceTitle: 'Replace all stickers?',
    replaceMessage: 'The stickers you placed will be removed and laid out again automatically.',
    clear: 'Clear all',
    brush: 'Brush',
    color: 'Colour',
    customColor: 'Any colour',
    size: 'Size',
    brushTypes: {
      solid: 'Solid',
      dashed: 'Dashed',
      dotted: 'Dotted',
      marker: 'Marker',
      pencil: 'Pencil',
      airbrush: 'Airbrush'
    },
    shuffle: 'Shuffle',
    none: 'No stickers',
    undo: 'Undo',
    redo: 'Redo',
    remove: 'Remove sticker',
    processing: 'Processing...',
    processingMessage: 'Please wait while we finish your strip',
    preparing: 'Preparing your strip...'
  },
  select: {
    title: 'Arrange your photos',
    instruction: 'Tap photos to select, swipe to move, pinch to zoom',
    processingPhotos: 'Processing Photos',
    pleaseWait: 'Please wait while we process your photos...',
    printPhotos: 'Print Photos',
    tapToFill: 'Tap your photos to fill the print slots',
    swipeInstructions: 'Swipe to move, pinch to zoom',
    mirror: 'Mirror',
    selectionLocked: 'Selection locked',
    processing: 'Processing...',
    processingVideo: 'Processing Video...',
    next: 'Next ({{count}}/{{max}})',
    capture: 'Capture',
    selected: 'Selected',
    clickToSelect: 'Click to Select',
    continue: 'Continue',
    errorGif: 'Error creating animated GIF',
    stripExportFailed: 'Failed to create strip preview',
    errorVideo: 'Error processing video',
    errorProcessing: 'There was an error processing your photos. Please try again.',
    selectPhotos: 'Select {{count}}/{{max}} photos',
    exportFailed: 'Failed to generate strip. Continuing without frame...',
    exportTimeout: 'Strip generation is taking longer than expected. Please wait...'
  },
  retake: {
    title: 'Not Happy with Some Photos?',
    headline: 'Tap photos to retake',
    maxHint: 'Max {{count}}',
    pillSessions: '{{count}} left · Session {{current}} of {{max}}',
    remainingSessions: 'Retakes left: {{count}}',
    selectUpTo: 'Select up to',
    photos: 'photos',
    toRetake: 'to retake',
    selectToRetake: 'Select the photos you want to retake',
    sessionCount: 'Retake session {{current}} of {{max}}',
    photoAlt: 'Photo {{number}}',
    photoSelectedAlt: 'Photo {{number}} selected for retake',
    overlayRetake: 'Retake',
    overlaySelected: 'Selected',
    selectedToRetake: 'Selected to Retake',
    clickToRetake: 'Click to Retake',
    retakeSelected: 'Retake Selected Photos ({{count}})',
    retakeCount: 'Retake ({{count}})',
    retakeAll: 'Retake All',
    keepAll: 'Keep all',
    explanation: 'Selected photos will be deleted and you will take new ones. This cannot be undone.',
    confirmContinue: 'Continue with all photos?',
    confirmContinueHint: 'You can go back to retake any photo, or continue to the next step.',
    confirmKeepAllTitle: 'Are you good with all the photos?',
    confirmKeepAllMessage: 'You can choose to retake some photos or continue to the next step.',
    back: 'Back',
    continue: 'Continue',
    confirmKeepAllNo: 'No, I want to retake my photos',
    confirmKeepAllYes: 'Yes, I want to continue',
    confirmingKeepAll: 'Continuing...'
  },
  result: {
    videoReady: 'Your Video Is Ready!',
    photosReady: 'Your Photos Are Ready!',
    scanQrInstruction: 'Scan the QR code below to access your {{type}}',
    video: 'video',
    digitalPhotos: 'digital photos',
    photoStripAlt: 'Photo Strip',
    qrCodeAlt: 'QR Code',
    sessionId: 'Session ID:',
    scanToView: 'Scan to view gallery',
    videoRecording: 'Video Recording',
    photoStrip: 'Photo Strip',
    backToHome: 'Back to Home',
    satisfaction: {
      question: 'How was your photobooth experience?',
      rateExperience: 'Rate your experience',
      thankYou: 'Thank you for your feedback!',
      starAlt: 'Rate {{rating}} stars'
    },
    feedback: {
      title: 'Tell us more',
      titleLow: 'Sorry that missed the mark',
      subtitle: 'Rate whichever parts matter — skip the rest.',
      tellUsMore: 'Tell us more',
      skip: 'Skip',
      submit: 'Send',
      submitCount: 'Send ({{count}})',
      thanks: 'Thanks — that helps us fix it.',
      starAlt: 'Rate {{aspect}} {{rating}} out of 5',
      aspects: {
        photo: { label: 'Photo quality', hint: 'Blurry, dark, odd colours' },
        print: { label: 'The print', hint: 'Did not print, cut off, faded' },
        timing: { label: 'Shot timing', hint: 'Countdown too fast or too slow' },
        choices: { label: 'Frames & filters', hint: 'Not enough to choose from' },
        payment: { label: 'Payment', hint: 'QR or checkout was awkward' },
        digital: { label: 'Digital photos', hint: 'Hard to scan, save or share' }
      }
    },
    emailGallery: {
      title: 'Email your gallery link',
      description: "We'll send one message with a link to this gallery.",
      emailLabel: 'Email',
      emailPlaceholder: 'you@example.com',
      sendEmail: 'Send',
      sendingEmail: 'Sending…',
      invalidEmail: 'Enter a valid email address.',
      shareSuccess: 'Sent! Check your inbox.',
      shareError: 'Could not send. Try again.',
      shareNetworkError: 'Connection problem. Try again.',
      close: 'Close'
    },
    postSessionAi: {
      title: 'AI enhancement',
      subtitle: 'Optional polish for your photos.',
      guestPromptLabel: 'Add a note (optional)',
      guestPromptPlaceholder: 'e.g. more vintage',
      skip: 'Skip',
      apply: 'Apply AI',
      applying: 'Applying…',
      continue: 'Continue',
      errorGeneric: 'Something went wrong. Tap Skip to continue.',
      insufficientCredits: 'AI is unavailable right now. Tap Skip to continue.',
      missingStrip: 'Strip not ready yet. Tap Skip to continue.',
      missingRaw: 'No photos available for AI. Tap Skip to continue.',
      missingRawNeedHttps: 'These photos are still uploading. Tap Skip to continue.',
      timeout: 'AI is taking longer than usual. Tap Skip to continue.',
      offline: 'AI needs an internet connection. Tap Skip to continue.',
      stripUploading: 'Finishing the strip upload…',
      previewBefore: 'Before',
      previewAfter: 'After',
      previewCompareHeading: 'Before and after preview',
      previewLoading: 'Loading preview…',
      previewMissingUrls: 'No preview yet.',
      previewLoadError: 'Could not load preview. Try again.',
      previewImageLoadError: 'Could not load preview. Try again.',
      filmstripAriaLabel: 'Your session photos',
      filmstripCaption: 'Session photos',
      filmstripHelper: 'Tap a photo to generate AI mode out of it.',
      filmstripThumb: 'Session photo',
      filmstripThumbWithAi: 'Session photo (AI ready)',
      aiProcessedTooltip: 'AI version ready',
      dragToCompareHint: 'Drag to compare before and after.',
      introBadge: 'AI effect',
      introHeadline: 'See your photos, transformed',
      introBody:
        'A one-of-a-kind AI version of your favourite shot. Try it on your own photos next.',
      introCta: 'Try it on my photos',
      previewEmptyMessage: 'No comparison preview yet. You can still apply AI below.',
      pickOneSpecial: 'Pick 1 special photo to enhance with AI.',
      rawOnce: 'AI can enhance one photo per session.',
      rawUsedBadge: 'AI used',
      processing: 'Creating your photo…',
      processingHint: 'This only takes a moment. Please hold on.',
      done: "Here's your AI photo!",
      resultMayVary: 'AI results can vary — keep it or discard.',
      discard: 'Discard'
    }
  },
  filter: {
    nextFilter: 'Next filter',
    prevFilter: 'Previous filter'
  },
  frame: {
    framesListbox: 'Frames',
    title: 'Choose your frame',
    count: '{{count}} frames',
    previewAlt: 'Preview'
  },
  payment: {
    skipGuide:
      'Need to skip payment? Click the money icon in the top right corner and enter the PIN.',
    qrisLogoAlt: 'QRIS logo',
    rail: { qris: 'QRIS', online: 'QR', upi: 'UPI' },
    qrCodeAlt: 'QR Code',
    qrLoadFailed: "Couldn't load the QR code.",
    qrReload: 'Reload QR code',
    automaticCheck: 'Payment status will be checked automatically.',
    card: {
      cantScan: "Can't scan?",
      payByCard: 'Pay by card',
      holdTitle: 'Please ask our staff',
      holdBody: 'Show them this screen — they will run your card on the terminal.',
      holdHeld: 'Your session is held for 5 minutes.',
      backToPayment: 'Back to {{rail}}',
      confirmStaff: 'Confirm card payment (staff)',
      pinTitle: 'Confirm card payment',
      pinSubtitle: 'Staff only. Enter the booth PIN.',
      codePlaceholder: 'Approval code from the terminal receipt',
      codeOptionalPlaceholder: 'Approval code (optional) — press Enter to confirm',
      codeTitle: 'Approval code',
      codeSubmit: 'Confirm payment',
      codeRequired: 'Enter the approval code from the receipt.',
      confirmed: 'Card payment recorded',
      terminalTitle: 'Pay on the card terminal',
      terminalSending: 'Sending the amount to the terminal...',
      terminalTapCard: 'Tap, insert or swipe your card on the terminal',
      terminalCheck: 'Check the terminal to finish the payment',
      terminalDeclined: 'The card was declined. Try again or use another card.',
      terminalCanceled: 'The payment was cancelled on the terminal.',
      terminalExpired: 'Payment time is up',
      terminalFinishing: 'Finish the payment on the terminal. This screen continues on its own.',
      terminalTryAgain: 'Try again',
      terminalCancelOnDevice: 'Cancel the payment on the terminal first.',
      terminalAlreadyPaid: 'That payment went through. One moment.',
      terminalUnavailable: 'The card terminal is not available. Please pay with the code on screen.'
    },
    scanInstruction: 'Scan the QR code with your payment app',
    timeToPay: 'Complete payment in',
    cancelOrder: 'Cancel and start over',
    expired: 'Payment code expired',
    restartPrompt: 'Click to restart transaction',
    restart: 'Restart Transaction',
    successMessage: 'Payment successful!',
    notSuccessful: 'Payment not successful. Please try again.',
    offline: 'No internet connection',
    offlineMessage: 'Please check your internet connection and try again',
    retry: 'Retry Payment',
    reload: 'Reload',
    checkingConnection: 'Checking connection...',
    networkError: 'Network error occurred',
    snapError: 'Payment failed. Please try again.',
    wrongCode: 'Incorrect code. Please try again.',
    enterCode: 'Enter Code to Continue',
    offlineMode: 'Offline Mode - No Internet',
    autoRestart: 'Auto-restart in',
    checkAgain: 'I have paid, check again',
    notReceivedYet: 'Payment not received yet. Please check your phone.',
    snapReady: 'Payment gateway ready',
    snapOpening: 'Opening payment...',
    openSnap: 'Open Payment',
    payNowTitle: 'PAY TO START SESSION',
    codeEntry: {
      title: 'Enter Access Code',
      subtitle: 'Enter 6-digit code to bypass payment in offline mode'
    }
  },
  checkout: {
    title: 'Review Your Order',
    print: 'Print',
    choosePrints: 'Choose number of prints',
    minus: 'Minus',
    plus: 'Plus',
    gif: 'GIF',
    getAnimated: 'Get animated image',
    recording: 'Recording',
    getRecording: 'Get photo session recording',
    video: 'Video',
    recordVideo: 'Record a video',
    free: 'Free',
    subtotal: 'Subtotal:',
    discount: 'Discount:',
    total: 'Total:',
    remainingTime: 'Remaining Time:',
    havePromo: 'Have a voucher code?',
    proceedToPayment: 'Proceed to Payment',
    enterPromo: 'Enter promo code',
    voucherCodeTitle: 'Voucher code',
    voucherCodePlaceholder: 'Type or paste your code',
    promoApplied: 'Promo code applied successfully!',
    invalidPromo: 'Invalid promo code',
    cancel: 'Cancel',
    apply: 'Apply',
    promoValidationFailed: 'Failed to validate promo code. Please try again.',
    digital: 'Digital Photos',
    getDigital: 'Get digital copies of your photos',
    keyboard: 'Keyboard',
    scanQR: 'Scan QR',
    scanningQR: 'Scanning for QR code...',
    qrInstructions:
      'Hold a QR code in front of your camera. The promo code will be automatically detected and applied.',
    qrScannerActive: 'QR Scanner Active',
    pointCamera: 'Point your camera at a QR code containing the promo code',
    failedToCaptureImage: 'Failed to capture image from camera',
    errorScanningQR: 'Error scanning QR code',
    addNumberOfPeople: 'Please select the number of people.',
    choosePeople: 'Choose number of people',
    pax: 'Number of People'
  },
  frameSurcharge: {
    badge: '+{{price}}',
    lineItem: 'Frame add-on'
  },
  frameCategoryBasePrice: {
    badge: '+{{price}}',
    lineItem: 'Base price'
  },
  bahela: {
    welcome: {
      title: 'Welcome',
      subtitle: 'LADIES AND GENTLEMEN!',
      promoButton: 'Promo Code',
      startButton: 'Start Session',
      tutorial: 'Tutorial',
      noCredits: 'No credits available. Please top up your credits.',
      startError: 'Failed to start session. Please try again.',
      discountApplied: 'Discount is already applied, please start the session!',
      promoValidationFailed: 'Gagal memvalidasi kode promo. Silakan coba lagi.',
      digital: 'Foto Digital',
      getDigital: 'Dapatkan salinan digital foto Anda',
      next: 'Selanjutnya',
      or: 'atau',
      savePathNew: 'File akan disimpan di folder Pictures/Dreambooth Gallery/{{date}} Anda.'
    },
    promoModal: {
      title: 'Enter Promo Code',
      placeholder: 'Promo Code',
      submit: 'Submit',
      cancel: 'Cancel',
      promoExpired: 'Promo code has expired',
      invalidPromo: 'Invalid promo code',
      percentageDiscountApplied: "{{value}}% discount applied, let's start the session!",
      fixedDiscountApplied: "{{value}} discount applied, let's start the session!",
      promoError: 'Error validating promo code',
      cashVoucherApplied: 'Cash voucher applied.',
      customFrameVoucherApplied:
        'Custom frame voucher applied. Your frame will be selected at the kiosk.'
    },
    tutorial: {
      title: 'BAHeLA Photobooth Tutorial'
    }
  },
  recording: {
    title: 'Recording',
    type: 'Recording Type',
    modes: {
      live: 'Live Mode',
      story: 'Story Mode',
      session: 'Session Mode'
    },
    description: {
      recording: 'Live mode video of the photo strip',
      strip_live: 'Live mode video the photo strip',
      strip_story: 'Story mode video the photo strip',
      session_recording: 'Recording of the entire photo session'
    },
    toggle: 'Toggle recording package'
  },
  view: {
    noCreditsModal: {
      title: 'Subscription or credits needed',
      description:
        "Your subscription has ended or you have no credits left. Renew in the dashboard or add credits to continue using the app without interruption.",
      purchaseSubscription: 'Activate Subscription',
      topUpCredits: 'Top Up Credits',
      close: 'Close'
    },
    subscriptionValidUntil: 'Your subscription is valid until {date}.'
  },
  modeSelect: {
    title: 'Which photo do you want?',
    subtitle: 'Each mode has its own packages, frames and print size.',
    from: 'from {{price}}',
    unavailable: 'Not available on this booth'
  },
  consent: {
    title: 'Photo Publication Consent',
    subtitle: "We'd love to showcase your amazing photos!",
    question: 'Can we use your photos for publication?',
    description:
      "Your beautiful photos could be featured on our website, social media, or marketing materials to inspire others. We'll never share your personal information.",
    yourPhotos: 'Your Photos',
    yourVideo: 'Your Video',
    photoStripAlt: 'Photo Strip',
    benefits: {
      title: 'What this means:',
      featured: 'May be featured on our social media or website',
      inspire:
        'Let your unique style and creativity inspire others to try our photobooth experience',
      privacy: 'No personal information will be shared publicly'
    },
    accept: "Yes, I'd love to!",
    decline: 'No, thanks',
    footer:
      'You can change this preference anytime by contacting our support team at dreamboothstudio.com.',
    newspaper: {
      headline: 'READY TO BE FAMOUS?'
    }
  },
  onboarding: {
    congratulations: {
      title: 'Congratulations!',
      subtitle: "You've successfully completed your first photobooth session!",
      nextSteps: "Here's what you can do next:",
      customize: 'Customize Your Project',
      analytics: 'View Analytics',
      gallery: 'Browse Gallery',
      payment: 'Manage Subscription',
      getStarted: 'Get Started'
    }
  },
  newspaper: {
    welcome: {
      headline: 'Newspaper Photobooth: {{content}}',
      takePhoto: 'Take Photo',
      subheadline: "{{city}}'s Most Beautiful {{year}}",
      subheadlineDefault: "World's Most Beautiful {{year}}",
      introWithCity: 'Citizens from {{city}} have gathered for this historic photo opportunity, coming together to capture memories that celebrate their community and local heritage.',
      introDefault: 'Local citizens have gathered for this historic photo opportunity, coming together to capture memories that celebrate their community and shared moments.',
      aboutCity: 'About {{city}}',
      photoboothSection: 'Photobooth That Brings It All',
      dreamboothSection: 'Creativity Unleashed By Innovative Software'
    },
    checkout: {
      steps: {
        title: 'How to Use',
        step1: 'Step 1: Review your order and select packages',
        step2: 'Step 2: Complete payment if required',
        step3: 'Step 3: Choose your filter style',
        step4: 'Step 4: Select your photo frame',
        step5: 'Step 5: Take your photos',
        step6: 'Step 6: Review and retake if needed',
        step7: 'Step 7: Get your photos!'
      }
    },
    filter: {
      timeRemaining: 'Time Remaining'
    },
    select: {
      timeRemaining: 'Time Remaining'
    }
  },
  strip: {
    step1Title: 'Step 1: Select Strip',
    step1Desc: 'Choose a frame and upload photos if required.',
    step2Title: 'Step 2: Upload Configuration',
    step2Desc: 'Choose whether to upload to gallery and configure project/session.',
    step3Title: 'Step 3: Remote Printing Configuration',
    step3Desc: 'Select a remote printer and configure print settings.',
    step4Title: 'Step 4: Preview & Generate',
    step4Desc: 'Review your configuration and generate the strip.',
    modeConfig: 'Create New',
    modeUpload: 'Upload existing photo strip',
    useExistingStrip: 'Use existing photo strip',
    chooseFromGallery: 'Choose from gallery',
    selectFrame: 'Please select a frame',
    selectFrameTitle: 'Select Photo Strip Frame',
    uploadStripImage: 'Please upload a photo strip image',
    uploadPhotos: 'Click to upload photos',
    uploadHint: 'Select up to {{count}} photos',
    remove: 'Click to Remove',
    mirror: 'Mirror',
    uploadToGallery: 'Upload to gallery',
    selectProjectTitle: 'Select Project',
    enterSessionId: 'Enter Session ID',
    validatingSession: 'Validating session...',
    sessionValid: 'Session ID valid',
    sessionInvalid: 'Session ID invalid or not authorized',
    printStrip: 'Print strip',
    selectRemotePrinterTitle: 'Select Remote Printer',
    noPrinterSelected: 'No printer selected.',
    preview: 'Preview',
    previewHint: 'Preview of your photo strip',
    generating: 'Generating...',
    generate: 'Generate Strip',
    downloading: 'Downloading...',
    uploading: 'Uploading...',
    printing: 'Printing...',
    errorFetchingData: 'Error fetching data',
    errorFetchingPrinters: 'Failed to fetch remote printers',
    exportFailed: 'Failed to create strip preview',
    noUser: 'User not authenticated',
    invalidSessionId: 'Not authorized to upload to this gallery',
    uploadTimeout: 'Upload is taking longer than expected. Continuing...',
    uploadSuccess: 'Strip uploaded successfully',
    uploadFailed: 'Failed to upload strip - Invalid Session ID',
    printSuccess: 'Print job sent successfully!',
    printFailed: 'Print failed: {{error}}',
    base64Error: 'Failed to prepare image for printing.',
    printError: 'Error sending print job.',
    generationError: 'Error generating photo strip',
    uploaded: 'Uploaded:',
    step2: {
      upload: 'Upload',
      skip: 'Skip'
    },
    step3: {
      print: 'Print',
      done: 'Done'
    },
    galleryModal: {
      title: 'Select Photos from Gallery',
      selectUpTo: 'Select up to {{count}} photos',
      loading: 'Loading photos...',
      loadingMore: 'Loading more photos...',
      noPhotos: 'No photos found',
      errorFetching: 'Failed to fetch photos',
      maxSelected: 'You can only select up to {{count}} photos',
      noSelection: 'Please select at least one photo'
    }
  }
}

// Spanish translations (neutral Latin American Spanish)
// Typed against `enTranslations` so a missing or misspelled key fails `tsc`
// instead of silently falling back to English at runtime.
const esTranslations: typeof enTranslations = {
  duplicateLaunch: {
    title: 'Esta cabina ya está abierta',
    secondBooth: 'La aplicación se abrió dos veces. Cierra esta ventana y usa la cabina que ya está abierta.',
    closeIn: 'Cerrar esta ventana ({{seconds}})',
    closeByHand: 'Pulsa Alt+F4 para cerrar esta ventana.',
    keepSecondBooth: 'Usar como segunda cabina',
    noOtherWindow: '¿No hay otra ventana de la cabina? Reinicia el equipo.',
    deadTitle: 'Dreambooth ya está abierto en este equipo',
    deadBody:
      'Esta ventana no puede leer la configuración, la sesión ni el código de activación de esta cabina, por eso parece que se reinició. Suele pasar cuando la aplicación se abre dos veces. No introduzcas el código de activación aquí.',
    closeNow: 'Cerrar esta ventana',
    deadNoOtherWindow:
      '¿No hay otra ventana de Dreambooth? Cierra Dreambooth por completo o reinicia el equipo y ábrelo una sola vez. Actualizar a la última versión evita que vuelva a pasar.',
    deadContinue: 'Seguir en esta ventana de todos modos'
  },
  crash: {
    title: 'La cabina se detuvo',
    body: 'Algo falló y la cabina no pudo continuar. Las sesiones ya subidas están a salvo. Recarga para volver a iniciar la cabina.',
    reload: 'Recargar la cabina',
    contact: 'Si vuelve a pasar, envíanos esta línea:'
  },
  cameraTest: {
    label: 'Foto de prueba',
    shoot: 'Tomar una foto de prueba',
    shooting: 'Tomando la foto...',
    empty: 'Revisa la foto real, no solo la vista previa.',
    hint: 'Esto es lo que recibe el invitado. Toca en cualquier lugar para cerrar.',
    open: 'Abrir la foto de prueba',
    noFrame: 'La camara no devolvio ninguna foto. Intentalo de nuevo.',
    copy: 'Copiar la foto de prueba',
    copied: 'Foto copiada.',
    copyFailed: 'No se pudo copiar la foto.',
    save: 'Guardar la foto de prueba',
    saved: 'Foto guardada.'
  },
  cameraPresets: {
    title: 'Copiar ajustes de otra cabina',
    count: '{{count}} guardados',
    empty: 'Aun no hay ajustes guardados. Esta cabina guarda los suyos al cerrar esta ventana, y desde ahi puedes copiarlos a otra cabina.',
    choose: 'Elige la cabina de la que copiar',
    use: 'Usar estos ajustes',
    checking: 'Comprobando la camara...',
    unknownProject: 'Cabina desconocida',
    unknownModel: 'desconocida',
    approxWarning: 'Esto se guardo en otra camara ({{model}}). Los valores que no existan aqui se omitiran, y los cercanos se aproximaran.',
    keep: 'Guardar como referencia',
    keptTitle: 'Guardado como referencia - el guardado automatico no lo toca',
    keepFailed: 'No se pudo guardar ({{detail}}). Intentalo de nuevo.',
    rename: 'Cambiar nombre',
    renameLabel: 'Nombre',
    renameFailed: 'No se pudo cambiar el nombre ({{detail}}). Intentalo de nuevo.',
    modifiedFrom: 'Modificado desde {{label}}',
    revert: 'Revertir',
    revertGone: 'Esa configuracion guardada ya no esta disponible.',
    planFailed: 'No se pudo leer la camara. Revisa el cable e intentalo de nuevo.',
    planSummary: '{{apply}} cambiaran - {{adjust}} aproximados - {{skip}} omitidos',
    alreadyMatching: '{{count}} ajustes ya coinciden - no se tocaran.',
    showDetail: 'Ver detalles',
    hideDetail: 'Ocultar detalles',
    wanted: 'se pedia {{v}}',
    confirm: 'Si, cambiar la camara',
    applyNotice: 'La vista previa se pausa mientras se aplica. No desconectes la camara.',
    applying: 'Aplicando {{done}} de {{total}}...',
    stop: 'Detener',
    resultSummary: '{{applied}} aplicados - {{skipped}} omitidos',
    resultFailed: '{{count}} fallaron',
    reconnect: 'Desconecta y vuelve a conectar la camara antes de iniciar una sesion.',
    unhealthy: 'La camara dejo de responder. Desconectala y vuelve a conectarla antes de la proxima sesion.',
    undo: 'Deshacer - restaurar los ajustes anteriores',
    previewPaused: 'Vista previa en pausa mientras se aplican los ajustes',
    tier: { exact: 'Misma camara', brand: 'Misma marca', cross: 'Otra camara' },
    groups: {
      choose: 'Elegir que copiar',
      hide: 'Listo',
      summary: 'Copia: {{list}}.',
      none: 'No hay nada seleccionado para copiar.',
      exposure: { title: 'Que tan clara sale la foto', examples: 'ISO, velocidad de obturacion, apertura, medicion' },
      image: { title: 'Color y calidad', examples: 'Balance de blancos, estilo de imagen, tamano de archivo' },
      behaviour: { title: 'Como dispara la camara', examples: 'Modo de enfoque, modo de disparo, horquillado' },
      geometry: { title: 'Como se encuadra la imagen', examples: 'Espejo, rotacion, relacion de recorte - dependen de como esta construida fisicamente la cabina. Copialos solo entre cabinas identicas.' }
    },
    skip: {
      missing: 'esta camara no tiene ese ajuste',
      readonly: 'se ajusta en el cuerpo de la camara, no por USB',
      noChoice: 'no hay un valor comparable en esta camara',
      modeLocked: 'gira el dial de modo a M para aplicar esto',
      groupOff: 'grupo desactivado arriba',
      denied: 'no es seguro cambiarlo automaticamente',
      crossDisabled: 'la copia entre camaras distintas esta desactivada'
    }
  },
  diskCleanup: {
    title: 'Almacenamiento',
    free: '{{size}} libres',
    lowTitle: 'Disco casi lleno',
    scanning: 'Revisando archivos…',
    running: 'Limpiando…',
    sessions: '{{count}} sesiones',
    nothing: 'Nada lo bastante antiguo para borrar.',
    protected: '{{count}} sin subir, se conservan',
    deleted: '{{count}} sesiones borradas',
    skippedOffline: '{{count}} conservadas: servidor no disponible',
    skippedNotUploaded: '{{count}} conservadas: aún no están en el servidor',
    failed: 'La limpieza falló. Inténtalo de nuevo.',
    confirm: 'Limpiar'
  },
  common: {
    continue: 'Continuar',
    cancel: 'Cancelar',
    save: 'Guardar',
    change: 'Cambiar',
    refresh: 'Actualizar',
    select: 'Seleccionar',
    edit: 'Editar',
    download: 'Descargar',
    autoStart: 'Inicio automático',
    back: 'Atrás',
    yes: 'Sí',
    no: 'No',
    close: 'Cerrar',
    logout: 'Cerrar sesión',
    upgrade: 'Mejorar plan',
    next: 'Siguiente',
    or: 'o',
    loading: 'Iniciando...',
    retry: 'Reintentar',
    savePathNew: 'Los archivos se guardan en tu carpeta Imágenes/Dreambooth Gallery/{{date}}.'
  },
  settings: {
    groups: {
      navLabel: 'Secciones de ajustes',
      camera: 'Cámara',
      printing: 'Impresión',
      booth: 'Cabina',
      files: 'Archivos',
      payments: 'Pagos'
    },
    payments: {
      intro: 'Para clientes que no pueden pagar con {{rail}}. La tarjeta se ofrece debajo, y {{rail}} sigue siendo la opción principal en pantalla.',
      introNoOnline: 'El pago en línea todavía no está disponible en esta región, así que los clientes canjean un código de descuento. Un terminal de tarjeta es la única forma de cobrar en la cabina.',
      railQris: 'QRIS',
      railOnline: 'el código QR',
      none: 'Sin pago con tarjeta',
      noneHint: 'Sin botón de tarjeta. Los clientes pagan solo con {{rail}}.',
      noneHintNoOnline: 'Sin botón de tarjeta. Los clientes solo pueden canjear un código de descuento.',
      manual: 'Terminal de tarjeta, confirmado por el personal',
      manualHint: 'El cliente toca el botón de tarjeta, el personal pasa la tarjeta por el terminal y luego confirma en la cabina con el PIN.',
      connected: 'Terminal conectado',
      connectedHint: 'La cabina envía el importe al terminal y el resultado vuelve solo.',
      connectedUnavailable: 'Un lector Nayax (conecta primero tu cuenta de Nayax en Studio → Wallet), o Mercado Pago Point en México.',
      nayaxLoading: 'Revisando tu cuenta de Nayax...',
      nayaxAccountError: 'No se pudo contactar a Studio para revisar tu cuenta de Nayax.',
      nayaxNoAccount: 'Primero conecta tu cuenta de Nayax en Studio: Wallet → Add payment gateway → Nayax card reader.',
      nayaxSerial: 'Número de serie del lector',
      nayaxSerialInvalid: 'Solo dígitos: el número de serie del lector o su machine id de Nayax.',
      nayaxChecking: 'Revisando...',
      nayaxPair: 'Revisar y vincular',
      nayaxPaired: 'Vinculado: {{serial}} en {{account}}',
      nayaxUnpair: 'Desvincular',
      terminalName: 'Nombre del terminal',
      terminalNamePlaceholder: 'p. ej. Terminal de tarjeta — recepción',
      terminalNameHint: 'Se muestra al personal al confirmar y se guarda en cada sesión con tarjeta.',
      requireCode: 'Exigir el código de aprobación',
      requireCodeHint: 'Del recibo del terminal. Es lo que permite conciliar un día de sesiones con tarjeta contra el lote del terminal.',
      showMarks: 'Mostrar marcas Visa / Mastercard',
      showMarksHint: 'Ayuda al visitante a reconocer el botón.',
      terminalTitle: 'Terminal',
      terminalLoading: 'Buscando terminales...',
      terminalNotApplicable:
        'Tu pasarela de pago activa no es Mercado Pago. Activa tu cuenta de Mercado Pago en el panel para usar una terminal conectada.',
      terminalError: 'No se pudieron cargar las terminales: {{reason}}',
      terminalRetry: 'Intentar de nuevo',
      terminalNone: 'No se encontraron terminales Point en tu cuenta de Mercado Pago.',
      terminalSerial: 'Serie {{serial}}',
      terminalReady: 'Lista (modo PDV)',
      terminalNotPdv: 'Todavía no está en modo PDV',
      terminalSwitchToPdv: 'Cambiar a modo PDV',
      terminalSwitching: 'Cambiando...',
      terminalPdvDone: 'Cambiada a modo PDV. Reinicia la terminal para terminar.',
      terminalPdvFailed: 'No se pudo cambiar la terminal: {{reason}}',
      terminalSaved: 'Esta cabina ahora cobra en esa terminal',
      terminalCleared: 'Ninguna terminal vinculada con esta cabina',
      terminalRefresh: 'Actualizar la lista',
      terminalMissing: 'La terminal guardada ({{serial}}) no está en esta cuenta de Mercado Pago.',
      terminalClear: 'Dejar de usarla',
      terminalNotPicked: 'Elige una terminal. Hasta entonces la cabina no muestra el botón de tarjeta.',
      terminalRestartHint:
        'Una terminal solo cobra desde la cabina en modo PDV. Después de cambiarla, reiníciala y comprueba que su modo de vinculación diga Punto de Venta (PDV).'
    },
    tiers: {
      advancedPrinter: 'Avanzado',
      advancedPrinterHint: 'Márgenes, escala, resolución, posición y método de impresión',
      advancedCamera: 'Avanzado',
      advancedCameraHint: 'Calidad, rotación, preajustes y configuración de la cámara'
    },
    title: 'Configuración del booth',
    boothGroups: {
      lockdown: 'Bloqueo',
      lockdownHint: 'Mantén a los invitados dentro del booth.',
      startup: 'Arranque',
      startupHint: 'Vuelve sola tras un reinicio o un corte de luz.',
      screen: 'Pantalla',
      screenHint: 'Dónde se muestra el booth y qué hace cuando no hay nadie.'
    },
    project: 'Proyecto',
    credits: {
      title: 'Créditos',
      refresh: 'Actualizar créditos',
      unlimited: 'Ilimitados'
    },
    camera: {
      title: 'Cámara',
      noPreview: 'Vista previa no disponible',
      selectCamera: 'Seleccionar cámara',
      defaultCamera: 'Predeterminada',
      noDevicesFound: 'No se encontraron cámaras',
      detect: 'Detectar cámara...',
      troubleshoot: '¿Problemas de conexión? Vuelve a conectar el cable USB y actualiza.',
      guideDescriptionPrefix: 'Consulta ',
      guideLink: 'la guía de cámara',
      guideDescriptionSuffix: 'para conectar una cámara DSLR/mirrorless',
      dslrInstruction:
        'Para conectar una cámara DSLR, sigue estas instrucciones de instalación y elige "Detectar cámara"',
      settings: 'Ajustes de cámara',
      settingsHint: 'Proporción, espejo, rotación, calidad y preajustes',
      debugFps: 'Mostrar FPS sobre la vista previa',
      eject: 'Expulsar cámara',
      ejecting: 'Expulsando cámara...',
      ejected: 'Cámara expulsada correctamente',
      ejectFailed: 'No se pudo expulsar la cámara',
      ejectAndRestart: 'Cámara expulsada, reiniciando la app...',
      ejectDevMode: 'Cámara expulsada. Reinicia la app manualmente en modo de desarrollo.',
      replugRequired: 'Vuelve a conectar el cable USB o actualiza la app',
      driverStatus: {
        installed: 'Driver listo',
        uninstalled: 'Desinstalado/Ocupado',
        wrongDriver: 'Driver incorrecto — ejecuta Zadig',
        disconnected: 'Desconectada',
        unknown: 'No se pudo comprobar el driver'
      },
      editModeOptional:
        'Seleccionar una cámara es opcional en el modo de edición. Puedes continuar sin elegir una.',
      instructionLink: 'Instrucciones',
      unknownWebcam: 'Cámara web desconocida'
    },
    paperRoll: {
      title: 'Papel',
      notSet: 'Sin configurar. Ingresa la cantidad al cargar un rollo.',
      remaining: 'Quedan ~{{left}} de {{total}}',
      loaded: 'cargado {{date}}',
      newRoll: 'Rollo nuevo',
      countPlaceholder: 'Restantes en impresora',
      errorCount: 'Ingresa el número que muestra la impresora.',
      errorAuth: 'Sesión expirada (401). Cierra sesión, vuelve a entrar e inténtalo de nuevo.',
      errorDevice: 'Esta cabina aún no está registrada (404). Espera unos minutos tras abrir la app e inténtalo de nuevo.',
      errorNetwork: 'Sin conexión. Revisa internet e inténtalo de nuevo.',
      errorGeneric: 'No se pudo guardar'
    },
    printer: {
      title: 'Impresora',
      enablePrinting: 'Activar impresión',
      selectPrinter: 'Seleccionar impresora',
      savedNotDetected: '{{printer}} — no detectada ahora mismo',
      settings: 'Ajustes de impresión',
      settingsHint: 'Tamaño de papel, DPI, márgenes, escala y posición',
      notAvailable: 'La configuración de impresión no está disponible.',
      webPrintHint:
        'Cuando está activado, la app puede iniciar la impresión (normalmente el diálogo de impresión del navegador). La impresión automática silenciosa requiere la app de escritorio.',
      downloadDesktop:
        'Descarga la aplicación de escritorio para configurar la impresión automática.',
      guideDescriptionPrefix: 'Consulta',
      guideLink: 'la guía de impresora',
      guideDescriptionSuffix: 'para la instalación y configuración de la impresora',
      systemDefault: 'Predeterminada del sistema',
      scaleFactor: 'Factor de escala (10-200 %)',
      scaleFactorHelp: 'Porcentaje de escala (10-200 %)',
      printBackground: 'Imprimir fondo',
      printBackgroundHint: 'Incluye los colores e imágenes de fondo en la impresión.'
    },
    download: {
      title: 'Ajustes de descarga',
      saveLocal: 'Guardar archivos en este dispositivo',
      explanation:
        'Cuando está activado, las fotos, los GIF y los videos se descargarán automáticamente en tu dispositivo.',
      savePath: 'Se guardarán en la ruta pictures/dreambooth.',
      savePathNew: 'Los archivos se guardan en tu carpeta Imágenes/Dreambooth Gallery/{{date}}.'
    },
    pin: {
      title: 'PIN para bloquear pantalla completa',
      placeholder: 'PIN',
      noPin: 'Sin PIN configurado',
      change: 'Cambiar'
    },
    autoStart: {
      title: 'Ajustes de inicio automático',
      withProject: 'Iniciar automáticamente con este proyecto',
      explanation:
        'Cuando está activado, la app se abrirá automáticamente con este proyecto al iniciarse'
    },
    display: {
      title: 'Monitor',
      monitor: 'Monitor',
      primary: 'Principal',
      here: 'En uso',
      moved: 'Cabina movida a ese monitor',
      moveFailed: 'No se pudo mover la cabina a ese monitor',
      unavailable: 'Elegir monitor no está disponible en este entorno',
      explanation:
        'Toca un monitor para mover esta cabina allí al instante. Cada cabina recuerda su propio monitor.'
    },
    screensaver: {
      title: 'Protector de pantalla',
      label: 'Activar protector de pantalla (modo inactivo)',
      noVideo:
        'Este proyecto no tiene un video de protector de pantalla. Sube uno en el panel para usar esta función.',
      usingProjectTimeout: 'Usando el tiempo de inactividad del proyecto:',
      seconds: 'segundos',
      configuredInDashboard: '(configurado en el panel)',
      timeout: 'Tiempo de espera (segundos):',
      explanation:
        'Reproduce automáticamente el video del protector de pantalla del proyecto cuando no hay actividad y desconecta la cámara para ahorrar energía.',
      tapToContinue: 'Toca en cualquier lugar para continuar',
      controlledByProject:
        'Este ajuste se controla desde la configuración del proyecto en el panel.'
    },
    browserGestures: {
      title: 'Navegador web',
      label: 'Bloquear gestos del navegador (recargar al deslizar, deslizar atrás, zoom)',
      explanation:
        'Protección táctil tipo kiosco para navegadores en tablet: suprime la recarga al deslizar y el sobredesplazamiento, bloquea la navegación por deslizamiento desde el borde y desactiva el zoom por pellizco y doble toque. Algunos gestos del sistema solo se bloquean desde el SO — en iPad usa Acceso Guiado. Agregar la app a la pantalla de inicio (modo independiente) mejora aún más el comportamiento.'
    },
    autoLaunch: {
      title: 'Inicio automático',
      label: 'Abrir la app al iniciar sesión en el sistema',
      explanation:
        'Abre la app automáticamente cuando inicias sesión en tu computadora (tras encenderla, reiniciarla o iniciar sesión). No aplica al salir de suspensión o hibernación.',
      enabled: 'La app se abrirá al iniciar sesión en el sistema',
      disabled: 'La app no se abrirá al iniciar sesión en el sistema',
      error: 'No se pudo actualizar el ajuste de inicio automático'
    },
    systemControls: {
      title: 'Controles del sistema Windows',
      description:
        'Estos son los ajustes de Windows recomendados para optimizar tu experiencia de photobooth. Requieren privilegios de administrador y pedirán elevación al aplicar los cambios.',
      edgeSwipe: {
        title: 'Desactivar los gestos táctiles desde el borde de la pantalla',
        explanation:
          'Evita que los clientes salgan sin querer de la app deslizando desde los bordes de la pantalla. (Será necesario reiniciar después de cambiar este ajuste.)'
      },
      lockOnWake: {
        title: 'Bloquear la pantalla al reanudar',
        disable: 'Desactivar',
        enable: 'Activar',
        explanation:
          'Desactiva la pantalla de bloqueo cuando la computadora se reanuda, para que la app del photobooth se abra directamente sin pedir contraseña ni un clic para iniciar sesión'
      },
      timeouts: {
        title:
          'Evitar que la pantalla se apague y que el equipo entre en suspensión (excepto en laptops con batería)',
        enable: 'Activar',
        explanation:
          'Evita que la pantalla se apague durante las sesiones del photobooth. Con corriente: nunca suspender ni apagar la pantalla. Con batería (laptops): 30 minutos de espera'
      },
      scriptDownloaded:
        'Script descargado en la carpeta Descargas. Haz clic derecho y elige "Ejecutar con PowerShell" para aplicarlo.',
      edgeSwipesEnabled: 'Gestos desde el borde activados',
      lockOnWakeEnabled: 'Bloqueo de pantalla al reanudar activado',
      applyingChanges: 'Aplicando cambios...',
      storeInstallWarning:
        'Los controles del sistema Windows requieren privilegios de administrador. Si la ejecución automática falla, los scripts se descargarán en tu carpeta Descargas para ejecutarlos manualmente.'
    },
    appVersion: {
      label: 'Versión de la app:',
      updateAvailable: '¡Hay una nueva versión disponible! Actualiza a'
    },
    autoUpdate: {
      title: 'Actualizaciones de la app',
      toggleLabel: 'Actualizar automáticamente',
      checkNow: 'Buscar ahora',
      installNow: 'Instalar y reiniciar',
      openDashboard: 'Abrir el panel',
      installFailed: 'No se pudo instalar la actualización. {{reason}}',
      installBusy:
        'La cabina está ocupada: todavía hay una sesión, una impresión o una subida en curso. Inténtalo de nuevo cuando esté libre.',
      explanation:
        'La cabina descarga sola las actualizaciones menores y de parche mientras está libre, y las instala la próxima vez que se abra la app (puedes omitirlo en ese momento) o cuando pulses Instalar y reiniciar. Una versión mayor nunca se instala sola: descárgala desde el panel.',
      launchPrompt: {
        title: 'Actualizando a la versión {{version}}',
        body: 'La app se cerrará y volverá a abrirse sola. Tarda alrededor de un minuto.',
        countdown: 'Empieza en {{seconds}} s',
        skip: 'Abrir sin actualizar'
      },
      status: {
        upToDate: 'Está al día',
        checking: 'Buscando actualizaciones...',
        available: 'La versión {{version}} está disponible — actívalo para instalarla',
        waiting: 'La versión {{version}} se descargará cuando la cabina esté libre',
        downloading: 'Descargando {{version}} ({{percent}}%)',
        ready:
          'La versión {{version}} está lista. Se instala la próxima vez que se abra la app, o pulsa Instalar y reiniciar',
        installing: 'La versión {{version}} se instalará en un momento',
        major: 'La versión {{version}} es una actualización mayor — instálala desde el panel',
        error: 'No se pudo contactar el servidor de actualizaciones. Lo intentará más tarde.'
      }
    },
    buttons: {
      seeAllProjects: 'Ver todos los proyectos',
      editProject: 'Editar proyecto',
      continue: 'Continuar'
    },
    pinModal: {
      change: {
        title: 'Cambiar PIN',
        subtitle: 'Ingresa un PIN de 6 dígitos que servirá para:'
      },
      set: {
        title: 'Configurar PIN',
        subtitle: 'Ingresa un nuevo PIN de 6 dígitos que servirá para:'
      },
      usage: {
        unlock: 'Desbloquear el modo de pantalla completa',
        skip: 'Omitir el pago si es necesario'
      }
    },
    feedback: {
      selectCamera: '¡Selecciona una cámara primero!',
      pinUpdated: 'PIN actualizado correctamente'
    }
  },
  camera: {
    prompt: {
      title: 'Haz clic para iniciar la cámara',
      instruction: 'Haz clic en cualquier lugar para conectar tu cámara'
    }
  },
  nav: {
    dashboard: 'Panel',
    title: 'Dreambooth',
    settings: 'Ajustes'
  },
  login: {
    successMessage: '¡Sesión iniciada correctamente!',
    networkError: 'Error de red. Inténtalo de nuevo más tarde.',
    logoAlt: 'Logo de DreamBooth',
    welcomeBack: 'Bienvenido de nuevo',
    signInPrompt: 'Inicia sesión en tu cuenta',
    email: 'Correo electrónico',
    password: 'Contraseña',
    loginButton: 'Iniciar sesión',
    resetPasswordPrompt: '¿Necesitas restablecer la contraseña o crear una cuenta?',
    visitDashboard: 'Ve al panel web',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    or: 'O',
    continueWithGoogle: 'Continuar con Google',
    googleWaitingTitle: 'Termina de iniciar sesión con Google',
    googleWaitingBody:
      'Se abrió tu navegador. Inicia sesión ahí y luego vuelve a esta aplicación: continuará sola.',
    googleWaitingCancel: 'Cancelar'
  },
  projects: {
    title: 'Proyectos',
    subtitle: 'Administra tus proyectos aquí',
    sessionCredits: 'Créditos',
    refreshCredits: 'Actualizar créditos',
    topUpButton: 'Recargar',
    loading: 'Cargando proyectos...',
    noProjects: 'No hay proyectos disponibles',
    lastUpdated: 'Última actualización',
    menuOptions: 'Opciones',
    run: 'Ejecutar'
  },
  projectNav: {
    logoAlt: 'Logo de DreamBooth',
    deviceSubscription: 'Suscripción del dispositivo',
    activeSubscription: 'Suscripción activa',
    planType: 'Tipo de plan:',
    computerName: 'Nombre de la computadora:',
    osInfo: 'Información del sistema:',
    activationCode: 'Código de activación:',
    changeSubscription: 'Cambiar suscripción',
    activateSubscription: 'Activar suscripción',
    enterActivationCode: 'Ingresa tu código de activación para {{action}} este dispositivo.',
    reactivate: 'reactivar',
    activate: 'activar',
    deactivateWarning: 'Nota: esto desactivará la suscripción en tu otro dispositivo.',
    enterActivationCodePlaceholder: 'Ingresa tu código de activación',
    noCodeQuestion: '¿No tienes un código de activación?',
    purchaseSubscription: 'Obtener una suscripción',
    greeting: '¡Hola, {{username}}!',
    subscription: 'suscripción',
    failedReactivate: 'No se pudo reactivar la suscripción',
    deviceChanged: '¡Dispositivo cambiado correctamente!',
    failedActivate: 'No se pudo activar la suscripción',
    activatedSuccess: '¡Suscripción activada correctamente!',
    reactivating: 'Reactivando...',
    activating: 'Activando...',
    recoverTitle: 'Restaura tu suscripción',
    recoverSavedCode: 'Código guardado',
    recoverReapply: 'Volver a aplicar la suscripción guardada',
    recoverCheckAgain: 'Volver a comprobar',
    recoverMoveDevice: 'Mover la licencia a esta computadora',
    recovering: 'Restaurando...',
    recoverMoveConfirmTitle: '¿Mover la suscripción a este dispositivo?',
    recoverMoveConfirmMessage:
      'Esto activará tu suscripción en ESTA computadora y la desactivará en el otro dispositivo. ¿Continuar?',
    recoverExpiredNote:
      'Tu suscripción venció. Renuévala en el panel y vuelve a comprobar.',
    recoverNotActiveNote:
      'Tu suscripción aún no está activa. Actívala en el panel y vuelve a comprobar.',
    recoverSessionNote: 'Inicia sesión de nuevo para restaurar tu suscripción.',
    recoverWrongAccountNote:
      'Esta suscripción pertenece a otra cuenta. Inicia sesión con la cuenta correcta.',
    recoverSuccess: '¡Suscripción restaurada correctamente!',
    recoverStillInvalid:
      'No se pudo restaurar la suscripción. Inténtalo de nuevo o contacta a soporte.',
    getSubscription: 'Obtener suscripción'
  },
  proTrial: {
    expired: 'Vencido',
    timeRemainingDays: 'Quedan {{days}} días',
    timeRemainingRealtime: 'Quedan {{hours}} h {{minutes}} m {{seconds}} s'
  },
  interruptedSession: {
    title: 'Tu sesión se interrumpió',
    message: 'Ya pagaste. Continúa sin pagar de nuevo.',
    resume: 'Continuar mi sesión',
    decline: 'No es mi sesión',
  },
  controls: {
    settings: 'Ajustes',
    exitFullscreen: 'Salir de pantalla completa',
    enterFullscreen: 'Entrar en pantalla completa',
    skipPayment: 'Omitir pago',
    exitSession: 'Salir de la sesión',
    enterPin: 'Ingresa el PIN de 6 dígitos para salir del modo de pantalla completa',
    skipPaymentPin: 'Ingresa el PIN de 6 dígitos para omitir el pago',
    exitPaidSessionPin: 'Esta sesión ya está pagada. Ingresa el PIN de 6 dígitos para terminarla.',
    cancelSession: 'Cancelar sesión',
    cancelConfirm: '¿Seguro que quieres cancelar esta sesión?',
    wrongPin: '¡Ups! PIN incorrecto. Inténtalo de nuevo.',
    quitting: 'Finalizando la sesión…',
    quittingMessage: 'Espera mientras guardamos los datos de tu sesión.'
  },
  reprint: {
    title: 'Reimprimir',
    pinRequired: 'Se requiere PIN',
    enterPinToAccess: 'Ingresa el PIN para acceder al menú de reimpresión'
  },
  cameraSettings: {
    title: 'Ajustes de cámara',
    appSettings: 'Ajustes de la app',
    appSettingsHint: {
      title: 'Guía de ajustes de la app',
      description:
        'Estos ajustes controlan cómo la app interactúa con tu cámara. Ajusta la calidad de la vista previa para mejorar el rendimiento, define tu proporción de captura preferida, activa el modo espejo para una vista previa tipo selfie y elige entre usar el obturador físico de la cámara o capturar el fotograma de la vista previa. También puedes activar o desactivar los filtros en la vista previa en vivo.'
    },
    powerSaving: {
      title: 'Modo de ahorro de energía',
      enable: 'Activar el modo de ahorro de energía',
      description: 'Reduce la carga de la cámara y evita el sobrecalentamiento'
    },
    frameRate: {
      title: 'Cuadros por segundo de la vista previa',
      adjust: 'Ajustar los cuadros por segundo',
      fps: 'FPS',
      description: 'Menos cuadros por segundo reducen la carga de la cámara y el calor'
    },
    previewQuality: {
      title: 'Calidad de la vista previa',
      options: {
        custom: 'Personalizada',
        pixelated: 'Pixelada (240p)',
        low: 'Baja (480p)',
        medium: 'Media (720p)',
        high: 'Alta (1080p)',
        ultra: 'Ultra (1440p)'
      }
    },
    captureRatio: {
      title: 'Proporción de captura',
      options: {
        auto: 'Automática',
        square: 'Cuadrada (1:1)',
        portrait: 'Vertical (3:4)',
        landscape: 'Horizontal (4:3)',
        widePortrait: 'Vertical panorámica (9:16)',
        wideLandscape: 'Horizontal panorámica (16:9)',
        classicPortrait: 'Vertical clásica (2:3)',
        classicLandscape: 'Horizontal clásica (3:2)'
      }
    },
    mirror: {
      title: 'Vista previa en espejo',
      enable: 'Reflejar la vista previa de la cámara'
    },
    useShutterCapture: {
      title: 'Usar el obturador para capturar',
      description:
        'Usa el obturador físico de la cámara para tomar las fotos. Si se desactiva, la app capturará el fotograma actual de la vista previa. Usa el obturador para fotos de mejor calidad, o la captura de vista previa para una respuesta más rápida.',
      enable: 'Alternar la captura con obturador'
    },
    focusMode: {
      title: 'Enfoque',
      description:
        'Cu\u00e1ndo enfoca la cabina antes de tomar una foto. El enfoque ocurre durante la cuenta regresiva, para que el obturador no tenga que esperarlo.',
      everyPhoto: 'Cada foto (por defecto)',
      oncePerSession: 'Una vez por sesi\u00f3n',
      fixed: 'Fijo \u2014 nunca enfocar',
      hint: {
        'every-photo':
          'La opci\u00f3n m\u00e1s segura. \u00dasala si los invitados se colocan a distintas distancias.',
        'once-per-session':
          'Enfoca una vez y reutiliza ese enfoque durante el resto de la tira. Ideal con una marca en el suelo para que todos se coloquen en el mismo punto.',
        fixed:
          'La cabina nunca toca el enfoque. \u00dasalo solo si has enfocado el objetivo manualmente (interruptor del lente en MF).'
      }
    },
    flashSetupGuide: {
      title: '¿Usas flash? Estos ajustes del menú de la cámara son obligatorios',
      description:
        'Estos ajustes están en el menú de la propia cámara y la app no puede cambiarlos. En live view, la cámara solo dispara un flash que reconozca como unidad TTL dedicada — con un flash manual de un solo pin (p. ej. Godox TT520) la cámara permanece en modo silencioso/electrónico y el flash nunca dispara.',
      canonDslr: {
        title: 'Canon DSLR (clase 1500D / 2000D)',
        silentLv:
          'Menú Live View shooting → Silent LV shoot → Disable. Este es el ajuste clave — el flash no disparará en live view hasta desactivarlo.',
        expoSim:
          'Exposure simulation → Disable, para que la vista previa se mantenga brillante en una sala oscura para flash.',
        flashFiring:
          'Flash Control → Flash firing → Enable (queda silenciosamente en Disabled).',
        mf: 'Objetivo en MF, preenfocado a la distancia del invitado — en una sala oscura el autofoco falla y bloquea el obturador.',
        exposure:
          'Exposición manual en o por debajo de la velocidad de sincronización: 1/125–1/160, f/7–f/8, ISO 100–200.'
      },
      canonMirrorless: {
        title: 'Canon mirrorless (R100, M50)',
        silentShutter:
          'Silent Shutter Mode → Off, y modo de obturador → Mecánico o Electrónico de 1.ª cortinilla. El obturador totalmente electrónico nunca dispara el flash.',
        m50Firmware:
          'M50: firmware 1.0.2 o superior (corrección oficial de Canon para la comunicación inestable de la zapata).',
        expoSimMf: 'Exposure simulation → Disable; objetivo en MF.'
      },
      nikon: {
        title: 'Nikon',
        silentLv: 'Photo Shooting Menu → Silent live view photography → Off.',
        releasePriority: 'Ajustes personalizados a1/a2 (prioridad AF-C/AF-S) → Release, no Focus.',
        mf: 'Enfoque manual con el interruptor del objetivo (el modo de enfoque es de solo lectura por USB en cuerpos de entrada).',
        popupAuto: 'Cierra el flash emergente; dial de modo fuera de AUTO.'
      },
      sony: {
        title: 'Sony',
        silent:
          'Silent Shooting → Off, Y reconfigura manualmente e-Front Curtain Shutter — desactivar Silent no lo restaura.',
        pcRemote: 'Modo USB → PC Remote.',
        releasePriority: 'Priority Set in AF-S → Release.',
        verify: 'Verifica que el menú de flash ya no esté en gris.'
      },
      hardwareTip:
        'Hardware de flash: usa los speedlites manuales a ≤ 1/2 de potencia (el reciclaje a plena potencia tarda hasta 5 s con pilas AA — las fotos durante el reciclaje salen negras). Un disparador TTL en la zapata (p. ej. Godox X1T) es detectado por la cámara y desactiva los modos silenciosos automáticamente.'
    },
    cameraConfig: 'Configuración de la cámara',
    cameraConfigWarning: {
      title: 'Advertencia sobre la configuración de la cámara',
      description:
        'Los ajustes pueden variar entre modelos de cámara. Es posible que algunos no se puedan modificar desde este software. Prueba cada ajuste para confirmar que funciona con tu cámara.'
    },
    primary: 'Cámara principal',
    secondary: 'Cámara secundaria',
    qualityPresets: {
      pixelated: 'Pixelada (240p)',
      low: 'Baja (480p)',
      medium: 'Media (720p)',
      high: 'Alta (1080p)',
      ultra: 'Ultra (1440p)',
      ultraPlus: 'Ultra+ (1800p)',
      max4k: 'Máxima (4K · 2160p)'
    },
    captureImpact: {
      level: {
        light: 'Subida ligera',
        moderate: 'Subida más pesada',
        heavy: 'Subida pesada'
      },
      estimate: '≈{{size}} MB por foto · ≈{{seconds}} s de subida con {{mbps}} Mbps',
      optionEstimate: '≈{{size}} MB',
      infoLabel: 'Detalles del impacto de la resolución',
      infoTitle: 'Más resolución cuesta tiempo de subida',
      infoBody:
        'Cada foto capturada se sube a la galería. Duplicar la resolución duplica aproximadamente el tamaño del archivo: cada subida tarda más, la cola avanza más lento con un Wi-Fi débil y el disco local se llena antes. Las estimaciones asumen una conexión de {{mbps}} Mbps.',
      heavyWarning:
        'Esta resolución genera archivos grandes. Úsala solo con una conexión estable y rápida: con un Wi-Fi débil la cola de subida se retrasa y los invitados esperan más por sus fotos.',
      jpegComparison: 'En JPEG: ≈{{size}} MB por foto',
      heavyWarningPng:
        'El formato PNG es lo que hace pesados estos archivos. Con un Wi-Fi débil cambia el Formato de foto, al final de esta sección, a JPEG; si no, la cola de subida se retrasa y los invitados esperan más por sus fotos.'
    },
    captureFormat: {
      title: 'Formato de foto',
      description:
        'Tipo de archivo de cada foto guardada: con él se arman la tira, las impresiones y la subida a la galería. Solo afecta a las fotos nuevas.',
      options: {
        png: 'PNG · sin pérdida, archivos más grandes',
        jpeg: 'JPEG · archivos más ligeros, subida más rápida'
      },
      hint: {
        png: 'Antes de guardarse, la imagen ya está comprimida en JPEG con el ajuste de Capture Image Quality, así que PNG la conserva sin pérdida pero sobre todo aumenta el tamaño del archivo.',
        jpeg: 'Se guarda con el ajuste de Capture Image Quality. Desde Alta en adelante se ve igual en impresiones y en la galería, con cerca de un tercio del tamaño en PNG.'
      }
    },
    shutterSpeed: {
      '1_4': '1/4 s (la más lenta, la más brillante)',
      '1_8': '1/8 s',
      '1_15': '1/15 s',
      '1_30': '1/30 s (predeterminada)',
      '1_60': '1/60 s',
      '1_125': '1/125 s',
      '1_200': '1/200 s (la más rápida, límite de sincronización del flash)'
    },
    warning: {
      blackTitle: 'Se detectó una imagen negra',
      blackImage:
        'La cámara muestra una imagen negra. Revisa que la tapa del lente esté quitada y que la cámara esté bien conectada.',
      darkTitle: 'Imagen demasiado oscura',
      darkImage:
        'La imagen de la cámara es demasiado oscura. Revisa la iluminación o los ajustes de la cámara.',
      noiseTitle: 'Calidad de imagen deficiente',
      noiseImage:
        'La calidad de imagen de la cámara es deficiente. Revisa la conexión y los ajustes de la cámara.',
      genericTitle: 'Se detectó un problema con la cámara',
      generic:
        'La cámara tiene problemas de calidad. Revisa los ajustes y la conexión de la cámara.',
      troubleshooting: 'Consejos de solución de problemas:',
      tip1: 'Asegúrate de haber quitado la tapa del lente',
      tip2: 'Revisa que la cámara esté bien conectada',
      tip3: 'Verifica que haya suficiente luz en el lugar',
      tip4: 'Intenta reconectar la cámara',
      retry: 'Reintentar cámara',
      ignore: 'Ignorar'
    }
  },
  cameraConfig: {
    label: '{label}'
  },
  cameraGuide: {
    title: '¡Primero configuremos tu cámara!',
    step1: {
      title: 'Conecta tu cámara',
      bullet1: 'Asegúrate de que tu cámara esté ENCENDIDA',
      bullet2: 'Conéctala a tu computadora con un cable USB',
      bullet3: 'Espera unos segundos a que Windows la detecte'
    },
    step2: {
      title: 'Instalar el controlador',
      storeWarning:
        'La instalación del controlador requiere privilegios de administrador. En la versión de Microsoft Store, los controladores deben instalarse manualmente.',
      viewManualInstructions: 'Ver las instrucciones de instalación manual',
      electronInstruction: 'Abre Zadig para instalar el controlador necesario.',
      webInstruction: 'Descarga y ejecuta Zadig para instalar el controlador necesario.',
      processing: 'Procesando...',
      openZadig: 'Abrir Zadig',
      downloadZadig: 'Descargar Zadig',
      failedToOpen: 'No se pudo abrir Zadig'
    },
    step3: {
      title: 'En Zadig',
      instruction1: 'Haz clic en Options → List All Devices',
      instruction2: 'Busca tu cámara en la lista desplegable (por ejemplo, Canon Digital Camera)',
      instruction3: 'Haz clic en Replace Driver y espera a que termine',
      instruction4: 'Cierra Zadig y haz clic en Listo'
    },
    buttons: {
      close: 'Cerrar',
      done: 'Listo'
    }
  },
  printerGuide: {
    title: 'Guía de configuración de la impresora',
    faq1: {
      question: '¿Cómo activo el corte de dos pulgadas?',
      answer:
        'El corte de dos pulgadas te permite imprimir dos imágenes en una hoja que se puede cortar a la mitad. Asegúrate de que el controlador de tu impresora esté instalado correctamente. Abre las preferencias de impresión de Windows (clic derecho en la impresora en Configuración > Impresoras y escáneres > Preferencias de impresión), configura el tamaño de papel correspondiente y ajusta las opciones de impresión. La app se encargará del diseño automáticamente.'
    },
    faq2: {
      question: '¿Qué son la impresora secundaria y la terciaria?',
      answer:
        'Las impresoras secundaria y terciaria sirven para dos cosas: marcos especiales (enviar ciertos marcos a impresoras dedicadas, útil cuando distintos tipos de marco van en distintas impresoras) e impresión en paralelo (repartir los trabajos entre varias impresoras para acelerar las sesiones con muchas copias).'
    },
    faq3: {
      question: '¿Qué es la impresora remota?',
      answer:
        'La impresora remota te permite imprimir desde una interfaz web hacia una impresora conectada a tu app de escritorio mediante un túnel seguro. La app de escritorio crea una URL de túnel que la interfaz web usa para enviar los trabajos de impresión. Es útil para imprimir desde ubicaciones remotas o flujos de trabajo web.'
    },
    buttons: {
      close: 'Cerrar'
    }
  },
  remotePrintSettings: {
    title: 'Ajustes de impresión remota',
    subtitle:
      'Reemplaza los ajustes de impresión del booth para las impresiones enviadas desde este dispositivo.',
    noPrinter: 'Selecciona primero una impresora remota.',
    useBoothDefaults: 'Usar los valores del booth'
  },
  printerSettings: {
    projectBinding: {
      useForProject: 'Usar esta impresora para el proyecto activo: {{title}}',
      noPrinter: 'No hay impresora seleccionada en esta ranura: las impresiones vinculadas vuelven al enrutamiento por marco/categoría/predeterminado.',
      unbind: 'Quitar vínculo',
      help: 'Los marcos y categorías reclamados por otra impresora siguen ganando; todo lo demás de este proyecto se imprime aquí.'
    },
    orientation: 'Orientación',
    landscape: 'Horizontal',
    portrait: 'Vertical',
    pageSize: 'Tamaño de página (micrones)',
    width: 'Ancho',
    height: 'Alto',
    dpi: 'Calidad de impresión (DPI)',
    'dpi.horizontal': 'Horizontal',
    'dpi.vertical': 'Vertical',
    margins: 'Márgenes',
    'margins.default': 'Predeterminados',
    'margins.none': 'Ninguno',
    'margins.printableArea': 'Área imprimible',
    'margins.custom': 'Personalizados',
    'margins.top': 'Superior',
    'margins.bottom': 'Inferior',
    'margins.left': 'Izquierdo',
    'margins.right': 'Derecho',
    'margins.topTitle': 'Margen superior',
    'margins.bottomTitle': 'Margen inferior',
    'margins.leftTitle': 'Margen izquierdo',
    'margins.rightTitle': 'Margen derecho',
    printPositionShift: 'Desplazamiento de la impresión (cm)',
    testPrintInfo: {
      title: 'Configuración de impresión de prueba',
      description:
        'Usa esta sección para probar los ajustes de impresión y guardar tu configuración preferida. Estos ajustes se usarán para la impresión automática durante las sesiones de fotos.'
    },
    secondaryPrinterInfo: {
      title: 'Configuración de la impresora secundaria',
      description:
        'Configura una impresora secundaria para marcos específicos. Puedes elegir qué marcos se imprimirán con esta impresora. Es útil para imprimir distintos tipos de marco en distintas impresoras.'
    },
    tertiaryPrinterInfo: {
      title: 'Configuración de la impresora terciaria',
      description:
        'Configura una impresora terciaria para marcos específicos. Puedes elegir qué marcos se imprimirán con esta impresora. Es útil para imprimir distintos tipos de marco en distintas impresoras.'
    },
    frameTest: {
      title: 'Marco para la impresión de prueba',
      dragAndDrop: 'Arrastra y suelta una imagen aquí, o haz clic para seleccionarla'
    },
    feedback: {
      selectPrinter: 'Selecciona primero una impresora',
      printInitiated: 'Impresión iniciada',
      printFailed: 'No se pudo imprimir'
    },
    smartSetup: {
      loading: 'Cargando las capacidades de la impresora...'
    },
    androidPrinterIp: {
      label: 'IP de la impresora (impresión silenciosa)',
      explanation:
        'Dirección de red de la impresora, para imprimir directamente por el puerto 9100. Déjalo vacío para usar el diálogo de impresión de Android.'
    },
    selectFrames: 'Seleccionar marcos',
    selectFramesDefault: 'Seleccionar marcos para la impresora principal',
    selectFramesDescriptionDefault:
      'Elige qué marcos y categorías debe imprimir esta impresora. Los marcos sin asignar se imprimen aquí igualmente.',
    routing: {
      framesForThisPrinter: 'Marcos para esta impresora',
      allFrames: 'Todos los marcos (predeterminado)',
      onlySelected: 'Solo marcos y categorías seleccionados',
      defaultSummary:
        '{{frames}} marcos y {{categories}} categorías asignados. Los marcos sin asignar se imprimen aquí igualmente.',
      categories: 'Categorías',
      categoriesHint:
        'Al asignar una categoría se incluyen todos sus marcos, también los que se añadan después.',
      individualFrames: 'Marcos individuales',
      frameCount: '{{count}} marcos',
      viaCategory: 'Incluido por categoría',
      sharedBadge: 'También en otra impresora'
    },
    custom: 'Personalizado',
    noCapabilities: 'No hay capacidades disponibles: configúralo manualmente',
    preset: 'Preajuste',
    presetHelp: 'Elige un preajuste o selecciona Personalizado para configurarlo manualmente',
    noCapabilitiesHelp: 'Configura los ajustes manualmente en la sección Detalles de abajo',
    recommended: {
      label: 'Recomendado por Dreambooth',
      short: 'Recomendado'
    },
    details: 'Detalles del preajuste',
    detailsHint: 'Orientación, tamaño de página y DPI detrás de este preajuste',
    printerDriverDefaults: {
      title: 'Usar los valores del controlador de la impresora',
      description:
        'Usa los valores predeterminados del controlador para los márgenes y los DPI. Mejor compatibilidad con controladores personalizados.',
      toggle: 'Alternar los valores del controlador'
    },
    csharpPrinting: {
      title: 'Impresión directa en C# (experimental)',
      description: 'Usa la impresión de .NET para mayor fiabilidad con ciertas impresoras',
      toggle: 'Alternar la impresión en C#'
    },
    printerPurpose: 'Uso de la impresora',
    printerPurposeHelpSecondary:
      'Elige el uso de la impresora secundaria: marcos especiales para enrutar por marco, o paralelo para repartir por turnos.',
    printerPurposeHelpTertiary:
      'Elige el uso de la impresora terciaria: marcos especiales para enrutar por marco, o paralelo para repartir por turnos.',
    specialFramesPrint: 'Impresión de marcos especiales',
    parallelPrinting: 'Impresión en paralelo',
    positionShift: 'Desplazamiento de la impresión (cm)',
    positionShiftHelp:
      'Ajusta con precisión la alineación de la impresión. Modifica los márgenes de @page de forma independiente a los ajustes de márgenes.',
    positionShiftTop: 'Superior',
    positionShiftBottom: 'Inferior',
    positionShiftLeft: 'Izquierdo',
    positionShiftRight: 'Derecho',
    positionShiftTopTitle: 'Desplazamiento superior',
    positionShiftBottomTitle: 'Desplazamiento inferior',
    positionShiftLeftTitle: 'Desplazamiento izquierdo',
    positionShiftRightTitle: 'Desplazamiento derecho',
    remotePrinterStatus: {
      title: 'Estado de la impresora remota',
      rowTitle: 'Impresora remota',
      stateActive: 'Activa',
      stateInactive: 'Sin conexión',
      activeHint: 'Accesible como {{name}}: imprime aquí desde {{appUrl}}/strip',
      inactiveHint: 'Ahora mismo no está disponible como impresora remota.',
      active: 'Estado: activa',
      inactive: 'Estado: inactiva o sin conexión.',
      name: 'Nombre: ',
      description:
        'La impresora remota está activa. Ve a {{appUrl}}/strip para imprimir de forma remota.'
    },
    preview: 'Imprimir',
    noPreview: 'Vista previa no disponible',
    upload: 'Subir plantilla',
    copies: 'Copias:',
    copiesTitle: 'Número de copias a imprimir',
    printResolutionScale: {
      title: 'Escala de resolución de impresión',
      description:
        'Ajusta la resolución de la imagen impresa final. Los valores más altos dan mejor calidad, pero archivos más grandes.'
    },
    printResultMinDisplay: {
      title: 'Tiempo mínimo en la pantalla de procesamiento antes del Resultado',
      shortTitle: 'Mantener la pantalla de proceso',
      shortHint: 'Segundos que se mantiene tras imprimir y guardar, antes de Resultado. 0 = en cuanto termine.',
      unit: 's',
      description:
        'Cuando terminen la impresión y el guardado, mantén la pantalla de procesamiento al menos esta cantidad de segundos. Usa 0 para pasar al Resultado en cuanto termine el trabajo. Solo aplica cuando la impresión está activada y el invitado realmente imprime.',
      suffix: 'segundos (0 = desactivado, máx. {{max}})'
    },
    defaultPrinter: 'Impresora predeterminada',
    secondaryPrinter: 'Impresora secundaria',
    tertiaryPrinter: 'Impresora terciaria'
  },
  capture: {
    getReady: '¡Prepárate!',
    sessionStarting: 'La sesión empezará en',
    startNow: 'Empezar ahora',
    orWaitCountdown: 'o espera la cuenta regresiva',
    lookAtCamera: 'Mira a la cámara',
    stayStill: 'No te muevas mientras tomamos tu foto...',
    captureFailed: 'No se pudo capturar',
    stayStillCapturing: 'No te muevas durante la captura',
    retryingMoment: 'Reintentando en un momento...',
    lastCaptured: 'Última captura',
    noPhotoYet: 'Aún no hay fotos',
    recording: 'REC',
    errorStartingSessionRecorder: 'Error al iniciar la grabación de la sesión',
    errorProcessingSessionVideo: 'Error al procesar el video de la sesión',
    selfPhotoScanQR: 'Escanea para disparar desde tu teléfono',
    selfPhotoQRAlt: 'Disparar desde el teléfono',
    selfPhotoEndSession: 'Finalizar sesión',
    selfPhotoPhotoCount: '{{count}} fotos',
    selfPhotoAutoLoop: 'Auto',
    selfPhotoSessionTimer: 'Tiempo de sesión',
    selfPhotoEndSessionBtn: 'Finalizar sesión',
    selfPhotoAutoLoopOn: 'Captura automática activada',
    selfPhotoAutoLoopOff: 'Captura automática desactivada',
    selfPhotoManualOnly: 'Manual',
    selfPhotoTriggerUrl: 'URL de disparo',
    selfPhotoTriggerNow: 'Disparar captura ahora',
    removePhoto: 'Quitar foto'
  },
  stickers: {
    title: 'Pegatinas',
    pageTitle: 'Decora tu tira',
    tabStickers: 'Pegatinas',
    tabDraw: 'Dibujar',
    autoLayout: 'Diseño automático',
    replaceTitle: '¿Reemplazar todas las pegatinas?',
    replaceMessage: 'Las pegatinas que colocaste se quitarán y se acomodarán de nuevo automáticamente.',
    clear: 'Borrar todo',
    brush: 'Pincel',
    color: 'Color',
    customColor: 'Cualquier color',
    size: 'Tamaño',
    brushTypes: {
      solid: 'Sólido',
      dashed: 'Discontinuo',
      dotted: 'Punteado',
      marker: 'Marcador',
      pencil: 'Lápiz',
      airbrush: 'Aerógrafo'
    },
    shuffle: 'Mezclar',
    none: 'Sin pegatinas',
    undo: 'Deshacer',
    redo: 'Rehacer',
    remove: 'Quitar pegatina',
    processing: 'Procesando...',
    processingMessage: 'Espera mientras terminamos tu tira',
    preparing: 'Preparando tu tira...'
  },
  select: {
    title: 'Acomoda tus fotos',
    instruction: 'Toca las fotos para seleccionarlas, desliza para moverlas, pellizca para acercar',
    processingPhotos: 'Procesando fotos',
    pleaseWait: 'Espera mientras procesamos tus fotos...',
    printPhotos: 'Imprimir fotos',
    tapToFill: 'Toca tus fotos para llenar los espacios de la impresión',
    swipeInstructions: 'Desliza para mover, pellizca para acercar',
    mirror: 'Espejo',
    selectionLocked: 'Selección bloqueada',
    processing: 'Procesando...',
    processingVideo: 'Procesando video...',
    next: 'Siguiente ({{count}}/{{max}})',
    capture: 'Capturar',
    selected: 'Seleccionada',
    clickToSelect: 'Haz clic para seleccionar',
    continue: 'Continuar',
    errorGif: 'Error al crear el GIF animado',
    stripExportFailed: 'No se pudo crear la vista previa de la tira',
    errorVideo: 'Error al procesar el video',
    errorProcessing: 'Hubo un error al procesar tus fotos. Inténtalo de nuevo.',
    selectPhotos: 'Selecciona {{count}}/{{max}} fotos',
    exportFailed: 'No se pudo generar la tira. Continuando sin marco...',
    exportTimeout: 'La tira está tardando más de lo esperado. Espera un momento...'
  },
  retake: {
    title: '¿No te convencen algunas fotos?',
    headline: 'Toca las fotos que quieras repetir',
    maxHint: 'Máx. {{count}}',
    pillSessions: 'Quedan {{count}} · Sesión {{current}} de {{max}}',
    remainingSessions: 'Repeticiones restantes: {{count}}',
    selectUpTo: 'Selecciona hasta',
    photos: 'fotos',
    toRetake: 'para repetir',
    selectToRetake: 'Selecciona las fotos que quieres repetir',
    sessionCount: 'Repetición {{current}} de {{max}}',
    photoAlt: 'Foto {{number}}',
    photoSelectedAlt: 'Foto {{number}} seleccionada para repetir',
    overlayRetake: 'Repetir',
    overlaySelected: 'Seleccionada',
    selectedToRetake: 'Seleccionada para repetir',
    clickToRetake: 'Haz clic para repetir',
    retakeSelected: 'Repetir las fotos seleccionadas ({{count}})',
    retakeCount: 'Repetir ({{count}})',
    retakeAll: 'Repetir todas',
    keepAll: 'Conservar todas',
    explanation:
      'Las fotos seleccionadas se borrarán y tomarás unas nuevas. Esto no se puede deshacer.',
    confirmContinue: '¿Continuar con todas las fotos?',
    confirmContinueHint: 'Puedes volver para repetir cualquier foto o continuar al siguiente paso.',
    confirmKeepAllTitle: '¿Te gustan todas las fotos?',
    confirmKeepAllMessage: 'Puedes repetir algunas fotos o continuar al siguiente paso.',
    back: 'Atrás',
    continue: 'Continuar',
    confirmKeepAllNo: 'No, quiero repetir mis fotos',
    confirmKeepAllYes: 'Sí, quiero continuar',
    confirmingKeepAll: 'Continuando...'
  },
  result: {
    videoReady: '¡Tu video está listo!',
    photosReady: '¡Tus fotos están listas!',
    scanQrInstruction: 'Escanea el código QR de abajo para acceder a {{type}}',
    video: 'tu video',
    digitalPhotos: 'tus fotos digitales',
    photoStripAlt: 'Tira de fotos',
    qrCodeAlt: 'Código QR',
    sessionId: 'ID de sesión:',
    scanToView: 'Escanea para ver la galería',
    videoRecording: 'Grabación de video',
    photoStrip: 'Tira de fotos',
    backToHome: 'Volver al inicio',
    satisfaction: {
      question: '¿Qué te pareció tu experiencia en el photobooth?',
      rateExperience: 'Califica tu experiencia',
      thankYou: '¡Gracias por tus comentarios!',
      starAlt: 'Calificar con {{rating}} estrellas'
    },
    feedback: {
      title: 'Cuéntanos más',
      titleLow: 'Lamentamos que no haya salido bien',
      subtitle: 'Califica lo que quieras; el resto puedes omitirlo.',
      tellUsMore: 'Dejar más comentarios',
      skip: 'Omitir',
      submit: 'Enviar',
      submitCount: 'Enviar ({{count}})',
      thanks: 'Gracias, nos ayuda a corregirlo.',
      starAlt: 'Calificar {{aspect}} con {{rating}} de 5',
      aspects: {
        photo: { label: 'Calidad de la foto', hint: 'Borrosa, oscura, colores raros' },
        print: { label: 'La impresión', hint: 'No salió, cortada, descolorida' },
        timing: { label: 'Momento de la foto', hint: 'La cuenta atrás fue muy rápida' },
        choices: { label: 'Marcos y filtros', hint: 'Pocas opciones para elegir' },
        payment: { label: 'Pago', hint: 'El QR o el proceso fue incómodo' },
        digital: { label: 'Fotos digitales', hint: 'Difícil de escanear o guardar' }
      }
    },
    emailGallery: {
      title: 'Recibe el enlace de tu galería por correo',
      description: 'Te enviaremos un mensaje con el enlace a esta galería.',
      emailLabel: 'Correo electrónico',
      emailPlaceholder: 'tu@ejemplo.com',
      sendEmail: 'Enviar',
      sendingEmail: 'Enviando…',
      invalidEmail: 'Ingresa un correo electrónico válido.',
      shareSuccess: '¡Enviado! Revisa tu bandeja de entrada.',
      shareError: 'No se pudo enviar. Inténtalo de nuevo.',
      shareNetworkError: 'Problema de conexión. Inténtalo de nuevo.',
      close: 'Cerrar'
    },
    postSessionAi: {
      title: 'Mejora con IA',
      subtitle: 'Un toque extra opcional para tus fotos.',
      guestPromptLabel: 'Agrega una nota (opcional)',
      guestPromptPlaceholder: 'por ejemplo, más vintage',
      skip: 'Omitir',
      apply: 'Aplicar IA',
      applying: 'Aplicando…',
      continue: 'Continuar',
      errorGeneric: 'Algo salió mal. Toca Omitir para continuar.',
      insufficientCredits: 'La IA no está disponible ahora. Toca Omitir para continuar.',
      missingStrip: 'La tira aún no está lista. Toca Omitir para continuar.',
      missingRaw: 'No hay fotos disponibles para la IA. Toca Omitir para continuar.',
      missingRawNeedHttps: 'Estas fotos aún se están subiendo. Toca Omitir para continuar.',
      timeout: 'La IA está tardando más de lo normal. Toca Omitir para continuar.',
      offline: 'La IA necesita conexión a internet. Toca Omitir para continuar.',
      stripUploading: 'Terminando de subir la tira…',
      previewBefore: 'Antes',
      previewAfter: 'Después',
      previewCompareHeading: 'Vista previa de antes y después',
      previewLoading: 'Cargando la vista previa…',
      previewMissingUrls: 'Aún no hay vista previa.',
      previewLoadError: 'No se pudo cargar la vista previa. Inténtalo de nuevo.',
      previewImageLoadError: 'No se pudo cargar la vista previa. Inténtalo de nuevo.',
      filmstripAriaLabel: 'Las fotos de tu sesión',
      filmstripCaption: 'Fotos de la sesión',
      filmstripHelper: 'Toca una foto para generar la versión con IA.',
      filmstripThumb: 'Foto de la sesión',
      filmstripThumbWithAi: 'Foto de la sesión (IA lista)',
      aiProcessedTooltip: 'Versión con IA lista',
      dragToCompareHint: 'Arrastra para comparar el antes y el después.',
      introBadge: 'Efecto IA',
      introHeadline: 'Mira tus fotos, transformadas',
      introBody:
        'Una versión única con IA de tu foto favorita. Pruébala con tus propias fotos.',
      introCta: 'Probar con mis fotos',
      previewEmptyMessage: 'Aún no hay comparación. De todos modos puedes aplicar la IA abajo.',
      pickOneSpecial: 'Elige 1 foto especial para mejorarla con IA.',
      rawOnce: 'La IA puede mejorar una foto por sesión.',
      rawUsedBadge: 'IA usada',
      processing: 'Creando tu foto…',
      processingHint: 'Solo tomará un momento. Espera por favor.',
      done: '¡Aquí está tu foto con IA!',
      resultMayVary: 'Los resultados de la IA pueden variar: consérvala o descártala.',
      discard: 'Descartar'
    }
  },
  filter: {
    nextFilter: 'Siguiente filtro',
    prevFilter: 'Filtro anterior'
  },
  frame: {
    framesListbox: 'Marcos',
    title: 'Elige tu marco',
    count: '{{count}} marcos',
    previewAlt: 'Vista previa'
  },
  payment: {
    skipGuide:
      '¿Necesitas omitir el pago? Haz clic en el icono de dinero en la esquina superior derecha e ingresa el PIN.',
    qrisLogoAlt: 'Logo de QRIS',
    rail: { qris: 'QRIS', online: 'QR', upi: 'UPI' },
    qrCodeAlt: 'Código QR',
    qrLoadFailed: 'No se pudo cargar el código QR.',
    qrReload: 'Volver a cargar el código QR',
    automaticCheck: 'El estado del pago se verificará automáticamente.',
    card: {
      cantScan: '¿No puedes escanear?',
      payByCard: 'Pagar con tarjeta',
      holdTitle: 'Pide ayuda a nuestro personal',
      holdBody: 'Muéstrales esta pantalla: pasarán tu tarjeta por el terminal.',
      holdHeld: 'Tu sesión se mantiene 5 minutos.',
      backToPayment: 'Volver a {{rail}}',
      confirmStaff: 'Confirmar pago con tarjeta (personal)',
      pinTitle: 'Confirmar pago con tarjeta',
      pinSubtitle: 'Solo personal. Introduce el PIN de la cabina.',
      codePlaceholder: 'Código de aprobación del recibo del terminal',
      codeOptionalPlaceholder: 'Código de aprobación (opcional): pulsa Intro para confirmar',
      codeTitle: 'Código de aprobación',
      codeSubmit: 'Confirmar pago',
      codeRequired: 'Introduce el código de aprobación del recibo.',
      confirmed: 'Pago con tarjeta registrado',
      terminalTitle: 'Paga en la terminal',
      terminalSending: 'Enviando el importe a la terminal...',
      terminalTapCard: 'Acerca, inserta o desliza tu tarjeta en la terminal',
      terminalCheck: 'Revisa la terminal para terminar el pago',
      terminalDeclined: 'La tarjeta fue rechazada. Inténtalo de nuevo o usa otra tarjeta.',
      terminalCanceled: 'El pago se canceló en la terminal.',
      terminalExpired: 'Se acabó el tiempo para pagar',
      terminalFinishing: 'Termina el pago en la terminal. Esta pantalla sigue sola.',
      terminalTryAgain: 'Intentar de nuevo',
      terminalCancelOnDevice: 'Cancela primero el pago en la terminal.',
      terminalAlreadyPaid: 'Ese pago se realizó. Un momento.',
      terminalUnavailable: 'La terminal no está disponible. Paga con el código en pantalla.'
    },
    scanInstruction: 'Escanea el código QR con tu app de pagos',
    timeToPay: 'Completa el pago en',
    cancelOrder: 'Cancelar y empezar de nuevo',
    expired: 'El código de pago venció',
    restartPrompt: 'Haz clic para reiniciar la transacción',
    restart: 'Reiniciar transacción',
    successMessage: '¡Pago exitoso!',
    notSuccessful: 'El pago no se completó. Inténtalo de nuevo.',
    offline: 'Sin conexión a internet',
    offlineMessage: 'Revisa tu conexión a internet e inténtalo de nuevo',
    retry: 'Reintentar el pago',
    reload: 'Volver a cargar',
    checkingConnection: 'Verificando la conexión...',
    networkError: 'Ocurrió un error de red',
    snapError: 'El pago falló. Inténtalo de nuevo.',
    wrongCode: 'Código incorrecto. Inténtalo de nuevo.',
    enterCode: 'Ingresa el código para continuar',
    offlineMode: 'Modo sin conexión: sin internet',
    autoRestart: 'Reinicio automático en',
    checkAgain: 'Ya pagué, verificar de nuevo',
    notReceivedYet: 'Aún no recibimos el pago. Revisa tu teléfono.',
    snapReady: 'Pasarela de pago lista',
    snapOpening: 'Abriendo el pago...',
    openSnap: 'Abrir el pago',
    payNowTitle: 'PAGA PARA INICIAR LA SESIÓN',
    codeEntry: {
      title: 'Ingresa el código de acceso',
      subtitle: 'Ingresa el código de 6 dígitos para omitir el pago en modo sin conexión'
    }
  },
  checkout: {
    title: 'Revisa tu pedido',
    print: 'Impresión',
    choosePrints: 'Elige el número de impresiones',
    minus: 'Menos',
    plus: 'Más',
    gif: 'GIF',
    getAnimated: 'Obtén una imagen animada',
    recording: 'Grabación',
    getRecording: 'Obtén la grabación de la sesión de fotos',
    video: 'Video',
    recordVideo: 'Graba un video',
    free: 'Gratis',
    subtotal: 'Subtotal:',
    discount: 'Descuento:',
    total: 'Total:',
    remainingTime: 'Tiempo restante:',
    havePromo: '¿Tienes un código de descuento?',
    proceedToPayment: 'Continuar al pago',
    enterPromo: 'Ingresa el código promocional',
    voucherCodeTitle: 'Código de descuento',
    voucherCodePlaceholder: 'Escribe o pega tu código',
    promoApplied: '¡Código promocional aplicado!',
    invalidPromo: 'Código promocional no válido',
    cancel: 'Cancelar',
    apply: 'Aplicar',
    promoValidationFailed: 'No se pudo validar el código promocional. Inténtalo de nuevo.',
    digital: 'Fotos digitales',
    getDigital: 'Obtén copias digitales de tus fotos',
    keyboard: 'Teclado',
    scanQR: 'Escanear QR',
    scanningQR: 'Buscando un código QR...',
    qrInstructions:
      'Sostén un código QR frente a la cámara. El código promocional se detectará y aplicará automáticamente.',
    qrScannerActive: 'Escáner QR activo',
    pointCamera: 'Apunta la cámara a un código QR que contenga el código promocional',
    failedToCaptureImage: 'No se pudo capturar la imagen de la cámara',
    errorScanningQR: 'Error al escanear el código QR',
    addNumberOfPeople: 'Selecciona el número de personas.',
    choosePeople: 'Elige el número de personas',
    pax: 'Número de personas'
  },
  frameSurcharge: {
    badge: '+{{price}}',
    lineItem: 'Complemento de marco'
  },
  frameCategoryBasePrice: {
    badge: '+{{price}}',
    lineItem: 'Precio base'
  },
  bahela: {
    welcome: {
      title: 'Bienvenido',
      subtitle: '¡DAMAS Y CABALLEROS!',
      promoButton: 'Código promocional',
      startButton: 'Iniciar sesión',
      tutorial: 'Tutorial',
      noCredits: 'No hay créditos disponibles. Recarga tus créditos.',
      startError: 'No se pudo iniciar la sesión. Inténtalo de nuevo.',
      discountApplied: '¡El descuento ya está aplicado, inicia la sesión!',
      promoValidationFailed: 'No se pudo validar el código promocional. Inténtalo de nuevo.',
      digital: 'Fotos digitales',
      getDigital: 'Obtén copias digitales de tus fotos',
      next: 'Siguiente',
      or: 'o',
      savePathNew: 'Los archivos se guardarán en tu carpeta Imágenes/Dreambooth Gallery/{{date}}.'
    },
    promoModal: {
      title: 'Ingresa el código promocional',
      placeholder: 'Código promocional',
      submit: 'Enviar',
      cancel: 'Cancelar',
      promoExpired: 'El código promocional venció',
      invalidPromo: 'Código promocional no válido',
      percentageDiscountApplied: 'Descuento del {{value}} % aplicado, ¡empecemos la sesión!',
      fixedDiscountApplied: 'Descuento de {{value}} aplicado, ¡empecemos la sesión!',
      promoError: 'Error al validar el código promocional',
      cashVoucherApplied: 'Vale de efectivo aplicado.',
      customFrameVoucherApplied:
        'Vale de marco personalizado aplicado. Tu marco se elegirá en el kiosco.'
    },
    tutorial: {
      title: 'Tutorial del photobooth BAHeLA'
    }
  },
  recording: {
    title: 'Grabación',
    type: 'Tipo de grabación',
    modes: {
      live: 'Modo en vivo',
      story: 'Modo historia',
      session: 'Modo sesión'
    },
    description: {
      recording: 'Video en modo en vivo de la tira de fotos',
      strip_live: 'Video en modo en vivo de la tira de fotos',
      strip_story: 'Video en modo historia de la tira de fotos',
      session_recording: 'Grabación de toda la sesión de fotos'
    },
    toggle: 'Alternar el paquete de grabación'
  },
  view: {
    noCreditsModal: {
      title: 'Se necesita una suscripción o créditos',
      description:
        'Tu suscripción terminó o te quedaste sin créditos. Renuévala en el panel o agrega créditos para seguir usando la app sin interrupciones.',
      purchaseSubscription: 'Activar suscripción',
      topUpCredits: 'Recargar créditos',
      close: 'Cerrar'
    },
    subscriptionValidUntil: 'Tu suscripción es válida hasta el {date}.'
  },
  modeSelect: {
    title: '¿Qué foto quieres?',
    subtitle: 'Cada modo tiene sus propios paquetes, marcos y tamaño de impresión.',
    from: 'desde {{price}}',
    unavailable: 'No disponible en esta cabina'
  },
  consent: {
    title: 'Consentimiento para publicar fotos',
    subtitle: '¡Nos encantaría mostrar tus increíbles fotos!',
    question: '¿Podemos usar tus fotos para publicarlas?',
    description:
      'Tus fotos podrían aparecer en nuestro sitio web, redes sociales o materiales de marketing para inspirar a otras personas. Nunca compartiremos tu información personal.',
    yourPhotos: 'Tus fotos',
    yourVideo: 'Tu video',
    photoStripAlt: 'Tira de fotos',
    benefits: {
      title: 'Qué significa esto:',
      featured: 'Podrían aparecer en nuestras redes sociales o en nuestro sitio web',
      inspire:
        'Deja que tu estilo y tu creatividad inspiren a otras personas a probar nuestro photobooth',
      privacy: 'No se compartirá públicamente ninguna información personal'
    },
    accept: '¡Sí, me encantaría!',
    decline: 'No, gracias',
    footer:
      'Puedes cambiar esta preferencia cuando quieras contactando a nuestro equipo de soporte en dreamboothstudio.com.',
    newspaper: {
      headline: '¿LISTO PARA SER FAMOSO?'
    }
  },
  onboarding: {
    congratulations: {
      title: '¡Felicidades!',
      subtitle: '¡Completaste tu primera sesión de photobooth!',
      nextSteps: 'Esto es lo que puedes hacer ahora:',
      customize: 'Personalizar tu proyecto',
      analytics: 'Ver analíticas',
      gallery: 'Explorar la galería',
      payment: 'Administrar la suscripción',
      getStarted: 'Empezar'
    }
  },
  newspaper: {
    welcome: {
      headline: 'Photobooth de periódico: {{content}}',
      takePhoto: 'Tomar foto',
      subheadline: 'Lo más bello de {{city}} en {{year}}',
      subheadlineDefault: 'Lo más bello del mundo en {{year}}',
      introWithCity:
        'Habitantes de {{city}} se han reunido para esta histórica sesión de fotos, uniéndose para capturar recuerdos que celebran su comunidad y su herencia local.',
      introDefault:
        'Vecinos del lugar se han reunido para esta histórica sesión de fotos, uniéndose para capturar recuerdos que celebran su comunidad y sus momentos compartidos.',
      aboutCity: 'Acerca de {{city}}',
      photoboothSection: 'El photobooth que lo reúne todo',
      dreamboothSection: 'Creatividad desatada por un software innovador'
    },
    checkout: {
      steps: {
        title: 'Cómo usarlo',
        step1: 'Paso 1: revisa tu pedido y elige los paquetes',
        step2: 'Paso 2: completa el pago si es necesario',
        step3: 'Paso 3: elige tu estilo de filtro',
        step4: 'Paso 4: selecciona tu marco',
        step5: 'Paso 5: toma tus fotos',
        step6: 'Paso 6: revísalas y repítelas si hace falta',
        step7: 'Paso 7: ¡recibe tus fotos!'
      }
    },
    filter: {
      timeRemaining: 'Tiempo restante'
    },
    select: {
      timeRemaining: 'Tiempo restante'
    }
  },
  strip: {
    step1Title: 'Paso 1: seleccionar la tira',
    step1Desc: 'Elige un marco y sube fotos si es necesario.',
    step2Title: 'Paso 2: configuración de subida',
    step2Desc: 'Elige si quieres subirla a la galería y configura el proyecto y la sesión.',
    step3Title: 'Paso 3: configuración de impresión remota',
    step3Desc: 'Selecciona una impresora remota y configura los ajustes de impresión.',
    step4Title: 'Paso 4: vista previa y generación',
    step4Desc: 'Revisa tu configuración y genera la tira.',
    modeConfig: 'Crear nueva',
    modeUpload: 'Subir una tira de fotos existente',
    useExistingStrip: 'Usar una tira de fotos existente',
    chooseFromGallery: 'Elegir de la galería',
    selectFrame: 'Selecciona un marco',
    selectFrameTitle: 'Selecciona el marco de la tira de fotos',
    uploadStripImage: 'Sube una imagen de tira de fotos',
    uploadPhotos: 'Haz clic para subir fotos',
    uploadHint: 'Selecciona hasta {{count}} fotos',
    remove: 'Haz clic para quitar',
    mirror: 'Espejo',
    uploadToGallery: 'Subir a la galería',
    selectProjectTitle: 'Seleccionar proyecto',
    enterSessionId: 'Ingresa el ID de sesión',
    validatingSession: 'Validando la sesión...',
    sessionValid: 'ID de sesión válido',
    sessionInvalid: 'ID de sesión no válido o sin autorización',
    printStrip: 'Imprimir la tira',
    selectRemotePrinterTitle: 'Seleccionar impresora remota',
    noPrinterSelected: 'No hay ninguna impresora seleccionada.',
    preview: 'Vista previa',
    previewHint: 'Vista previa de tu tira de fotos',
    generating: 'Generando...',
    generate: 'Generar tira',
    downloading: 'Descargando...',
    uploading: 'Subiendo...',
    printing: 'Imprimiendo...',
    errorFetchingData: 'Error al obtener los datos',
    errorFetchingPrinters: 'No se pudieron obtener las impresoras remotas',
    exportFailed: 'No se pudo crear la vista previa de la tira',
    noUser: 'Usuario no autenticado',
    invalidSessionId: 'Sin autorización para subir a esta galería',
    uploadTimeout: 'La subida está tardando más de lo esperado. Continuando...',
    uploadSuccess: 'Tira subida correctamente',
    uploadFailed: 'No se pudo subir la tira: ID de sesión no válido',
    printSuccess: '¡Trabajo de impresión enviado!',
    printFailed: 'La impresión falló: {{error}}',
    base64Error: 'No se pudo preparar la imagen para imprimir.',
    printError: 'Error al enviar el trabajo de impresión.',
    generationError: 'Error al generar la tira de fotos',
    uploaded: 'Subido:',
    step2: {
      upload: 'Subir',
      skip: 'Omitir'
    },
    step3: {
      print: 'Imprimir',
      done: 'Listo'
    },
    galleryModal: {
      title: 'Selecciona fotos de la galería',
      selectUpTo: 'Selecciona hasta {{count}} fotos',
      loading: 'Cargando fotos...',
      loadingMore: 'Cargando más fotos...',
      noPhotos: 'No se encontraron fotos',
      errorFetching: 'No se pudieron obtener las fotos',
      maxSelected: 'Solo puedes seleccionar hasta {{count}} fotos',
      noSelection: 'Selecciona al menos una foto'
    }
  }
}

// Indonesian translations
const idTranslations = {
  duplicateLaunch: {
    title: 'Booth ini sudah terbuka',
    secondBooth: 'Aplikasi terbuka dua kali. Tutup jendela ini dan pakai booth yang sudah terbuka.',
    closeIn: 'Tutup jendela ini ({{seconds}})',
    closeByHand: 'Tekan Alt+F4 untuk menutup jendela ini.',
    keepSecondBooth: 'Pakai sebagai booth kedua',
    noOtherWindow: 'Tidak ada jendela booth lain? Restart komputer.',
    deadTitle: 'Dreambooth sudah terbuka di komputer ini',
    deadBody:
      'Jendela ini tidak bisa membaca pengaturan, login, dan kode aktivasi booth ini, jadi booth terlihat seperti ter-reset. Ini biasanya terjadi kalau aplikasi dibuka dua kali. Jangan masukkan kode aktivasi di sini.',
    closeNow: 'Tutup jendela ini',
    deadNoOtherWindow:
      'Tidak ada jendela Dreambooth lain? Tutup Dreambooth sepenuhnya atau restart komputer, lalu buka aplikasinya sekali saja. Update ke versi terbaru supaya ini tidak terjadi lagi.',
    deadContinue: 'Tetap lanjut di jendela ini'
  },
  crash: {
    title: 'Booth berhenti',
    body: 'Ada yang error dan booth tidak bisa lanjut. Sesi yang sudah terunggah tetap aman. Muat ulang untuk menjalankan booth lagi.',
    reload: 'Muat ulang booth',
    contact: 'Kalau terus terjadi, kirim baris ini ke kami:'
  },
  cameraTest: {
    label: 'Foto uji',
    shoot: 'Ambil foto uji',
    shooting: 'Mengambil foto...',
    empty: 'Periksa foto aslinya, bukan hanya pratinjau.',
    hint: 'Inilah yang didapat tamu. Ketuk di mana saja untuk menutup.',
    open: 'Buka foto uji',
    noFrame: 'Kamera tidak mengirim foto. Coba lagi.',
    copy: 'Salin foto uji',
    copied: 'Foto disalin.',
    copyFailed: 'Foto tidak bisa disalin.',
    save: 'Simpan foto uji',
    saved: 'Foto disimpan.'
  },
  cameraPresets: {
    title: 'Salin pengaturan dari booth lain',
    count: '{{count}} tersimpan',
    empty: 'Belum ada pengaturan tersimpan. Booth ini menyimpan miliknya saat Anda menutup jendela ini, lalu bisa disalin ke booth lain dari sana.',
    choose: 'Pilih booth yang ingin disalin',
    use: 'Pakai pengaturan ini',
    checking: 'Memeriksa kamera...',
    unknownProject: 'Booth tidak dikenal',
    unknownModel: 'tidak diketahui',
    approxWarning: 'Ini disimpan pada kamera lain ({{model}}). Nilai yang tidak ada di sini akan dilewati, dan yang mirip akan didekatkan.',
    keep: 'Simpan sebagai acuan',
    keptTitle: 'Disimpan sebagai acuan - tidak akan ditimpa otomatis',
    keepFailed: 'Tidak bisa menyimpan ({{detail}}). Coba lagi.',
    rename: 'Ganti nama',
    renameLabel: 'Nama',
    renameFailed: 'Tidak bisa mengganti nama ({{detail}}). Coba lagi.',
    modifiedFrom: 'Diubah dari {{label}}',
    revert: 'Kembalikan',
    revertGone: 'Pengaturan tersimpan itu sudah tidak tersedia.',
    planFailed: 'Tidak bisa membaca kamera. Periksa kabelnya lalu coba lagi.',
    planSummary: '{{apply}} akan berubah - {{adjust}} didekatkan - {{skip}} dilewati',
    alreadyMatching: '{{count}} pengaturan sudah sama - tidak akan disentuh.',
    showDetail: 'Lihat detail',
    hideDetail: 'Sembunyikan detail',
    wanted: 'diminta {{v}}',
    confirm: 'Ya, ubah kameranya',
    applyNotice: 'Pratinjau langsung berhenti sementara proses berjalan. Jangan cabut kamera.',
    applying: 'Menerapkan {{done}} dari {{total}}...',
    stop: 'Hentikan',
    resultSummary: '{{applied}} diterapkan - {{skipped}} dilewati',
    resultFailed: '{{count}} gagal',
    reconnect: 'Cabut dan pasang ulang kamera sebelum menjalankan sesi.',
    unhealthy: 'Kamera berhenti merespons. Cabut dan pasang ulang sebelum sesi berikutnya.',
    undo: 'Batalkan - kembalikan pengaturan sebelumnya',
    previewPaused: 'Pratinjau dijeda saat pengaturan kamera diterapkan',
    tier: { exact: 'Kamera sama persis', brand: 'Merek sama', cross: 'Kamera lain' },
    groups: {
      choose: 'Pilih apa yang disalin',
      hide: 'Selesai',
      summary: 'Menyalin: {{list}}.',
      none: 'Tidak ada yang dipilih untuk disalin.',
      exposure: { title: 'Terang gelapnya foto', examples: 'ISO, kecepatan rana, bukaan, metering' },
      image: { title: 'Warna dan kualitas', examples: 'White balance, picture style, ukuran file' },
      behaviour: { title: 'Cara kamera memotret', examples: 'Mode fokus, mode drive, bracketing' },
      geometry: { title: 'Cara gambar dibingkai', examples: 'Cermin, rotasi, rasio potong - ini tergantung bentuk fisik booth. Salin hanya antar booth yang identik.' }
    },
    skip: {
      missing: 'kamera ini tidak punya pengaturan tersebut',
      readonly: 'diatur di bodi kamera, bukan lewat USB',
      noChoice: 'tidak ada nilai yang sebanding di kamera ini',
      modeLocked: 'putar mode dial ke M untuk menerapkan ini',
      groupOff: 'grup dimatikan di atas',
      denied: 'tidak aman diubah otomatis',
      crossDisabled: 'penyalinan antar kamera berbeda sedang dimatikan'
    }
  },
  diskCleanup: {
    title: 'Penyimpanan',
    free: '{{size}} kosong',
    lowTitle: 'Disk hampir penuh',
    scanning: 'Mengecek file…',
    running: 'Membersihkan…',
    sessions: '{{count}} sesi',
    nothing: 'Belum ada file yang cukup lama untuk dihapus.',
    protected: '{{count}} belum ter-upload, disimpan',
    deleted: '{{count}} sesi dihapus',
    skippedOffline: '{{count}} disimpan: server tidak terjangkau',
    skippedNotUploaded: '{{count}} disimpan: belum ada di server',
    failed: 'Gagal membersihkan. Coba lagi.',
    confirm: 'Bersihkan'
  },
  common: {
    continue: 'Lanjutkan',
    cancel: 'Batal',
    save: 'Simpan',
    change: 'Ubah',
    refresh: 'Muat ulang',
    select: 'Pilih',
    edit: 'Edit',
    download: 'Unduh',
    autoStart: 'Mulai Otomatis',
    back: 'Kembali',
    yes: 'Ya',
    no: 'Tidak',
    close: 'Tutup',
    logout: 'Keluar',
    upgrade: 'Tingkatkan',
    next: 'Selanjutnya',
    or: 'atau',
    loading: 'Memulai...',
    retry: 'Coba Lagi',
    savePathNew: 'File akan disimpan di folder Pictures/Dreambooth Gallery/{{date}} Anda.'
  },
  settings: {
    groups: {
      navLabel: 'Bagian pengaturan',
      camera: 'Kamera',
      printing: 'Cetak',
      booth: 'Booth',
      files: 'Berkas',
      payments: 'Pembayaran'
    },
    payments: {
      intro: 'Untuk tamu yang tidak bisa bayar dengan {{rail}}. Kartu ditawarkan di bawahnya, dan {{rail}} tetap jadi pilihan utama di layar.',
      introNoOnline: 'Pembayaran online belum tersedia di wilayah ini, jadi tamu menukarkan kode voucher. Mesin EDC adalah satu-satunya cara menerima pembayaran di booth.',
      railQris: 'QRIS',
      railOnline: 'kode QR',
      none: 'Tanpa pembayaran kartu',
      noneHint: 'Tombol kartu tidak ditampilkan. Tamu hanya bayar dengan {{rail}}.',
      noneHintNoOnline: 'Tombol kartu tidak ditampilkan. Tamu hanya bisa menukarkan kode voucher.',
      manual: 'Mesin EDC, dikonfirmasi staf',
      manualHint: 'Tamu menekan tombol kartu, staf menggesek kartu di mesin EDC, lalu konfirmasi di booth dengan PIN.',
      connected: 'Mesin EDC tersambung',
      connectedHint: 'Booth mengirim nominal ke mesin dan hasilnya kembali otomatis.',
      connectedUnavailable: 'Mesin Nayax (sambungkan dulu akun Nayax di Studio → Wallet), atau Mercado Pago Point di Meksiko.',
      nayaxLoading: 'Mengecek akun Nayax...',
      nayaxAccountError: 'Tidak bisa menghubungi Studio untuk mengecek akun Nayax.',
      nayaxNoAccount: 'Sambungkan dulu akun Nayax di Studio: Wallet → Add payment gateway → Nayax card reader.',
      nayaxSerial: 'Serial mesin',
      nayaxSerialInvalid: 'Angka saja: nomor serial di mesin, atau machine id Nayax-nya.',
      nayaxChecking: 'Mengecek...',
      nayaxPair: 'Cek & pasangkan',
      nayaxPaired: 'Terpasang: {{serial}} di {{account}}',
      nayaxUnpair: 'Lepas',
      terminalName: 'Nama mesin',
      terminalNamePlaceholder: 'mis. EDC BCA — meja depan',
      terminalNameHint: 'Ditampilkan ke staf saat konfirmasi, dan tersimpan di setiap sesi kartu.',
      requireCode: 'Wajib isi kode approval',
      requireCodeHint: 'Dari struk EDC. Inilah yang membuat sesi kartu seharian bisa dicocokkan dengan settlement mesin.',
      showMarks: 'Tampilkan logo Visa / Mastercard',
      showMarksHint: 'Membantu tamu asing mengenali tombolnya.',
      terminalTitle: 'Mesin',
      terminalLoading: 'Mencari mesin...',
      terminalNotApplicable:
        'Gateway pembayaran aktif kamu bukan Mercado Pago. Aktifkan akun Mercado Pago di dashboard untuk memakai mesin tersambung.',
      terminalError: 'Gagal memuat daftar mesin: {{reason}}',
      terminalRetry: 'Coba lagi',
      terminalNone: 'Tidak ada mesin Point di akun Mercado Pago kamu.',
      terminalSerial: 'Serial {{serial}}',
      terminalReady: 'Siap (mode PDV)',
      terminalNotPdv: 'Belum mode PDV',
      terminalSwitchToPdv: 'Ubah ke mode PDV',
      terminalSwitching: 'Mengubah...',
      terminalPdvDone: 'Sudah diubah ke mode PDV. Restart mesinnya untuk menyelesaikan.',
      terminalPdvFailed: 'Gagal mengubah mesin: {{reason}}',
      terminalSaved: 'Booth ini sekarang menagih lewat mesin itu',
      terminalCleared: 'Belum ada mesin yang dipasangkan ke booth ini',
      terminalRefresh: 'Muat ulang daftar',
      terminalMissing: 'Mesin yang tersimpan ({{serial}}) tidak ada di akun Mercado Pago ini.',
      terminalClear: 'Berhenti memakainya',
      terminalNotPicked: 'Pilih satu mesin. Sampai itu dipilih, booth tidak menampilkan tombol kartu.',
      terminalRestartHint:
        'Mesin hanya bisa menerima tagihan dari booth dalam mode PDV. Setelah diubah, restart mesinnya dan pastikan mode penautannya menunjukkan Punto de Venta (PDV).'
    },
    tiers: {
      advancedPrinter: 'Lanjutan',
      advancedPrinterHint: 'Margin, skala, resolusi, posisi, dan metode cetak',
      advancedCamera: 'Lanjutan',
      advancedCameraHint: 'Kualitas, rotasi, preset, dan konfigurasi kamera'
    },
    title: 'Pengaturan Booth',
    boothGroups: {
      lockdown: 'Penguncian',
      lockdownHint: 'Tamu tetap berada di dalam booth.',
      startup: 'Mulai otomatis',
      startupHint: 'Kembali sendiri setelah restart atau listrik padam.',
      screen: 'Layar',
      screenHint: 'Di layar mana booth tampil, dan apa yang terjadi saat tidak ada orang.'
    },
    project: 'Proyek',
    credits: {
      title: 'Kredit',
      refresh: 'Muat ulang kredit',
      unlimited: 'Tidak Terbatas'
    },
    camera: {
      title: 'Kamera',
      noPreview: 'Tidak Ada Pratinjau',
      selectCamera: 'Pilih Kamera',
      defaultCamera: 'Default',
      secondaryTitle: 'Kamera Kedua',
      noDevicesFound: 'Tidak ada kamera yang ditemukan',
      detect: 'Deteksi Kamera...',
      troubleshoot:
        'Mengalami masalah koneksi? Cabut dan tancapkan kembali USB dan muat ulang mungkin membantu!',
      guideDescriptionPrefix: 'Lihat ',
      guideLink: 'panduan kamera',
      guideDescriptionSuffix: 'untuk koneksi kamera DSLR/mirrorless',
      dslrInstruction:
        'Untuk terhubung ke kamera DSLR, ikuti petunjuk instalasi ini dan pilih "Deteksi Kamera"',
      settings: 'Pengaturan Kamera',
      settingsHint: 'Rasio, cermin, rotasi, kualitas, dan preset',
      debugFps: 'Tampilkan overlay FPS di pratinjau',
      eject: 'Lepas Kamera',
      ejecting: 'Melepas kamera...',
      ejected: 'Kamera berhasil dilepas',
      ejectFailed: 'Gagal melepas kamera',
      ejectAndRestart: 'Kamera dilepas, memulai ulang aplikasi...',
      ejectDevMode:
        'Kamera dilepas. Silakan mulai ulang aplikasi secara manual dalam mode pengembangan.',
      replugRequired: 'Silakan cabut dan pasang kembali kabel USB atau muat ulang aplikasi',
      driverStatus: {
        installed: 'Driver siap',
        uninstalled: 'Belum terpasang/Terpakai',
        wrongDriver: 'Driver salah — jalankan Zadig',
        disconnected: 'Terputus',
        unknown: 'Gagal memeriksa driver'
      },
      warning: {
        blackTitle: 'Gambar Hitam Terdeteksi',
        blackImage:
          'Kamera menampilkan gambar hitam. Silakan periksa apakah penutup lensa sudah dilepas atau kamera terhubung dengan benar.',
        darkTitle: 'Gambar Terlalu Gelap',
        darkImage:
          'Gambar kamera terlalu gelap. Silakan periksa kondisi pencahayaan atau pengaturan kamera.',
        noiseTitle: 'Kualitas Gambar Buruk',
        noiseImage: 'Kualitas gambar kamera buruk. Silakan periksa koneksi kamera dan pengaturan.',
        genericTitle: 'Masalah Kamera Terdeteksi',
        generic: 'Kamera memiliki masalah kualitas. Silakan periksa pengaturan kamera dan koneksi.',
        troubleshooting: 'Tips Troubleshooting:',
        tip1: 'Pastikan penutup lensa kamera sudah dilepas',
        tip2: 'Periksa apakah kamera terhubung dengan benar',
        tip3: 'Verifikasi pencahayaan yang memadai di area',
        tip4: 'Coba hubungkan kembali kamera',
        retry: 'Coba Lagi Kamera',
        ignore: 'Abaikan'
      }
    },
    paperRoll: {
      title: 'Kertas',
      notSet: 'Belum diisi. Isi jumlahnya saat pasang roll.',
      remaining: 'Sisa ~{{left}} dari {{total}}',
      loaded: 'dipasang {{date}}',
      newRoll: 'Roll baru',
      countPlaceholder: 'Sisa di printer',
      errorCount: 'Isi angka yang tampil di printer.',
      errorAuth: 'Login habis (401). Logout, login lagi, lalu coba lagi.',
      errorDevice: 'Booth belum terdaftar (404). Tunggu beberapa menit setelah buka aplikasi, lalu coba lagi.',
      errorNetwork: 'Tidak ada koneksi. Cek internet, lalu coba lagi.',
      errorGeneric: 'Gagal menyimpan'
    },
    printer: {
      title: 'Printer',
      enablePrinting: 'Aktifkan Pencetakan',
      selectPrinter: 'Pilih Printer',
      savedNotDetected: '{{printer}} — belum terdeteksi saat ini',
      settings: 'Pengaturan Printer',
      settingsHint: 'Ukuran kertas, DPI, margin, skala, dan posisi',
      notAvailable: 'Konfigurasi pencetakan tidak tersedia.',
      webPrintHint:
        'Jika diaktifkan, aplikasi dapat memicu pencetakan (biasanya dialog cetak browser). Cetak otomatis tanpa dialog memerlukan aplikasi desktop.',
      downloadDesktop: 'Unduh aplikasi desktop untuk konfigurasi cetak otomatis.',
      guideDescriptionPrefix: 'Lihat',
      guideLink: 'panduan printer',
      guideDescriptionSuffix: 'untuk pengaturan dan konfigurasi printer',
      systemDefault: 'System Default',
      scaleFactor: 'Faktor Skala (10-200%)',
      scaleFactorHelp: 'Persentase skala (10-200%)',
      printBackground: 'Cetak Latar Belakang',
      printBackgroundHint: 'Sertakan warna dan gambar latar belakang saat mencetak.'
    },
    download: {
      title: 'Pengaturan Unduhan',
      saveLocal: 'Simpan file ke perangkat lokal',
      explanation:
        'Saat diaktifkan, foto, GIF, dan video akan secara otomatis diunduh ke perangkat Anda.',
      savePath: 'File akan disimpan di jalur pictures/dreambooth.',
      savePathNew: 'File akan disimpan di folder Pictures/Dreambooth Gallery/{{date}} Anda.'
    },
    pin: {
      title: 'PIN Kunci Layar Penuh',
      placeholder: 'PIN',
      noPin: 'PIN belum diatur',
      change: 'Ubah'
    },
    autoStart: {
      title: 'Pengaturan Mulai Otomatis',
      withProject: 'Mulai otomatis dengan proyek ini',
      explanation:
        'Saat diaktifkan, aplikasi akan otomatis memulai dengan proyek ini saat diluncurkan'
    },
    display: {
      title: 'Monitor',
      monitor: 'Monitor',
      primary: 'Utama',
      here: 'Dipakai',
      moved: 'Booth dipindah ke monitor itu',
      moveFailed: 'Gagal memindahkan booth ke monitor itu',
      unavailable: 'Pemilihan monitor tidak tersedia di lingkungan ini',
      explanation:
        'Ketuk monitor untuk langsung memindahkan booth ini ke sana. Tiap booth mengingat monitornya sendiri.'
    },
    screensaver: {
      title: 'Screensaver',
      label: 'Aktifkan Screensaver (Mode Idle)',
      noVideo: 'Tidak ada video screensaver yang dikonfigurasi untuk proyek ini. Unggah video di dashboard untuk menggunakan fitur ini.',
      usingProjectTimeout: 'Menggunakan timeout idle proyek:',
      seconds: 'detik',
      configuredInDashboard: '(dikonfigurasi di dashboard)',
      timeout: 'Timeout (detik):',
      explanation: 'Secara otomatis memutar video screensaver proyek saat idle, dan memutuskan kamera untuk menghemat daya.',
      tapToContinue: 'Ketuk di mana saja untuk melanjutkan',
      controlledByProject: 'Pengaturan ini dikontrol oleh konfigurasi proyek di dashboard.'
    },
    browserGestures: {
      title: 'Browser web',
      label: 'Blokir gestur browser (pull-to-refresh, swipe-back, pinch zoom)',
      explanation:
        'Pelindung sentuh mode kiosk untuk browser tablet: menekan pull-to-refresh dan overscroll, memblokir navigasi swipe dari tepi layar, serta menonaktifkan zoom cubit dan ketuk ganda. Beberapa gestur sistem hanya bisa dikunci dari OS — di iPad gunakan Guided Access. Menambahkan aplikasi ke layar beranda (standalone) makin memperbaiki perilaku.'
    },
    autoLaunch: {
      title: 'Auto-Launch',
      label: 'Start app at system login',
      explanation:
        'Secara otomatis memulai aplikasi saat Anda masuk ke komputer (setelah startup, restart, atau login). Tidak berlaku untuk bangun dari sleep/hibernate.',
      enabled: 'App will start at system login',
      disabled: 'App will not start at system login',
      error: 'Gagal memperbarui pengaturan auto-launch'
    },
    systemControls: {
      title: 'Windows System Controls',
      description:
        'Pengaturan Windows yang direkomendasikan untuk mengoptimalkan pengalaman photobooth Anda. Pengaturan ini memerlukan hak administrator dan akan meminta elevasi saat menerapkan perubahan.',
      edgeSwipe: {
        title: 'Disable screen-edge touch swipes',
        explanation:
          'Mencegah pelanggan secara tidak sengaja keluar dari aplikasi photobooth dengan swipe dari tepi layar. Perubahan ini memerlukan restart Windows agar berlaku.'
      },
      lockOnWake: {
        title: 'Lock screen on wake',
        disable: 'Nonaktifkan',
        enable: 'Aktifkan',
        explanation:
          'Menonaktifkan lock screen saat komputer bangun, memungkinkan aplikasi photobooth terbuka langsung tanpa memerlukan password atau klik sign in'
      },
      timeouts: {
        title: 'Cegah layar mati & mode sleep (kecuali laptop pada mode baterai)',
        enable: 'Aktifkan',
        explanation:
          'Mencegah layar mati selama sesi photobooth. Daya AC: tidak pernah sleep/display off. Daya DC (laptop): timeout 30 menit'
      },
      scriptDownloaded:
        'Skrip diunduh ke folder Downloads. Klik kanan dan pilih "Run with PowerShell" untuk menerapkan.',
      edgeSwipesEnabled: 'Edge swipes diaktifkan',
      lockOnWakeEnabled: 'Lock screen on wake diaktifkan',
      applyingChanges: 'Menerapkan perubahan...',
      storeInstallWarning:
        'Windows system controls memerlukan hak administrator. Jika eksekusi otomatis gagal, skrip akan diunduh ke folder Downloads untuk eksekusi manual.'
    },
    appVersion: {
      label: 'Versi Aplikasi:',
      updateAvailable: 'Versi Aplikasi Baru Tersedia! Silakan perbarui ke'
    },
    autoUpdate: {
      title: 'Pembaruan Aplikasi',
      toggleLabel: 'Perbarui otomatis',
      checkNow: 'Cek sekarang',
      installNow: 'Pasang dan mulai ulang',
      openDashboard: 'Buka dashboard',
      installFailed: 'Pembaruan gagal dipasang. {{reason}}',
      installBusy:
        'Booth sedang dipakai: masih ada sesi, cetak, atau unggahan yang berjalan. Coba lagi saat booth sudah senggang.',
      explanation:
        'Booth mengunduh sendiri pembaruan patch dan minor saat sedang senggang, lalu memasangnya saat aplikasi dibuka berikutnya (bisa dilewati saat itu) atau saat Anda menekan Pasang dan mulai ulang. Versi mayor tidak pernah dipasang sendiri: unduh dari dashboard.',
      launchPrompt: {
        title: 'Memperbarui ke versi {{version}}',
        body: 'Aplikasi akan menutup lalu terbuka lagi sendiri. Prosesnya sekitar satu menit.',
        countdown: 'Mulai dalam {{seconds}} detik',
        skip: 'Buka tanpa update'
      },
      status: {
        upToDate: 'Sudah versi terbaru',
        checking: 'Mengecek pembaruan...',
        available: 'Versi {{version}} tersedia — aktifkan untuk memasangnya',
        waiting: 'Versi {{version}} akan diunduh saat booth senggang',
        downloading: 'Mengunduh {{version}} ({{percent}}%)',
        ready:
          'Versi {{version}} siap. Dipasang saat aplikasi dibuka berikutnya, atau tekan Pasang dan mulai ulang',
        installing: 'Versi {{version}} segera dipasang',
        major: 'Versi {{version}} adalah pembaruan mayor — pasang lewat dashboard',
        error: 'Server pembaruan tidak bisa dihubungi. Akan dicoba lagi nanti.'
      }
    },
    buttons: {
      seeAllProjects: 'Lihat Semua Proyek',
      editProject: 'Edit Proyek',
      continue: 'Lanjutkan'
    },
    pinModal: {
      change: {
        title: 'Ubah PIN',
        subtitle: 'Masukkan PIN 6-digit yang akan digunakan untuk:'
      },
      set: {
        title: 'Atur PIN',
        subtitle: 'Masukkan PIN 6-digit baru yang akan digunakan untuk:'
      },
      usage: {
        unlock: 'Membuka kunci mode layar penuh',
        skip: 'Melewati pembayaran jika diperlukan'
      }
    },
    feedback: {
      selectCamera: 'Pilih kamera terlebih dahulu!',
      pinUpdated: 'PIN berhasil diperbarui'
    }
  },
  camera: {
    prompt: {
      title: 'Klik untuk Memulai Kamera',
      instruction: 'Klik di mana saja untuk menghubungkan kamera Anda'
    }
  },
  nav: {
    dashboard: 'Dasbor',
    title: 'Dreambooth',
    settings: 'Pengaturan'
  },
  login: {
    successMessage: 'Berhasil masuk!',
    networkError: 'Kesalahan jaringan. Silakan coba lagi nanti.',
    logoAlt: 'Logo DreamBooth',
    welcomeBack: 'Selamat Datang Kembali',
    signInPrompt: 'Masuk ke akun Anda',
    email: 'Email',
    password: 'Kata Sandi',
    loginButton: 'Masuk',
    resetPasswordPrompt: 'Perlu mengatur ulang kata sandi atau membuat akun?',
    visitDashboard: 'Kunjungi dasbor web',
    showPassword: 'Tampilkan kata sandi',
    hidePassword: 'Sembunyikan kata sandi',
    or: 'ATAU',
    continueWithGoogle: 'Lanjutkan dengan Google',
    googleWaitingTitle: 'Selesaikan masuk dengan Google',
    googleWaitingBody:
      'Browser sudah terbuka. Masuk di sana, lalu kembali ke aplikasi ini — prosesnya akan lanjut sendiri.',
    googleWaitingCancel: 'Batal'
  },
  projects: {
    title: 'Proyek',
    subtitle: 'Kelola proyek Anda di sini',
    sessionCredits: 'Kredit',
    refreshCredits: 'Muat ulang kredit',
    topUpButton: 'Isi Ulang',
    loading: 'Memuat proyek...',
    noProjects: 'Tidak ada proyek tersedia',
    lastUpdated: 'Terakhir Diperbarui',
    menuOptions: 'Opsi',
    run: 'Jalankan'
  },
  projectNav: {
    logoAlt: 'Logo DreamBooth',
    deviceSubscription: 'Langganan Perangkat',
    activeSubscription: 'Langganan Aktif',
    planType: 'Jenis Paket:',
    computerName: 'Nama Komputer:',
    osInfo: 'Info OS:',
    activationCode: 'Kode Aktivasi:',
    changeSubscription: 'Ubah Langganan',
    activateSubscription: 'Aktifkan Langganan',
    enterActivationCode: 'Masukkan kode aktivasi Anda untuk {{action}} perangkat ini.',
    reactivate: 'Aktifkan Kembali',
    activate: 'Aktifkan',
    deactivateWarning: 'Catatan: Ini akan menonaktifkan langganan di perangkat lain Anda.',
    enterActivationCodePlaceholder: 'Masukkan kode aktivasi Anda',
    noCodeQuestion: 'Tidak punya kode aktivasi?',
    purchaseSubscription: 'Dapatkan kode langganan',
    greeting: 'Halo, {{username}}!',
    subscription: 'langganan',
    failedReactivate: 'Gagal mengaktifkan kembali langganan',
    deviceChanged: 'Perangkat berhasil diubah!',
    failedActivate: 'Gagal mengaktifkan langganan',
    activatedSuccess: 'Langganan berhasil diaktifkan!',
    reactivating: 'Mengaktifkan kembali...',
    activating: 'Mengaktifkan...',
    recoverTitle: 'Pulihkan langganan Anda',
    recoverSavedCode: 'Kode tersimpan',
    recoverReapply: 'Terapkan ulang langganan tersimpan',
    recoverCheckAgain: 'Periksa lagi',
    recoverMoveDevice: 'Pindahkan lisensi ke komputer ini',
    recovering: 'Memulihkan...',
    recoverMoveConfirmTitle: 'Pindahkan langganan ke perangkat ini?',
    recoverMoveConfirmMessage:
      'Ini akan mengaktifkan langganan Anda di komputer INI dan menonaktifkannya di perangkat lain. Lanjutkan?',
    recoverExpiredNote: 'Langganan Anda telah berakhir. Perpanjang di dasbor, lalu periksa lagi.',
    recoverNotActiveNote: 'Langganan Anda belum aktif. Aktifkan di dasbor, lalu periksa lagi.',
    recoverSessionNote: 'Silakan masuk kembali untuk memulihkan langganan Anda.',
    recoverWrongAccountNote: 'Langganan ini milik akun lain. Masuk dengan akun yang benar.',
    recoverSuccess: 'Langganan berhasil dipulihkan!',
    recoverStillInvalid: 'Tidak dapat memulihkan langganan. Silakan coba lagi atau hubungi dukungan.',
    getSubscription: 'Dapatkan langganan'
  },
  proTrial: {
    expired: 'Kadaluarsa',
    timeRemainingDays: '{{days}} hari tersisa',
    timeRemainingRealtime: '{{hours}}j {{minutes}}m {{seconds}}d tersisa'
  },
  interruptedSession: {
    title: 'Sesi kamu tadi terputus',
    message: 'Kamu sudah bayar. Lanjutkan tanpa bayar lagi.',
    resume: 'Lanjutkan sesi saya',
    decline: 'Bukan sesi saya',
  },
  controls: {
    settings: 'Pengaturan',
    exitFullscreen: 'Keluar Layar Penuh',
    enterFullscreen: 'Masuk Layar Penuh',
    skipPayment: 'Lewati Pembayaran',
    exitSession: 'Keluar Sesi',
    enterPin: 'Masukkan PIN 6-digit untuk keluar dari mode layar penuh',
    skipPaymentPin: 'Masukkan PIN 6-digit untuk melewati pembayaran',
    exitPaidSessionPin: 'Sesi ini sudah dibayar. Masukkan PIN 6-digit untuk mengakhirinya.',
    cancelSession: 'Batalkan Sesi',
    cancelConfirm: 'Kembali ke Beranda?',
    wrongPin: 'Oops! PIN salah. Silakan coba lagi.',
    quitting: 'Mengakhiri sesi…',
    quittingMessage: 'Mohon tunggu sementara kami menyimpan data sesi Anda.'
  },
  reprint: {
    title: 'Cetak Ulang',
    pinRequired: 'PIN Diperlukan',
    enterPinToAccess: 'Masukkan PIN untuk mengakses menu cetak ulang',
    unavailableTitle: 'Cetak ulang tidak tersedia di browser',
    unavailableBody:
      'Daftar sesi, cetak ulang, dan unggah ulang hanya ada di aplikasi desktop Dreambooth, karena aplikasi itu yang menyimpan salinan foto di PC booth.',
    unavailableHowTo:
      'Kalau booth ini dijalankan lewat browser, foto yang gagal terunggah tidak bisa dikirim ulang dari sini. Foto yang berhasil terunggah ada di galeri sesi di dashboard. Kalau booth Anda memakai aplikasi desktop, buka Cetak Ulang di sana: tombol Retry All dan Re-sync records ada di layar itu.',
    deleteSession: 'Hapus sesi',
    deleteButton: 'Hapus',
    deleting: 'Menghapus...',
    deleteConfirmTitle: 'Hapus sesi ini?',
    deleteConfirmBody:
      'Semua {{count}} foto strip dan media lain dari sesi ini akan dihapus dari PC booth ini. Tindakan ini tidak bisa dibatalkan.',
    deleteNotUploadedWarning:
      'Sesi ini belum terkonfirmasi terunggah. Setelah dihapus, fotonya tidak bisa diunggah ke galeri lagi.',
    deleteSuccess: 'Sesi dihapus dari booth ini',
    deleteBusy: 'Sesi ini sedang diunggah. Coba lagi sebentar lagi.',
    deleteError: 'Gagal menghapus sesi',
    deleteUnsupported: 'Hapus tidak didukung di versi aplikasi ini'
  },
  cameraSettings: {
    title: 'Pengaturan Kamera',
    appSettings: 'Pengaturan Aplikasi',
    appSettingsHint: {
      title: 'Panduan Pengaturan Aplikasi',
      description:
        'Pengaturan ini mengontrol bagaimana aplikasi berinteraksi dengan kamera Anda. Sesuaikan kualitas pratinjau untuk performa lebih baik, atur rasio pengambilan yang diinginkan, aktifkan mode cermin untuk pratinjau gaya selfie, dan pilih antara menggunakan "shutter" fisik kamera atau menangkap tangkapan layar pratinjau. Anda juga dapat mengaktifkan/menonaktifkan efek filter pada pratinjau langsung.'
    },
    powerSaving: {
      title: 'Mode Hemat Daya',
      enable: 'Aktifkan mode hemat daya',
      description: 'Mengurangi beban kamera dan mencegah panas berlebih'
    },
    frameRate: {
      title: 'Kecepatan Frame Pratinjau',
      adjust: 'Sesuaikan kecepatan frame',
      fps: 'FPS',
      description:
        'Kecepatan frame yang lebih rendah mengurangi beban kamera dan panas yang dihasilkan'
    },
    previewQuality: {
      title: 'Kualitas Pratinjau',
      options: {
        custom: 'Custom',
        pixelated: 'Pixelated (240p)',
        low: 'Low (480p)',
        medium: 'Medium (720p)',
        high: 'High (1080p)',
        ultra: 'Ultra (1440p)'
      }
    },
    captureRatio: {
      title: 'Capture Ratio',
      options: {
        auto: 'Auto',
        square: 'Square (1:1)',
        portrait: 'Portrait (3:4)',
        landscape: 'Landscape (4:3)',
        widePortrait: 'Wide Portrait (9:16)',
        wideLandscape: 'Wide Landscape (16:9)',
        classicPortrait: 'Classic Portrait (2:3)',
        classicLandscape: 'Classic Landscape (3:2)'
      }
    },
    mirror: {
      title: 'Cermin Pratinjau',
      enable: 'Cerminkan pratinjau kamera'
    },
    useShutterCapture: {
      title: 'Gunakan shutter capture',
      description:
        'Gunakan shutter fisik kamera untuk mengambil foto. Saat dinonaktifkan, aplikasi akan menangkap tangkapan layar pratinjau saat ini. Gunakan mode shutter untuk foto berkualitas lebih baik, atau tangkapan pratinjau untuk respons lebih cepat.',
      enable: 'Alihkan shutter capture'
    },
    focusMode: {
      title: 'Fokus',
      description:
        'Kapan booth melakukan fokus sebelum mengambil foto. Proses fokus berjalan selama hitung mundur, jadi shutter tidak perlu menunggunya.',
      everyPhoto: 'Setiap foto (default)',
      oncePerSession: 'Sekali per sesi',
      fixed: 'Tetap \u2014 jangan pernah fokus',
      hint: {
        'every-photo': 'Paling aman. Pakai ini kalau tamu berdiri di jarak yang berbeda-beda.',
        'once-per-session':
          'Fokus sekali, lalu dipakai lagi untuk sisa strip. Paling cocok kalau ada tanda di lantai supaya semua orang berdiri di titik yang sama.',
        fixed:
          'Booth tidak akan pernah menyentuh fokus. Pakai ini hanya kalau lensa sudah difokuskan manual (saklar lensa di MF).'
      }
    },
    flashSetupGuide: {
      title: 'Pakai flash? Setting menu kamera ini wajib diubah',
      description:
        'Setting ini ada di menu kamera dan tidak bisa diubah oleh aplikasi. Di live view, kamera hanya memicu flash yang dikenali sebagai unit TTL asli — dengan flash manual satu pin (mis. Godox TT520) kamera tetap di mode silent/elektronik dan flash tidak akan pernah menyala.',
      canonDslr: {
        title: 'Canon DSLR (kelas 1500D / 2000D)',
        silentLv:
          'Menu Live View shooting → Silent LV shoot → Disable. Ini kuncinya — flash tidak akan menyala di live view sampai setting ini dimatikan.',
        expoSim:
          'Exposure simulation → Disable, supaya pratinjau tetap terang di ruangan gelap untuk flash.',
        flashFiring:
          'Flash Control → Flash firing → Enable (setting ini sering diam-diam kembali ke Disabled).',
        mf: 'Lensa ke MF, fokuskan sekali di jarak tamu berdiri — di ruangan gelap autofocus gagal dan memblokir shutter.',
        exposure:
          'Exposure manual di atau di bawah sync speed: 1/125–1/160, f/7–f/8, ISO 100–200.'
      },
      canonMirrorless: {
        title: 'Canon mirrorless (R100, M50)',
        silentShutter:
          'Silent Shutter Mode → Off, dan Shutter mode → Mechanical atau Electronic 1st-curtain. Electronic shutter penuh tidak akan pernah memicu flash.',
        m50Firmware:
          'M50: firmware 1.0.2 atau lebih baru (perbaikan resmi Canon untuk komunikasi hot shoe yang tidak stabil).',
        expoSimMf: 'Exposure simulation → Disable; lensa ke MF.'
      },
      nikon: {
        title: 'Nikon',
        silentLv: 'Photo Shooting Menu → Silent live view photography → Off.',
        releasePriority: 'Custom setting a1/a2 (prioritas AF-C/AF-S) → Release, bukan Focus.',
        mf: 'Manual focus lewat switch lensa (mode fokus bersifat read-only lewat USB di bodi entry level).',
        popupAuto: 'Tutup flash pop-up; mode dial jangan di AUTO.'
      },
      sony: {
        title: 'Sony',
        silent:
          'Silent Shooting → Off, DAN set ulang e-Front Curtain Shutter secara manual — mematikan Silent tidak mengembalikannya.',
        pcRemote: 'Mode USB → PC Remote.',
        releasePriority: 'Priority Set in AF-S → Release.',
        verify: 'Pastikan menu flash sudah tidak abu-abu lagi.'
      },
      hardwareTip:
        'Hardware flash: jalankan speedlite manual di ≤ 1/2 power (recycle di power penuh sampai 5 detik dengan baterai AA — foto saat recycle hasilnya hitam). Trigger TTL di hot shoe (mis. Godox X1T) dikenali kamera dan otomatis mematikan mode silent.'
    },
    cameraConfig: 'Konfigurasi Kamera',
    cameraConfigWarning: {
      title: 'Peringatan Konfigurasi Kamera',
      description:
        'Pengaturan kamera dapat bervariasi antar model kamera. Beberapa pengaturan mungkin tidak dapat disesuaikan melalui perangkat lunak ini. Please test each setting to ensure it works with your camera.'
    },
    primary: 'Kamera Utama',
    secondary: 'Kamera Kedua',
    qualityPresets: {
      pixelated: 'Pixelated (240p)',
      low: 'Low (480p)',
      medium: 'Medium (720p)',
      high: 'High (1080p)',
      ultra: 'Ultra (1440p)',
      ultraPlus: 'Ultra+ (1800p)',
      max4k: 'Maksimum (4K · 2160p)'
    },
    captureImpact: {
      level: {
        light: 'Upload ringan',
        moderate: 'Upload lebih berat',
        heavy: 'Upload berat'
      },
      estimate: '≈{{size}} MB per foto · ≈{{seconds}} detik upload di koneksi {{mbps}} Mbps',
      optionEstimate: '≈{{size}} MB',
      infoLabel: 'Detail dampak resolusi',
      infoTitle: 'Resolusi lebih tinggi menambah waktu upload',
      infoBody:
        'Setiap foto hasil capture diupload ke galeri. Resolusi dua kali lebih besar membuat ukuran file ikut kira-kira dua kali lebih besar, jadi tiap upload makin lama, antrean makin lambat kalau Wi-Fi venue lemah, dan disk lokal lebih cepat penuh. Estimasi memakai asumsi koneksi {{mbps}} Mbps.',
      heavyWarning:
        'Resolusi ini menghasilkan file besar. Pakai hanya kalau koneksi stabil dan cepat — di Wi-Fi venue yang lemah antrean upload bisa tertinggal dan tamu menunggu lebih lama untuk fotonya.',
      jpegComparison: 'Sebagai JPEG: ≈{{size}} MB per foto',
      heavyWarningPng:
        'Format PNG yang membuat file ini berat. Kalau Wi-Fi venue lemah, ganti Format Foto (paling bawah bagian ini) ke JPEG — kalau tidak, antrean upload tertinggal dan tamu menunggu lebih lama untuk fotonya.'
    },
    captureFormat: {
      title: 'Format Foto',
      description:
        'Jenis file setiap foto yang disimpan — dipakai untuk strip, cetak, dan upload ke galeri. Hanya berlaku untuk foto baru.',
      options: {
        png: 'PNG · lossless, file paling besar',
        jpeg: 'JPEG · file lebih kecil, upload lebih cepat'
      },
      hint: {
        png: 'Sebelum disimpan, gambar sudah dikompres JPEG sesuai preset Capture Image Quality, jadi PNG menyimpannya tanpa kompresi tambahan tapi terutama hanya menambah ukuran file.',
        jpeg: 'Disimpan sesuai preset Capture Image Quality. Mulai High ke atas hasilnya terlihat sama di cetakan dan galeri, dengan ukuran sekitar sepertiga PNG.'
      }
    },
    shutterSpeed: {
      '1_4': '1/4s (paling lambat, paling terang)',
      '1_8': '1/8s',
      '1_15': '1/15s',
      '1_30': '1/30s (default)',
      '1_60': '1/60s',
      '1_125': '1/125s',
      '1_200': '1/200s (paling cepat, batas flash sync)'
    }
  },
  cameraConfig: {
    label: '{label}'
  },
  cameraGuide: {
    title: 'Mari siapkan kamera Anda terlebih dahulu!',
    step1: {
      title: 'Hubungkan Kamera Anda',
      bullet1: 'Pastikan kamera Anda dalam keadaan ON',
      bullet2: 'Hubungkan ke komputer Anda melalui kabel USB',
      bullet3: 'Tunggu beberapa detik agar Windows mendeteksinya'
    },
    step2: {
      title: 'Instal Driver',
      storeWarning:
        'Instalasi driver memerlukan hak administrator. Dalam versi Microsoft Store, driver harus diinstal secara manual.',
      viewManualInstructions: 'Lihat instruksi instalasi manual',
      electronInstruction: 'Buka Zadig untuk menginstal driver yang diperlukan.',
      webInstruction: 'Unduh dan jalankan Zadig untuk menginstal driver yang diperlukan.',
      processing: 'Memproses...',
      openZadig: 'Buka Zadig',
      downloadZadig: 'Unduh Zadig',
      failedToOpen: 'Gagal membuka Zadig'
    },
    step3: {
      title: 'Di Zadig',
      instruction1: 'Klik Options → List All Devices',
      instruction2: 'Temukan kamera Anda di daftar dropdown (mis. Canon Digital Camera)',
      instruction3: 'Klik Replace Driver dan tunggu hingga selesai',
      instruction4: 'Tutup Zadig dan klik Done'
    },
    buttons: {
      close: 'Tutup',
      done: 'Selesai'
    }
  },
  printerGuide: {
    title: 'Panduan Pengaturan Printer',
    faq1: {
      question: 'Bagaimana cara mengaktifkan double inch cut?',
      answer:
        'Double inch cut memungkinkan Anda mencetak dua gambar pada satu lembar yang dapat dipotong menjadi dua. Pastikan driver printer Anda terpasang dengan benar. Buka Windows Print Preferences (klik kanan printer di Settings > Printers & scanners > Printer preferences), konfigurasi ukuran kertas sesuai kebutuhan, dan sesuaikan pengaturan cetak. Aplikasi akan menangani tata letak secara otomatis.'
    },
    faq2: {
      question: 'Apa itu printer sekunder dan tersier?',
      answer:
        'Printer sekunder dan tersier menawarkan dua tujuan: Special Frames - mengarahkan frame tertentu ke printer khusus (berguna untuk jenis frame berbeda di printer berbeda), dan Parallel Printing - mendistribusikan pekerjaan cetak ke beberapa printer untuk mempercepat sesi multi-cetak.'
    },
    faq3: {
      question: 'Apa itu remote printer?',
      answer:
        'Remote printer memungkinkan Anda mencetak dari antarmuka web ke printer yang terhubung ke aplikasi desktop Anda melalui tunnel yang aman. Aplikasi desktop membuat URL tunnel yang digunakan antarmuka web untuk mengirim pekerjaan cetak. Ini berguna untuk mencetak dari lokasi jarak jauh atau alur kerja berbasis web.'
    },
    buttons: {
      close: 'Tutup'
    }
  },
  remotePrintSettings: {
    title: 'Pengaturan Cetak Jarak Jauh',
    subtitle: 'Ganti pengaturan cetak booth untuk cetakan yang dikirim dari perangkat ini.',
    noPrinter: 'Pilih printer jarak jauh terlebih dahulu.',
    useBoothDefaults: 'Gunakan pengaturan booth'
  },
  printerSettings: {
    projectBinding: {
      useForProject: 'Pakai printer ini untuk project aktif: {{title}}',
      noPrinter: 'Belum ada printer dipilih di slot ini — cetakan yang terikat kembali ke routing frame/kategori/default.',
      unbind: 'Lepas ikatan',
      help: 'Frame dan kategori yang diklaim printer lain tetap menang; sisanya dari project ini dicetak di sini.'
    },
    orientation: 'Orientasi',
    landscape: 'Lanskap',
    portrait: 'Potret',
    pageSize: 'Ukuran Halaman (mikron)',
    width: 'Lebar',
    height: 'Tinggi',
    printPositionShift: 'Pergeseran Posisi Cetak (cm)',
    testPrintInfo: {
      title: 'Test Print Configuration',
      description:
        'Use this section to test print settings and save your preferred configuration. These settings will be used for automated printing during photo sessions.'
    },
    secondaryPrinterInfo: {
      title: 'Secondary Printer Configuration',
      description:
        'Configure a secondary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
    },
    tertiaryPrinterInfo: {
      title: 'Konfigurasi Printer Tersier',
      description:
        'Konfigurasi printer tersier untuk frame tertentu. Anda dapat memilih frame mana yang akan dicetak menggunakan printer ini. Ini berguna untuk mencetak jenis frame berbeda di printer berbeda.'
    },
    frameTest: {
      title: 'Frame For Print Testing',
      dragAndDrop: 'Drag and drop an image here, or click to select'
    },
    feedback: {
      selectPrinter: 'Pilih printer terlebih dahulu',
      printInitiated: 'Pencetakan Dimulai',
      printFailed: 'Gagal mencetak'
    },
    smartSetup: {
      loading: 'Memuat kemampuan printer...'
    },
    androidPrinterIp: {
      label: 'IP Printer (cetak tanpa dialog)',
      explanation:
        'Alamat jaringan printer, untuk mencetak langsung lewat port 9100. Kosongkan untuk memakai dialog cetak Android.'
    },
    selectFrames: 'Pilih Frame',
    selectFramesDefault: 'Pilih Frame untuk Printer Default',
    selectFramesDescriptionDefault:
      'Pilih frame dan kategori yang dicetak printer ini. Frame yang tidak dipilih tetap dicetak di sini sebagai fallback.',
    routing: {
      framesForThisPrinter: 'Frame untuk printer ini',
      allFrames: 'Semua frame (default)',
      onlySelected: 'Hanya frame dan kategori terpilih',
      defaultSummary:
        '{{frames}} frame, {{categories}} kategori dipilih. Frame yang tidak dipilih tetap dicetak di sini.',
      categories: 'Kategori',
      categoriesHint:
        'Memilih kategori mencakup semua frame di dalamnya — termasuk frame yang ditambahkan nanti.',
      individualFrames: 'Frame satuan',
      frameCount: '{{count}} frame',
      viaCategory: 'Termasuk lewat kategori',
      sharedBadge: 'Juga di printer lain'
    },
    custom: 'Custom',
    noCapabilities: 'Tidak ada kemampuan tersedia - konfigurasi manual',
    preset: 'Preset',
    presetHelp: 'Pilih preset atau pilih Custom untuk mengonfigurasi pengaturan secara manual',
    noCapabilitiesHelp: 'Konfigurasi pengaturan secara manual di bagian Details di bawah',
    recommended: {
      label: 'Rekomendasi Dreambooth',
      short: 'Rekomendasi'
    },
    details: 'Detail Preset',
    detailsHint: 'Orientasi, ukuran kertas, dan DPI di balik preset ini',
    printerDriverDefaults: {
      title: 'Gunakan Default Driver Printer',
      description:
        'Gunakan pengaturan default driver printer untuk margin dan DPI. Kompatibilitas lebih baik dengan driver printer khusus.',
      toggle: 'Alihkan default driver printer'
    },
    csharpPrinting: {
      title: 'C# Direct Printing (Eksperimental)',
      description: 'Gunakan .NET printing untuk keandalan lebih baik dengan printer tertentu',
      toggle: 'Alihkan C# printing'
    },
    printerPurpose: 'Tujuan Printer',
    printerPurposeHelpSecondary:
      'Pilih tujuan untuk printer sekunder: special frames untuk routing berbasis frame, atau parallel untuk distribusi round-robin.',
    printerPurposeHelpTertiary:
      'Pilih tujuan untuk printer tersier: special frames untuk routing berbasis frame, atau parallel untuk distribusi round-robin.',
    specialFramesPrint: 'Cetak frame khusus',
    parallelPrinting: 'Pencetakan Paralel',
    positionShift: 'Perpindahan Posisi Cetak (cm)',
    positionShiftHelp:
      'Sesuaikan perataan cetak. Menyesuaikan margin @page secara independen dari pengaturan margin.',
    positionShiftTop: 'Atas',
    positionShiftBottom: 'Bawah',
    positionShiftLeft: 'Kiri',
    positionShiftRight: 'Kanan',
    positionShiftTopTitle: 'Perpindahan posisi atas',
    positionShiftBottomTitle: 'Perpindahan posisi bawah',
    positionShiftLeftTitle: 'Perpindahan posisi kiri',
    positionShiftRightTitle: 'Perpindahan posisi kanan',
    remotePrinterStatus: {
      title: 'Status Remote Printer',
      rowTitle: 'Printer jarak jauh',
      stateActive: 'Aktif',
      stateInactive: 'Belum tersambung',
      activeHint: 'Terjangkau sebagai {{name}} — cetak ke sini dari {{appUrl}}/strip',
      inactiveHint: 'Saat ini belum bisa dipakai sebagai printer jarak jauh.',
      active: 'Status: Aktif',
      inactive: 'Status: Tidak aktif atau tidak terhubung.',
      name: 'Nama: ',
      description: 'Remote printer aktif. Buka {{appUrl}}/strip untuk mencetak dari jarak jauh.'
    },
    preview: 'Cetak',
    noPreview: 'Tidak ada pratinjau tersedia',
    upload: 'Unggah Template',
    copies: 'Salinan:',
    copiesTitle: 'Jumlah salinan untuk dicetak',
    printResolutionScale: {
      title: 'Skala Resolusi Cetak',
      description:
        'Sesuaikan resolusi gambar cetak akhir. Nilai yang lebih tinggi menghasilkan kualitas lebih baik tetapi ukuran file lebih besar.'
    },
    printResultMinDisplay: {
      title: 'Waktu minimum di layar pemrosesan sebelum Hasil',
      shortTitle: 'Tahan layar proses',
      shortHint: 'Detik untuk menahan layar proses setelah cetak dan simpan selesai, sebelum Result. 0 = langsung.',
      unit: 'dtk',
      description:
        'Setelah cetak dan penyimpanan selesai, biarkan layar pemrosesan terbuka setidaknya selama detik ini. Gunakan 0 untuk langsung ke Hasil begitu selesai. Hanya berlaku jika cetak diaktifkan dan tamu benar-benar mencetak.',
      suffix: 'detik (0 = mati, maks {{max}})'
    },
    defaultPrinter: 'Printer Default',
    secondaryPrinter: 'Printer Sekunder',
    tertiaryPrinter: 'Printer Tersier'
  },
  capture: {
    getReady: 'Bersiaplah!',
    sessionStarting: 'Sesi akan dimulai dalam',
    startNow: 'Mulai Sekarang',
    orWaitCountdown: 'atau tunggu hitungan mundur',
    lookAtCamera: 'Lihat ke Kamera',
    stayStill: 'Harap tetap diam saat kami mengambil foto Anda...',
    captureFailed: 'Pengambilan Gagal',
    stayStillCapturing: 'Harap tetap diam saat pengambilan gambar',
    retryingMoment: 'Mencoba ulang sebentar lagi...',
    lastCaptured: 'Terakhir diambil',
    noPhotoYet: 'Belum ada foto',
    recording: 'REC',
    errorStartingSessionRecorder: 'Kesalahan memulai perekam sesi',
    errorProcessingSessionVideo: 'Kesalahan memproses video sesi',
    selfPhotoScanQR: 'Pindai untuk memicu dari ponsel',
    selfPhotoQRAlt: 'Picu dari ponsel',
    selfPhotoEndSession: 'Akhiri sesi',
    selfPhotoPhotoCount: '{{count}} foto',
    selfPhotoAutoLoop: 'Auto',
    selfPhotoSessionTimer: 'Waktu sesi',
    selfPhotoEndSessionBtn: 'Akhiri Sesi',
    selfPhotoAutoLoopOn: 'Auto capture aktif',
    selfPhotoAutoLoopOff: 'Auto capture nonaktif',
    selfPhotoManualOnly: 'Manual',
    selfPhotoTriggerUrl: 'URL Pemicu',
    selfPhotoTriggerNow: 'Picu pengambilan sekarang',
    removePhoto: 'Hapus foto'
  },
  stickers: {
    title: 'Sticker',
    pageTitle: 'Hias strip kamu',
    tabStickers: 'Sticker',
    tabDraw: 'Draw',
    autoLayout: 'Auto layout',
    replaceTitle: 'Ganti semua sticker?',
    replaceMessage: 'Sticker yang sudah kamu atur akan dihapus, lalu disusun ulang otomatis.',
    clear: 'Hapus semua',
    brush: 'Kuas',
    color: 'Warna',
    customColor: 'Warna lain',
    size: 'Ukuran',
    brushTypes: {
      solid: 'Penuh',
      dashed: 'Putus-putus',
      dotted: 'Titik-titik',
      marker: 'Spidol',
      pencil: 'Pensil',
      airbrush: 'Semprot'
    },
    shuffle: 'Acak',
    none: 'Tanpa sticker',
    undo: 'Urungkan',
    redo: 'Ulangi',
    remove: 'Hapus sticker',
    processing: 'Memproses...',
    processingMessage: 'Tunggu sebentar, strip kamu sedang disiapkan',
    preparing: 'Menyiapkan strip...'
  },
  select: {
    title: 'Atur foto Anda',
    instruction: 'Klik foto untuk memilih, geser untuk memindahkan, cubit untuk memperbesar',
    processingPhotos: 'Memproses Foto',
    pleaseWait: 'Mohon tunggu saat kami memproses foto Anda...',
    printPhotos: 'Cetak Foto',
    tapToFill: 'Ketuk foto Anda untuk mengisi slot cetak',
    swipeInstructions: 'Geser untuk memindahkan, cubit untuk memperbesar',
    mirror: 'Mirror',
    selectionLocked: 'Pilihan dikunci',
    processing: 'Memproses...',
    processingVideo: 'Memproses Video...',
    next: 'Selanjutnya ({{count}}/{{max}})',
    capture: 'Ambil Gambar',
    selected: 'Terpilih',
    clickToSelect: 'Klik untuk Memilih',
    continue: 'Lanjutkan',
    errorGif: 'Kesalahan membuat GIF animasi',
    stripExportFailed: 'Gagal membuat pratinjau strip',
    errorVideo: 'Kesalahan memproses video',
    errorProcessing: 'Terjadi kesalahan saat memproses foto Anda. Silakan coba lagi.',
    selectPhotos: 'Pilih {{count}}/{{max}} foto',
    exportFailed: 'Gagal membuat strip. Melanjutkan tanpa bingkai...',
    exportTimeout: 'Pembuatan strip membutuhkan waktu lebih lama dari yang diharapkan. Mohon tunggu...'
  },
  retake: {
    title: 'Tidak Puas dengan Beberapa Foto?',
    headline: 'Klik foto untuk ambil ulang',
    maxHint: 'Maks {{count}}',
    pillSessions: '{{count}} tersisa · Sesi {{current}} dari {{max}}',
    remainingSessions: 'Ambil ulang tersisa: {{count}}',
    selectUpTo: 'Pilih hingga',
    photos: 'foto',
    toRetake: 'untuk diambil ulang',
    selectToRetake: 'Pilih foto yang ingin Anda ambil ulang',
    sessionCount: 'Sesi ambil ulang {{current}} dari {{max}}',
    photoAlt: 'Foto {{number}}',
    photoSelectedAlt: 'Foto {{number}} dipilih untuk ambil ulang',
    overlayRetake: 'Ambil ulang',
    overlaySelected: 'Terpilih',
    selectedToRetake: 'Dipilih untuk Diambil Ulang',
    clickToRetake: 'Klik untuk Ambil Ulang',
    retakeSelected: 'Retake Foto Terpilih ({{count}})',
    retakeCount: 'Ambil ulang ({{count}})',
    retakeAll: 'Retake Semua',
    keepAll: 'Simpan semua',
    explanation: 'Foto yang dipilih akan dihapus dan Anda akan mengambil foto baru. Tindakan ini tidak dapat dibatalkan.',
    confirmContinue: 'Lanjutkan dengan semua foto?',
    confirmContinueHint: 'Anda bisa kembali untuk mengambil ulang foto mana pun, atau lanjutkan ke langkah berikutnya.',
    confirmKeepAllTitle: 'Apakah Anda puas dengan semua foto?',
    confirmKeepAllMessage: 'Anda dapat memilih untuk mengambil ulang beberapa foto atau melanjutkan ke langkah berikutnya.',
    back: 'Kembali',
    continue: 'Lanjutkan',
    confirmKeepAllNo: 'Tidak, saya ingin mengambil ulang foto saya',
    confirmKeepAllYes: 'Ya, saya ingin melanjutkan',
    confirmingKeepAll: 'Melanjutkan...'
  },
  result: {
    videoReady: 'Video Anda Siap!',
    photosReady: 'Foto Anda Siap!',
    scanQrInstruction: 'Pindai kode QR di bawah untuk mengakses {{type}} Anda',
    video: 'video',
    digitalPhotos: 'foto digital',
    photoStripAlt: 'Strip Foto',
    qrCodeAlt: 'Kode QR',
    sessionId: 'ID Sesi:',
    scanToView: 'Pindai untuk melihat galeri',
    videoRecording: 'Rekaman Video',
    photoStrip: 'Strip Foto',
    backToHome: 'Kembali ke Beranda',
    satisfaction: {
      question: 'Bagaimana pengalaman bilik foto Anda?',
      rateExperience: 'Beri nilai pengalaman Anda',
      thankYou: 'Terima kasih atas masukan Anda!',
      starAlt: 'Beri nilai {{rating}} bintang'
    },
    feedback: {
      title: 'Ceritakan lebih lanjut',
      titleLow: 'Maaf, pengalamannya kurang menyenangkan',
      subtitle: 'Beri nilai bagian yang perlu, sisanya boleh dilewati.',
      tellUsMore: 'Beri masukan lain',
      skip: 'Lewati',
      submit: 'Kirim',
      submitCount: 'Kirim ({{count}})',
      thanks: 'Terima kasih — masukan Anda membantu kami memperbaikinya.',
      starAlt: 'Beri nilai {{aspect}} {{rating}} dari 5',
      aspects: {
        photo: { label: 'Hasil foto', hint: 'Buram, gelap, warnanya aneh' },
        print: { label: 'Hasil cetak', hint: 'Tidak keluar, terpotong, pudar' },
        timing: { label: 'Waktu pengambilan', hint: 'Hitung mundurnya terlalu cepat' },
        choices: { label: 'Frame & filter', hint: 'Pilihannya kurang banyak' },
        payment: { label: 'Pembayaran', hint: 'QR atau prosesnya ribet' },
        digital: { label: 'Foto digital', hint: 'Susah dipindai atau disimpan' }
      }
    },
    emailGallery: {
      title: 'Kirim tautan galeri ke email',
      description: 'Satu pesan berisi tautan ke galeri ini.',
      emailLabel: 'Email',
      emailPlaceholder: 'nama@email.com',
      sendEmail: 'Kirim',
      sendingEmail: 'Mengirim…',
      invalidEmail: 'Masukkan alamat email yang valid.',
      shareSuccess: 'Terkirim! Periksa email Anda.',
      shareError: 'Tidak terkirim. Coba lagi.',
      shareNetworkError: 'Koneksi bermasalah. Coba lagi.',
      close: 'Tutup'
    },
    postSessionAi: {
      title: 'Peningkatan AI',
      subtitle: 'Poles opsional untuk foto Anda.',
      guestPromptLabel: 'Tambahkan catatan (opsional)',
      guestPromptPlaceholder: 'mis. lebih vintage',
      skip: 'Lewati',
      apply: 'Terapkan AI',
      applying: 'Menerapkan…',
      continue: 'Lanjutkan',
      errorGeneric: 'Terjadi kesalahan. Ketuk Lewati untuk lanjut.',
      insufficientCredits: 'AI sedang tidak tersedia. Ketuk Lewati untuk lanjut.',
      missingStrip: 'Strip belum siap. Ketuk Lewati untuk lanjut.',
      missingRaw: 'Belum ada foto untuk AI. Ketuk Lewati untuk lanjut.',
      missingRawNeedHttps: 'Foto masih diunggah. Ketuk Lewati untuk lanjut.',
      timeout: 'AI butuh waktu lebih lama dari biasanya. Ketuk Lewati untuk lanjut.',
      offline: 'AI butuh koneksi internet. Ketuk Lewati untuk lanjut.',
      stripUploading: 'Menyelesaikan unggahan strip…',
      previewBefore: 'Sebelum',
      previewAfter: 'Sesudah',
      previewCompareHeading: 'Pratinjau sebelum dan sesudah',
      previewLoading: 'Memuat pratinjau…',
      previewMissingUrls: 'Belum ada pratinjau.',
      previewLoadError: 'Tidak dapat memuat pratinjau. Coba lagi.',
      previewImageLoadError: 'Tidak dapat memuat pratinjau. Coba lagi.',
      filmstripAriaLabel: 'Foto sesi Anda',
      filmstripCaption: 'Foto sesi',
      filmstripHelper: 'Ketuk foto untuk menghasilkan mode AI darinya.',
      filmstripThumb: 'Foto sesi',
      filmstripThumbWithAi: 'Foto sesi (AI siap)',
      aiProcessedTooltip: 'Versi AI siap',
      dragToCompareHint: 'Geser untuk membandingkan sebelum dan sesudah.',
      introBadge: 'Efek AI',
      introHeadline: 'Lihat fotomu jadi luar biasa',
      introBody:
        'Versi AI yang unik dari foto favoritmu. Coba di fotomu sendiri sekarang.',
      introCta: 'Coba di fotoku',
      previewEmptyMessage: 'Belum ada pratinjau perbandingan. Anda masih bisa menerapkan AI di bawah.',
      pickOneSpecial: 'Pilih 1 foto spesial untuk dipoles AI.',
      rawOnce: 'AI bisa memoles satu foto per sesi.',
      rawUsedBadge: 'AI dipakai',
      processing: 'Membuat fotomu…',
      processingHint: 'Hanya sebentar. Mohon tunggu.',
      done: 'Ini foto AI-mu!',
      resultMayVary: 'Hasil AI bisa berbeda — simpan atau buang.',
      discard: 'Buang'
    }
  },
  filter: {
    nextFilter: 'Filter berikutnya',
    prevFilter: 'Filter sebelumnya'
  },
  frame: {
    framesListbox: 'Bingkai',
    title: 'Pilih bingkai Anda',
    count: '{{count}} bingkai',
    previewAlt: 'Pratinjau'
  },
  payment: {
    skipGuide: 'Perlu melewati pembayaran? Klik ikon uang di pojok kanan atas dan masukkan PIN.',
    qrisLogoAlt: 'Logo QRIS',
    rail: { qris: 'QRIS', online: 'QR', upi: 'UPI' },
    qrCodeAlt: 'Kode QR',
    qrLoadFailed: 'Gagal memuat kode QR.',
    qrReload: 'Muat ulang kode QR',
    automaticCheck: 'Status pembayaran akan diperiksa secara otomatis.',
    card: {
      cantScan: 'Tidak bisa scan? · Can\'t scan?',
      payByCard: 'Bayar pakai kartu · Pay by card',
      holdTitle: 'Panggil staf kami · Please ask our staff',
      holdBody: 'Tunjukkan layar ini, staf akan memproses kartu Anda di mesin EDC. · Show this screen — staff will run your card on the terminal.',
      holdHeld: 'Sesi Anda ditahan 5 menit · Held for 5 minutes',
      backToPayment: 'Kembali ke {{rail}} · Back to {{rail}}',
      confirmStaff: 'Konfirmasi pembayaran kartu (staf)',
      pinTitle: 'Konfirmasi pembayaran kartu',
      pinSubtitle: 'Khusus staf. Masukkan PIN booth.',
      codePlaceholder: 'Kode approval dari struk EDC',
      codeOptionalPlaceholder: 'Kode approval (opsional) — tekan Enter untuk konfirmasi',
      codeTitle: 'Kode approval',
      codeSubmit: 'Konfirmasi pembayaran',
      codeRequired: 'Masukkan kode approval dari struk.',
      confirmed: 'Pembayaran kartu tercatat',
      terminalTitle: 'Bayar di mesin EDC',
      terminalSending: 'Mengirim nominal ke mesin...',
      terminalTapCard: 'Tap, masukkan, atau gesek kartu di mesin',
      terminalCheck: 'Cek mesin untuk menyelesaikan pembayaran',
      terminalDeclined: 'Kartu ditolak. Coba lagi atau pakai kartu lain.',
      terminalCanceled: 'Pembayaran dibatalkan di mesin.',
      terminalExpired: 'Waktu pembayaran habis',
      terminalFinishing: 'Selesaikan pembayaran di mesin. Layar ini lanjut sendiri.',
      terminalTryAgain: 'Coba lagi',
      terminalCancelOnDevice: 'Batalkan dulu pembayarannya di mesin.',
      terminalAlreadyPaid: 'Pembayaran itu berhasil. Sebentar ya.',
      terminalUnavailable: 'Mesin EDC tidak tersedia. Silakan bayar dengan kode di layar.'
    },
    scanInstruction: 'Pindai kode QR dengan aplikasi pembayaran Anda',
    timeToPay: 'Selesaikan pembayaran dalam',
    cancelOrder: 'Batalkan dan mulai ulang',
    expired: 'Kode pembayaran kedaluwarsa',
    restartPrompt: 'Klik untuk memulai ulang transaksi',
    restart: 'Mulai Ulang Transaksi',
    successMessage: 'Pembayaran berhasil!',
    notSuccessful: 'Pembayaran tidak berhasil. Silakan coba lagi.',
    offline: 'Tidak ada koneksi internet',
    offlineMessage: 'Silakan periksa koneksi internet Anda dan coba lagi',
    retry: 'Coba Lagi',
    reload: 'Muat Ulang',
    checkingConnection: 'Memeriksa koneksi...',
    networkError: 'Terjadi kesalahan jaringan',
    snapError: 'Pembayaran gagal. Silakan coba lagi.',
    wrongCode: 'Kode salah. Silakan coba lagi.',
    enterCode: 'Masukkan Kode untuk Melanjutkan',
    offlineMode: 'Mode Offline - Tidak Ada Internet',
    autoRestart: 'Mulai ulang otomatis dalam',
    checkAgain: 'Sudah bayar, cek lagi',
    notReceivedYet: 'Pembayaran belum diterima. Silakan cek ponsel Anda.',
    snapReady: 'Gateway pembayaran siap',
    snapOpening: 'Membuka pembayaran...',
    openSnap: 'Buka Pembayaran',
    payNowTitle: 'BAYAR UNTUK MEMULAI SESI',
    codeEntry: {
      title: 'Masukkan Kode Akses',
      subtitle: 'Masukkan kode 6-digit untuk melewati pembayaran dalam mode offline'
    }
  },
  checkout: {
    title: 'Tinjau Pesanan Anda',
    print: 'Cetak',
    choosePrints: 'Pilih jumlah cetakan',
    minus: 'Kurang',
    plus: 'Tambah',
    gif: 'GIF',
    getAnimated: 'Dapatkan gambar animasi',
    recording: 'Rekaman',
    getRecording: 'Dapatkan rekaman sesi foto',
    video: 'Video',
    recordVideo: 'Rekam video',
    free: 'Gratis',
    subtotal: 'Subtotal:',
    discount: 'Diskon:',
    total: 'Total:',
    remainingTime: 'Waktu Tersisa:',
    havePromo: 'Punya kode voucher?',
    proceedToPayment: 'Lanjutkan ke Pembayaran',
    enterPromo: 'Masukkan kode promo',
    voucherCodeTitle: 'Kode voucher',
    voucherCodePlaceholder: 'Ketik atau tempel kode Anda',
    promoApplied: 'Kode promo berhasil diterapkan!',
    invalidPromo: 'Kode promo tidak valid',
    cancel: 'Batal',
    apply: 'Terapkan',
    promoValidationFailed: 'Gagal memvalidasi kode promo. Silakan coba lagi.',
    digital: 'Foto Digital',
    getDigital: 'Dapatkan salinan digital foto Anda',
    keyboard: 'Papan Ketik',
    scanQR: 'Pindai QR',
    scanningQR: 'Memindai kode QR...',
    qrInstructions:
      'Pegang kode QR di depan kamera Anda. Kode promo akan otomatis terdeteksi dan diterapkan.',
    qrScannerActive: 'Pemindai QR Aktif',
    pointCamera: 'Arahkan kamera Anda ke kode QR yang berisi kode promo',
    failedToCaptureImage: 'Gagal mengambil gambar dari kamera',
    errorScanningQR: 'Kesalahan memindai kode QR',
    addNumberOfPeople: 'Tolong pilih jumlah orang.',
    choosePeople: 'Pilih jumlah orang',
    pax: 'Jumlah Orang'
  },
  frameSurcharge: {
    badge: '+{{price}}',
    lineItem: 'Tambahan frame'
  },
  frameCategoryBasePrice: {
    badge: '+{{price}}',
    lineItem: 'Harga dasar'
  },
  bahela: {
    welcome: {
      title: 'Selamat Datang',
      subtitle: 'TUAN DAN NONA!',
      promoButton: 'Kode Promo',
      startButton: 'Mulai Sesi',
      tutorial: 'Panduan',
      noCredits: 'Kredit tidak tersedia. Silakan isi ulang kredit Anda.',
      startError: 'Gagal memulai sesi. Silakan coba lagi.',
      discountApplied: 'Diskon sudah diterapkan, silahkan mulai sesi!',
      next: 'Selanjutnya',
      or: 'atau',
      savePathNew: 'File akan disimpan di folder Pictures/Dreambooth Gallery/{{date}} Anda.'
    },
    promoModal: {
      title: 'Masukkan Kode Promo',
      placeholder: 'Kode Promo',
      submit: 'Masukkan',
      cancel: 'Batal',
      promoExpired: 'Kode promo sudah tidak berlaku',
      invalidPromo: 'Kode promo tidak valid',
      percentageDiscountApplied: 'Diskon {{value}}% diterapkan, silahkan mulai sesi!',
      fixedDiscountApplied: 'Diskon {{value}} diterapkan, silahkan mulai sesi!',
      promoError: 'Gagal memvalidasi kode promo',
      cashVoucherApplied: 'Voucher tunai diterapkan.',
      customFrameVoucherApplied:
        'Voucher frame kustom diterapkan. Frame Anda akan dipilih di kiosk.'
    },
    tutorial: {
      title: 'Panduan Bilik Foto BAHeLA'
    }
  },
  recording: {
    title: 'Rekaman',
    type: 'Tipe Rekaman',
    modes: {
      live: 'Live Mode',
      story: 'Story Mode',
      session: 'Session Mode'
    },
    description: {
      recording: 'Video strip foto live mode',
      strip_live: 'Video strip foto live mode',
      strip_story: 'Video strip foto story mode',
      session_recording: 'Rekaman seluruh sesi foto'
    },
    toggle: 'Aktifkan rekaman',
    errorScanningQR: 'Kesalahan memindai kode QR',
    addNumberOfPeople: 'Tolong pilih jumlah orang.',
    choosePeople: 'Pilih jumlah orang',
    pax: 'Jumlah Orang'
  },
  view: {
    noCreditsModal: {
      title: 'Perlu langganan atau kredit',
      description:
        'Langganan Anda telah berakhir atau kredit habis. Perpanjang di dashboard atau isi kredit untuk terus menggunakan aplikasi tanpa gangguan.',
      purchaseSubscription: 'Aktifkan Langganan',
      topUpCredits: 'Isi Ulang Kredit',
      close: 'Tutup'
    },
    subscriptionValidUntil: 'Langganan Anda berlaku hingga {date}.'
  },
  modeSelect: {
    title: 'Mau foto yang mana?',
    subtitle: 'Tiap mode punya pilihan paket, frame, dan ukuran cetaknya sendiri.',
    from: 'mulai {{price}}',
    unavailable: 'Belum tersedia di booth ini'
  },
  consent: {
    title: 'Persetujuan Publikasi Foto',
    subtitle: 'Kami ingin menampilkan foto-foto menakjubkan Anda!',
    question: 'Bisakah kami menggunakan foto Anda untuk publikasi?',
    description:
      'Foto-foto indah Anda bisa ditampilkan di website, media sosial, atau materi pemasaran kami untuk menginspirasi orang lain. Kami tidak akan pernah membagikan informasi pribadi Anda.',
    yourPhotos: 'Foto Anda',
    yourVideo: 'Video Anda',
    photoStripAlt: 'Strip Foto',
    benefits: {
      title: 'Apa artinya ini:',
      featured: 'Dapat ditampilkan di media sosial atau website kami',
      inspire:
        'Jadikan gaya dan kreativitas unik Anda inspirasi bagi orang lain untuk mencoba pengalaman bilik foto kami',
      privacy: 'Tidak ada informasi pribadi yang akan dibagikan secara publik'
    },
    accept: 'Ya, dengan senang hati!',
    decline: 'Tidak, terima kasih',
    footer:
      'Anda bisa mengubah preferensi ini kapan saja dengan menghubungi tim dukungan kami di dreamboothstudio.com.',
    newspaper: {
      headline: 'SIAP MENJADI TERKENAL?'
    }
  },
  onboarding: {
    congratulations: {
      title: 'Selamat!',
      subtitle: 'Anda telah berhasil menyelesaikan sesi bilik foto pertama Anda!',
      nextSteps: 'Apa yang ingin Anda lakukan selanjutnya?',
      customize: 'Sesuaikan Proyek Anda',
      analytics: 'Lihat Analitik',
      gallery: 'Jelajahi Galeri',
      payment: 'Kelola Langganan',
      getStarted: 'Mulai'
    }
  },
  newspaper: {
    welcome: {
      headline: 'Newspaper Photobooth: {{content}}',
      takePhoto: 'Mulai Foto',
      subheadline: "{{city}}'s Most Beautiful {{year}}",
      subheadlineDefault: "World's Most Beautiful {{year}}",
      introWithCity: 'Citizens from {{city}} have gathered for this historic photo opportunity, coming together to capture memories that celebrate their community and local heritage.',
      introDefault: 'Local citizens have gathered for this historic photo opportunity, coming together to capture memories that celebrate their community and shared moments.',
      aboutCity: 'About {{city}}',
      photoboothSection: 'Photobooth That Brings It All',
      dreamboothSection: 'Creativity Unleashed By Innovative Software'
    },
    checkout: {
      steps: {
        title: 'Cara Menggunakan',
        step1: 'Langkah 1: Tinjau pesanan Anda dan pilih paket',
        step2: 'Langkah 2: Selesaikan pembayaran jika diperlukan',
        step3: 'Langkah 3: Pilih gaya filter Anda',
        step4: 'Langkah 4: Pilih bingkai foto Anda',
        step5: 'Langkah 5: Ambil foto Anda',
        step6: 'Langkah 6: Tinjau dan ambil ulang jika diperlukan',
        step7: 'Langkah 7: Dapatkan foto Anda!'
      }
    },
    filter: {
      timeRemaining: 'Waktu Tersisa'
    },
    select: {
      timeRemaining: 'Waktu Tersisa'
    }
  },
  strip: {
    step1Title: 'Langkah 1: Pilih Strip',
    step1Desc: 'Pilih bingkai dan unggah foto jika diperlukan.',
    step2Title: 'Langkah 2: Konfigurasi Unggah',
    step2Desc: 'Pilih apakah akan mengunggah ke galeri dan konfigurasi proyek/sesi.',
    step3Title: 'Langkah 3: Konfigurasi Pencetakan Jarak Jauh',
    step3Desc: 'Pilih printer jarak jauh dan konfigurasi pengaturan cetak.',
    step4Title: 'Langkah 4: Pratinjau & Buat',
    step4Desc: 'Tinjau konfigurasi Anda dan buat strip.',
    modeConfig: 'Buat baru',
    modeUpload: 'Unggah gambar strip yang sudah ada',
    useExistingStrip: 'Gunakan strip yang ada',
    chooseFromGallery: 'Pilih dari galeri',
    selectFrame: 'Silakan pilih bingkai',
    selectFrameTitle: 'Pilih Bingkai Strip Foto',
    uploadStripImage: 'Silakan unggah gambar strip foto',
    uploadPhotos: 'Klik untuk mengunggah foto',
    uploadHint: 'Pilih hingga {{count}} foto',
    remove: 'Klik untuk Menghapus',
    mirror: 'Cermin',
    uploadToGallery: 'Unggah ke galeri',
    selectProjectTitle: 'Pilih Proyek',
    enterSessionId: 'Masukkan ID Sesi',
    validatingSession: 'Memvalidasi sesi...',
    sessionValid: 'ID Sesi valid',
    sessionInvalid: 'ID Sesi tidak valid atau tidak diizinkan',
    printStrip: 'Cetak strip',
    selectRemotePrinterTitle: 'Pilih Printer Jarak Jauh',
    noPrinterSelected: 'Tidak ada printer yang dipilih.',
    preview: 'Pratinjau',
    previewHint: 'Pratinjau strip foto Anda',
    generating: 'Membuat...',
    generate: 'Buat Strip',
    downloading: 'Mengunduh...',
    uploading: 'Mengunggah...',
    printing: 'Mencetak...',
    errorFetchingData: 'Kesalahan mengambil data',
    errorFetchingPrinters: 'Gagal mengambil printer jarak jauh',
    exportFailed: 'Gagal membuat pratinjau strip',
    noUser: 'Pengguna tidak terautentikasi',
    invalidSessionId: 'Tidak diizinkan untuk mengunggah ke galeri ini',
    uploadTimeout: 'Unggah membutuhkan waktu lebih lama dari yang diharapkan. Melanjutkan...',
    uploadSuccess: 'Strip berhasil diunggah',
    uploadFailed: 'Gagal mengunggah strip - ID Sesi Tidak Valid',
    printSuccess: 'Pekerjaan cetak berhasil dikirim!',
    printFailed: 'Cetak gagal: {{error}}',
    base64Error: 'Gagal menyiapkan gambar untuk dicetak.',
    printError: 'Kesalahan mengirim pekerjaan cetak.',
    generationError: 'Kesalahan membuat strip foto',
    uploaded: 'Diunggah:',
    step2: {
      upload: 'Unggah',
      skip: 'Lewati'
    },
    step3: {
      print: 'Cetak',
      done: 'Selesai'
    },
    galleryModal: {
      title: 'Pilih Foto dari Galeri',
      selectUpTo: 'Pilih hingga {{count}} foto',
      loading: 'Memuat foto...',
      loadingMore: 'Memuat foto lebih banyak...',
      noPhotos: 'Tidak ada foto ditemukan',
      errorFetching: 'Gagal mengambil foto',
      maxSelected: 'Anda hanya dapat memilih hingga {{count}} foto',
      noSelection: 'Silakan pilih setidaknya satu foto'
    }
  }
}

// Initialize i18n
i18n.use(initReactI18next).init({
  resources: {
    en: {
      translation: enTranslations
    },
    id: {
      translation: idTranslations
    },
    es: {
      translation: esTranslations
    }
  },
  lng: 'en', // Default language
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false // React already escapes values
  },
  react: {
    useSuspense: false
  }
})

export default i18n
